// Entrypoint: load WASM core, load a map, and run a first-person
// Warfork-style movement controller (pointer lock + WASD + jump/dash).
//
// Milestone 2: playable movement. Multiplayer + weapons wire in later.

import { Renderer, perspective, lookAt, multiply, modelMatrix, extractFrustum } from "./render/renderer";
import type { DrawMaterial, ModelInstance, MoverChunk, PortalChunk, TextureMode } from "./render/renderer";
import { parseMd3, parseSkin } from "./render/md3";
import type { Md3Model } from "./render/md3";
import { fetchBsp, bspUrl } from "./sim/map";
import { api } from "./base";
import { loadTexture, setTextureMap } from "./render/textures";
import { loadShader, loadShaders, buildPlan, clearShaderCache, setShaderMap } from "./render/shader";
import type { CullMode } from "./render/shader";
import type { BlendMode, StageEffects, FogParms } from "./render/shader";
import { getIdentity, registerNickname, submitTime, fetchGhost, fetchLeaderboardBoard } from "./net/leaderboard";
import type { LeaderboardBoard } from "./net/leaderboard";
import { computeCheckpointReadout } from "./race/checkpoint";
import {
  NetTransport,
  NetSnapshot,
  NetVote,
  RosterEntry,
  NetPlayerState,
  NetEntity,
  NetBeam,
  MatchState,
  NetKill,
  NetHealZone,
  NetFlag,
  NetPingMark,
} from "./net/transport";
import { SnapshotBuffer, lerpAngle } from "./net/snapshot_buffer";
import {
  EV_DEATH,
  EV_FIRE,
  EV_HIT,
  EV_JUMP,
  EV_KILL_CONFIRM,
  EV_LAND,
  EV_PAIN,
  EV_PREJUMP,
} from "./net/events";
import { appendQ3Text, hasQ3Colors, q3Html, stripQ3Colors } from "./net/q3colors";
import { Sponge, SyncError, noteSyncCorrection, resetSyncError } from "./net/sync_error";
import { Menu } from "./ui/menu";
import { PRESETS, dumpRules } from "./rules";
import { GameMode, MODE_PRESETS, dumpMode, parseModeDump } from "./modes";
import {
  DOWNSTREAM_RATES,
  createSettingsStore,
  loadSettings,
  type HudSettings,
  type Settings,
  type TrailColorMode,
} from "./settings";
import {
  applyGlobalDefaults,
  clearExplicit,
  effectiveValue,
  fetchGlobalDefaults,
  isExplicit,
  loadCachedGlobalDefaults,
  markExplicit,
  resetSetting,
} from "./settings/defaults";
import { SETTINGS, setSettingValue, validateSettingsRegistry } from "./settings/registry";
import { searchSettings } from "./settings/search";
import {
  clientCommands,
  commandSummary,
  findClientCommand,
  formatSettingValue,
  parseCommandValue,
  readCommand,
  usageFor,
  type ClientCommand,
} from "./console/catalog";
import { HudLayoutRuntime } from "./hud/runtime";
import type { HudLayoutStore } from "./hud/types";
import { hudStageRect } from "./stage";
import { ParticleSystem } from "./render/particles";
import { PlayerTrailSystem, type TrailSample } from "./render/player_trail";
import {
  fetchAdminSettings,
  loadAdminToken,
  loadCachedAdmin,
  loadPreviewRole,
  saveAdminSettings,
  saveAdminToken,
  savePreviewRole,
  type AdminSettings,
  type Role,
} from "./admin";
import { loadAssetPack, resetAssetGpu, assetModelId, assetEntry, assetGpuCount, assetKeys, packReady } from "./assets";
import { MovementHud } from "./ui/movement_hud";
import { fastInvSqrt } from "./render/fastmath";
import { hideHopper, installHopper, lastCaughtBug, showHopper } from "./hopper";
import { WEAPON_NAMES } from "./weapons/names";
import { CrosshairHud } from "./crosshair/hud";
import { configFor } from "./crosshair/config";
import type { CrosshairStore } from "./crosshair/types";
import { resolveWeapon } from "./weapons/settings";
import type { ResolvedWeapon } from "./weapons/settings";
import type { WeaponSettings } from "./weapons/settings";
import { BindMap, ACTION_LABELS, codeToAction, mouseButtonToCode, Action } from "./binds";
import { SoundManager } from "./audio";
import { Profiler } from "./profiler";
import { Scheduler } from "./scheduler";
import init, * as core from "../pkg/webrace_core.js";
import { TICK_RATE, TICK_MS, ticksToMs } from "./tickrate";
import { pickRay, PICK_MOVER, PICK_PORTAL } from "./render/pick";
import type { PickChunk } from "./render/pick";
import { applyTheme, loadCachedTheme, normalizeTheme, refreshThemeFromServer } from "./ui/theme";
import { installCycleControls } from "./ui/cycle";

// ---- Single settings owner -------------------------------------------------
// The menu mutates this object and commits it; console/import paths commit with
// a notification so the runtime + UI re-apply. Server rules/mode are separate
// (see `serverRules`/`serverMode` below) and never overwrite user preferences.
if (import.meta.env.DEV) {
  for (const error of validateSettingsRegistry()) console.error(`[settings] ${error}`);
}

const settingsStore = createSettingsStore(applyGlobalDefaults(loadSettings()));

/** Apply a settings change from a non-menu path (console, import, reset). */
function commitSettings(mutate: (s: Settings) => void): void {
  mutate(settingsStore.value());
  settingsStore.commit();
}

/** Runtime debug flags follow published defaults unless explicitly changed. */
function applyRuntimeSettingDefaults(doc = loadCachedGlobalDefaults()): void {
  for (const def of SETTINGS) {
    if (!def.runtimeKey || isExplicit(def.id)) continue;
    if (urlPinnedRuntimeKeys.has(def.runtimeKey)) continue;
    applyDebug(def.runtimeKey, effectiveValue(def, doc) as number | boolean);
  }
}

/** Refresh from the server; apply anything that changed without clobbering users. */
function refreshGlobalSettingsDefaults(): void {
  void fetchGlobalDefaults().then((doc) => {
    const before = JSON.stringify(settingsStore.value());
    applyGlobalDefaults(settingsStore.value(), doc);
    applyRuntimeSettingDefaults(doc);
    if (JSON.stringify(settingsStore.value()) !== before) settingsStore.commit();
    // Visibility lives in the same document and can change without any value
    // changing, so always refresh the menu's access pass.
    menu?.refreshSettingsAccess();
  });
}

// Apply the published design theme from the local cache before the first paint,
// then refresh it from the server (a published theme changes for everyone).
applyTheme(loadCachedTheme());
void refreshThemeFromServer();
// Native dropdowns become click-to-cycle controls; the observer also catches
// the ones the menu creates dynamically.
installCycleControls();

// The design page runs in another tab. When it publishes, it updates the shared
// cache; apply that immediately. Also re-apply/refresh on tab focus and bfcache
// restores (e.g. returning to the landing page) so the theme cannot go stale.
window.addEventListener("storage", (e) => {
  if (e.key !== "webrace.theme.cache" || !e.newValue) return;
  try {
    applyTheme(normalizeTheme(JSON.parse(e.newValue)));
  } catch {
    /* ignore malformed cross-tab data */
  }
});
window.addEventListener("pageshow", () => {
  applyTheme(loadCachedTheme());
  void refreshThemeFromServer();
});
document.addEventListener("visibilitychange", () => {
  if (document.hidden) return;
  applyTheme(loadCachedTheme());
  void refreshThemeFromServer();
});

const overlay = document.getElementById("overlay")!;
const statusEl = document.getElementById("status")!;
const fpsEl = document.getElementById("fps")!;
const netGraphEl = document.getElementById("netgraph")!;
const speedEl = document.getElementById("speed")!;
const coordsEl = document.getElementById("coords")!;
const mapInput = document.getElementById("map") as HTMLInputElement;
const nicknameInput = document.getElementById("nickname") as HTMLInputElement;
const playRaceBtn = document.getElementById("play-race")!;
const playTeamBtn = document.getElementById("play-team")!;
const logEl = document.getElementById("log")!;
const lockEl = document.getElementById("lock")!;
const timerEl = document.getElementById("timer")!;
const checkpointHudEl = document.getElementById("checkpoint-hud")!;
const recallHudEl = document.getElementById("recall-hud")!;
const voteHudEl = document.getElementById("vote-hud")!;
const voteActionsEl = document.getElementById("vote-actions")!;
const voteYesBtn = document.getElementById("vote-yes") as HTMLButtonElement;
const voteNoBtn = document.getElementById("vote-no") as HTMLButtonElement;
const collisionStateEl = document.getElementById("collision-state");
const collisionToggleBtn = document.getElementById("collision-toggle") as HTMLButtonElement | null;
const announceEl = document.getElementById("announce")!;
const hitmarkerCanvas = document.getElementById("hitmarker") as HTMLCanvasElement;
const damageNumbersEl = document.getElementById("damagenumbers")!;
// Combat HUD (health / armor / weapon / ammo).
const hudHealthEl = document.getElementById("hud-health")!;
const hudArmorEl = document.getElementById("hud-armor")!;
const hudWeaponEl = document.getElementById("hud-weapon")!;
const hudAmmoEl = document.getElementById("hud-ammo")!;
// Match HUD / scoreboard / kill feed.
const matchHudEl = document.getElementById("match-hud")!;
const matchPhaseEl = document.getElementById("match-phase")!;
const matchScoreEl = document.getElementById("match-score")!;
const matchTimerEl = document.getElementById("match-timer")!;
const killFeedEl = document.getElementById("killfeed")!;
const scoreboardEl = document.getElementById("scoreboard")!;
const scoreboardBodyEl = document.getElementById("scoreboard-body")!;
const scoreboardTitleEl = document.getElementById("scoreboard-title")!;
const spectateHudEl = document.getElementById("spectate-hud")!;
const flagHudEl = document.getElementById("flag-hud")!;
// Show the unlock state immediately on load (pointerlockchange doesn't fire
// until a lock attempt happens, so we initialize the label ourselves).
lockEl.textContent = "🔓 click to lock mouse";
lockEl.style.color = "var(--ui-danger)";
const canvas = document.getElementById("view") as HTMLCanvasElement;

let renderer: Renderer | null = null;
let particles: ParticleSystem | null = null; // additive glow VFX (particles.ts)
let smoke: ParticleSystem | null = null; // alpha-blended smoke/dust VFX
let railRings: ParticleSystem | null = null; // additive ring sprites for the rail spiral
let maxParticles = 4096; // glow pool cap; `?particles=N` overrides
let maxSmoke = 2048; // smoke pool cap; `?smoke=N` overrides
let maxRailRings = 768; // rail spiral ring cap
// VFX tuning (Video tab; presentation only).
let vfxAmount = 1; // glow emit-count multiplier
let vfxSmokeDensity = 0.5; // smoke emit-count multiplier
let vfxSmokeOpacity = 0.1; // smoke alpha multiplier
let vfxSize = 1; // billboard-size multiplier (both layers)
let vfxTrails = true; // projectile trails on/off
let vfxCollide = true; // particles collide with the world on/off
// Neon player trails (Video tab; persisted). Presentation only.
let playerTrails: PlayerTrailSystem | null = null;
let trailSeconds = settingsStore.value().trailSeconds;
let trailWidth = settingsStore.value().trailWidth;
let trailIntensity = settingsStore.value().trailIntensity;
// Server-backed admin defaults (ping timings for now) + local role preview.
let adminSettings: AdminSettings = loadCachedAdmin();
let currentRole: Role = loadPreviewRole();
/** Unsaved slider tweaks: don't overwrite them with the server copy. */
let adminDirty = false;

/** Re-fetch the global admin defaults and refresh the Admin page. */
function refreshAdminSettings(): void {
  if (adminDirty) return;
  void fetchAdminSettings().then((settings) => {
    if (!settings || adminDirty) return;
    adminSettings = settings;
    menu?.refreshAdmin();
  });
}
let trailSelfMode: TrailColorMode = settingsStore.value().trailSelfMode;
let trailSelfColor = settingsStore.value().trailSelfColor;
let trailSelfTeamColor = settingsStore.value().trailSelfTeamColor;
let trailEnemyMode: TrailColorMode = settingsStore.value().trailEnemyMode;
let trailEnemyColor = settingsStore.value().trailEnemyColor;
let trailEnemyTeamColor = settingsStore.value().trailEnemyTeamColor;
// Bloom post-process (Video tab; presentation only). On by default;
// `?bloom=0` disables it (also a Debug toggle).
let bloomEnabled = true;
let bloomIntensity = 0.9;
let bloomThreshold = 0.7;
// VFX colour overrides (hex; presentation only, applied live).
let vfxColorExplosion = "#a3e635";
let vfxColorSparks = "#22d3ee";
let vfxColorMuzzle = "#f22f2f";
let vfxColorSmoke = "#ffffff";
/** Per-weapon projectile/beam colour overrides (weapon id -> hex). */
const projectileColorOverride: Record<number, string> = {};
/** Per-weapon projectile/orb opacity override (0..1); absent = fully opaque. */
const projectileAlphaOverride: Record<number, number> = {};

/** "#rrggbb" -> [r,g,b] in 0..1 (falls back to white). */
function hexToRgb(hex: string): [number, number, number] {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex.trim());
  if (!m) return [1, 1, 1];
  const n = parseInt(m[1], 16);
  return [((n >> 16) & 255) / 255, ((n >> 8) & 255) / 255, (n & 255) / 255];
}
let memory: WebAssembly.Memory | null = null;
let sessionId: number | null = null;
let currentMap = ""; // map name shown/being loaded
let activeMap = ""; // map name the *active session* belongs to (used for submits)
let currentMapId = -1; // WASM map handle of the loaded map (for PVS queries)
// Face probe (debug): brute-force ray pick over the loaded mesh, keyed off the
// camera. `?face=1` enables a continuous (throttled) probe; the `face_probe`
// bind (default F) reports the nearest hit's chunk/material/face.
let pickCtx: {
  vertsPtr: number;
  vertCount: number;
  idxPtr: number;
  idxCount: number;
  moverVertsPtr: number;
  moverVertCount: number;
  moverIdxPtr: number;
  moverIdxCount: number;
  chunks: PickChunk[];
  moverChunks: PickChunk[];
} | null = null;
let faceProbeOn = false;
let lastProbeKey = ""; // suppress repeated identical continuous-report lines
// Debug: `?nomodels=1` skips MD3/model instances (to isolate overlay artifacts).
let hideModels = false;
// Debug: `?noparticles=1` / `?noviewmodel=1` skip those overlay passes.
let hideParticles = false;
let hideViewModel = false;
// Debug: `?multidraw=0` forces the old dynamic PVS index buffer (A/B the
// `WEBGL_multi_draw` path).
let multiDrawEnabled = true;
/**
 * The player collision box from the core (so the debug probes always match
 * `trace::PLAYER_MINS/MAXS`). Call after WASM init — `core` is undefined during
 * module evaluation.
 */
function playerBox(): { mins: Float32Array; maxs: Float32Array } {
  const b = core.player_box_bounds() as unknown as Float32Array;
  return {
    mins: new Float32Array([b[0], b[1], b[2]]),
    maxs: new Float32Array([b[3], b[4], b[5]]),
  };
}
// Stuck auto-probe bookkeeping.
let stuckSinceMs = 0;
let stuckLogged = false;
// Camera ray captured each frame (eye + camera basis), used by the throttled
// probe and by `__webrace.pickScreen(x, y)`.
let lastRay: {
  eye: [number, number, number];
  fwd: [number, number, number];
  right: [number, number, number];
  up: [number, number, number];
} | null = null;
let ghostModelId = -1; // procedural player capsule body
let ghostOutlineModelId = -1; // inflated capsule hull for the neon outline
let ghostHaloModelId = -1; // wider, dimmer halo shell around the outline
let playerGearModelId = -1; // dark gun + visor frame attached to the capsule
let playerGlowModelId = -1; // glowing visor/chest/antenna/shoulder accents
let projectileModelId = -1; // uploaded synthetic cube used to draw projectiles
let ballModelId = -1; // uploaded synthetic sphere used to draw physics balls
let itemModelId = -1; // uploaded synthetic cube used to draw map item pickups
let teleportModelId = -1; // uploaded synthetic diamond for teleporter destinations
let teleporterDests: [number, number, number][] = [];
const itemScratch = new Float32Array(8); // [x,y,z,kind,weapon,amount,present,remaining_ms]

/** Pickup marker colour: health green · armor blue · ammo yellow · weapon orange. */
function itemColor(kind: number): [number, number, number] {
  return kind === 1 ? [0.3, 1, 0.4] : kind === 2 ? [0.4, 0.7, 1] : kind === 3 ? [1, 0.85, 0.3] : [1, 0.5, 0.15];
}
/**
 * Manifest key for a map item pickup, or "" when unmapped. The core exposes
 * only `kind`/`weapon`/`amount` (see `bsp_item`), so health/armor sizes are
 * inferred from the grant amount (5/25/100 health, 5/50/100 armor).
 */
function itemAssetKey(kind: number, weapon: number, amount: number): string {
  if (kind === 0) return `weapon.${weapon | 0}.pickup`;
  if (kind === 1) return amount <= 5 ? "item.health.small" : amount <= 25 ? "item.health.medium" : "item.health.large";
  if (kind === 2) return amount <= 5 ? "item.armor.shard" : amount <= 50 ? "item.armor.yellow" : "item.armor.red";
  if (kind === 3) return "item.ammo.box";
  return "";
}

/**
 * Map-item opacity: 100% when available, 50% while it is gone, then +10% per
 * second over the last 5 seconds (`remainingMs` < 0 = never respawns).
 */
function itemAlpha(present: boolean, remainingMs: number): number {
  if (present) return 1;
  if (remainingMs < 0) return 0.5; // unknown or never respawns
  const step = Math.max(0, Math.min(5, 5 - Math.ceil(remainingMs / 1000)));
  return 0.5 + step * 0.1;
}

/** `[pitch, yaw, roll]` (radians) so a model's +X axis points along `v`. */
function velocityAngles(v: number[] | Float32Array): [number, number, number] {
  const x = v[0], y = v[1], z = v[2];
  const len = Math.hypot(x, y, z) || 1;
  return [-Math.asin(Math.max(-1, Math.min(1, z / len))), Math.atan2(y, x), 0];
}

let beamModelId = -1; // uploaded synthetic cube (unit, stretched) for tracers
let viewModelId = -1; // uploaded synthetic first-person weapon placeholder
// First-person viewmodel placement relative to the eye (units). `DOWN` is
// negative screen-up, i.e. below the eye. The gun model is authored along +X
// with its barrel tip at model x = VIEWMODEL_TIP_X.
// NOTE: the placeholder gun is ~18 units long; placed unscaled a few units from
// a 140°-FOV eye it filled a third of the screen as a flat grey slab (it was
// repeatedly mistaken for a misplaced world surface). `VIEWMODEL_SCALE` shrinks
// it into the corner where a weapon belongs.
const VIEWMODEL_SCALE = 0.30;
const VIEWMODEL_FWD = 7.0;
const VIEWMODEL_RIGHT = 6.5;
const VIEWMODEL_DOWN = -5.0;
/** Authored barrel-tip offset; scaled by `VIEWMODEL_SCALE` for world offsets. */
const VIEWMODEL_TIP_X = 15;
const VIEWMODEL_COLOR: [number, number, number] = [0.30, 0.36, 0.48];
const VIEWMODEL_ALPHA = 0.85;
// Ghost source: the last run (default), your personal record, or the world record.
let ghostSource: "last" | "pr" | "wr" | "off" = "off";
let ghostLast: Float32Array | null = null;
let ghostPR: Float32Array | null = null;
let ghostWR: Float32Array | null = null;
let wasRecording = false; // detect a fresh run start (to re-apply the ghost)
// Checkpoint HUD: leaderboard splits for the active map, loaded once per map.
let raceBoard: LeaderboardBoard | null = null;
let raceBoardSerial = 0; // bumped on load so an open HUD re-renders
let raceBoardReq = 0; // ignore stale responses after a map switch
let checkpointSig = ""; // avoid rebuilding the DOM every frame

// Multiplayer (Phase 4): connection + prediction/reconciliation.
let net: NetTransport | null = null;
let mySlot = -1;
let serverOverride: string | null = null;
/** Which deployed instance to join (race/freestyle vs combat). */
type ServerKind = "race" | "team";
let reconcileOffset: { d: [number, number, number]; t: number } | null = null;
let RECONCILE_MS = 150; // smooth server corrections over this long
let INTERP_MS = 100; // render remote players this far in the past
let interpOverride = false; // set when `?interp=` pins the delay explicitly
/** Downstream snapshot rate (Hz) the client consumes; 125 is the public default. */
let downstreamHz = settingsStore.value().downstreamHz;
/**
 * `cg_truelightning` (0..1): the LG beam is always anchored to the weapon muzzle;
 * this blends how well its far end follows the crosshair — 1 = perfect/instant
 * follow, 0 = the server's real (lagged, swaying) impact. Recomputes every frame.
 * Set with `?cg_truelightning=`.
 */
let CG_TRUELIGHTNING = settingsStore.value().cgTrueLightning;
/**
 * The snapshot pair + fraction the client rendered in the **last completed
 * frame**. Sent on fire commands so the server mirrors exactly what the shooter
 * saw (Phase 6.5). Written only by `renderFrame`; every other caller reads it.
 */
let lastRenderStamp: { a: number; b: number; f: number } | null = null;
/**
 * The shooter's own rendered camera pose from the last frame. Sent with
 * commands so the server can start the shot from what the player actually saw
 * (the server validates it against the predicted state before using it).
 */
let lastLocalPose: { x: number; y: number; z: number; yaw: number; pitch: number } | null =
  null;
/**
 * Pose + target stamp captured on the attack *press*, so the shot uses the
 * frame the player clicked rather than the next command batch. Consumed by the
 * next sent batch.
 */
let pendingShotStamp: {
  t: number;
  st: { a: number; b: number; f: number } | null;
  lp: { x: number; y: number; z: number; yaw: number; pitch: number } | null;
} | null = null;

/** Capture the exact rendered frame when an attack button goes down. */
function noteShotPress(): void {
  if (!lastLocalPose) return;
  pendingShotStamp = {
    t: performance.now(),
    st: lastRenderStamp ? { ...lastRenderStamp } : null,
    lp: { ...lastLocalPose },
  };
}
const snapBuffer = new SnapshotBuffer(32, () => INTERP_MS);
let lastAck = -1;
let reconcileCount = 0;
let lastCorr = 0;
let corrCount = 0;
// Coalesced reconciliation: the latest snapshot's (ack, ps) is applied once per
// frame instead of inside every snapshot message (62.5–125 Hz).
let pendingReconcile: { ack: number; ps: string } | null = null;
let lastReconcileMs = 0;
let lastRulesHash = -1;
// Practice detach (multiplayer): `wasPracticeDetached` detects the
// practice -> normal-play transition so we can resync with the server.
let wasPracticeDetached = false;
// Input-timing instrumentation: time from a mousemove event to the sim step that
// applies it (p50/p75/p95/p99). Mouse feel must be local, not network-bound.
let mousePendingT = 0;
const mouseLatencies: number[] = [];
// Netgraph stats (Phase 6.5 observability) — surfaced by `?netstats=1` and mp().
let netGraphOn = false;
let netSnapRate = 0; // snapshots/s over the last fps window
let netSnapCount = 0; // snapshots since the last fps window
let lastSnapArrival = 0;
let lastSnapTick = -1;
let snapGaps = 0;
let netJitterMs = 0;
let snapIntervalMean = 0; // EWMA of snapshot arrival interval (ms)
let expectedTickStep = 0; // observed snapshot tick stride (rate-agnostic)
let corrMax = 0;
const ngSnapHist: number[] = []; // recent snapshot intervals (ms)
const ngCorrHist: number[] = []; // recent correction magnitudes (units)
let serverSpawn: [number, number, number, number] | null = null;
let activeVote: NetVote | null = null;
let pvpCollision = false;
let roster: RosterEntry[] = [];

// Combat (Phase 5): HUD state + replicated projectiles from the last snapshot.
let lastSelf: NetPlayerState | null = null;
let lastMatch: MatchState | null = null;
let lastPlayers: NetPlayerState[] = [];
let latestHeals: NetHealZone[] = [];
let latestFlags: NetFlag[] = [];
let scoreboardVisible = false;
let spectateTarget = -1;
let spectateMode = 0; // 0 = first person, 1 = chase
let lastFlagKey = "";
let lastSpectateKey = "";
let lastScoreHtml = "";
let latestEntities: NetEntity[] = [];
// Network callbacks only queue presentation work; the frame phase drains it.
// This keeps WebSocket arrivals out of the DOM/particle/audio hot path.
type PendingBeamFx = { a: [number, number, number]; b: [number, number, number]; w: number };
const pendingBeamFx: PendingBeamFx[] = [];
let pendingCombatEv = 0;
let pendingKills: NetKill[] = [];
let pendingCaptures: { team: number; slot: number }[] = [];
let pendingChat: { nick: string; text: string; sys: boolean }[] = [];
let pendingPings: NetPingMark[] = [];
const PENDING_FX_CAP = 256;
// Replicated hitscan beams (server sends each once; aged + expired locally).
let netBeams: (NetBeam & { die: number; born: number })[] = [];
// Client fire prediction: an instant local beam for your own shot (the
// authoritative beam still arrives ~1 RTT later for everyone else).
let predictedBeams: {
  start: [number, number, number];
  end: [number, number, number];
  weapon: number;
  born: number;
  die: number;
}[] = [];
// Local beams are copied here so they can linger for the per-weapon trail
// time (the core only keeps them ~100 ms).
let localBeams: (NetBeam & { key: string; born: number; die: number })[] = [];
/** Local-beam keys already ingested, with the wall-clock time they may repeat. */
const localSeenBeams = new Map<string, number>();
let predNextFire = 0;
// Locally-predicted projectiles (visual only): your own rocket/plasma/grenade
// appears immediately instead of after a round-trip. Each is retired as the
// server's authoritative entity replicates (matched by owner+weapon, oldest
// first), or when it expires.
let predictedProjectiles: {
  start: [number, number, number];
  vel: [number, number, number];
  grenade: boolean;
  gravity: number;
  weapon: number;
  spawn: number;
  die: number;
  replaced: boolean;
}[] = [];
let predProjT = 0;
// Server projectiles seen last frame (id -> last position/weapon). A *vanish*
// between frames means an impact → emit explosion/spark VFX there. (Own entities
// are never PVS-culled; a remote one can also vanish by leaving view — the burst
// then lands near wherever it entered cover, which reads acceptably.)
const prevProj = new Map<number, { o: [number, number, number]; w: number }>();
/** Weapon ids whose projectile disappears with a fireball (vs a spark). */
const EXPLOSIVE_WEAPONS = new Set([4, 5, 8]); // grenade, rocket, railgun

// Low-poly debris chunks (client-local visual): tumbling cubes with world
// collision, rendered through the existing model-instance path.
interface DebrisChunk {
  x: number; y: number; z: number;
  vx: number; vy: number; vz: number;
  ax: number; ay: number; az: number; // euler angles
  sx: number; sy: number; sz: number; // angular velocity
  life: number; // ms
  size: number;
  color: [number, number, number];
}
const debrisChunks: DebrisChunk[] = [];
let debrisModelId = -1;
let debrisT = 0;

/** Spawn low-poly debris chunks at an explosion. */
function emitDebris(
  pos: readonly [number, number, number],
  scale: number,
  color: [number, number, number],
): void {
  const n = Math.round(8 * scale);
  for (let i = 0; i < n; i++) {
    const sp = 160 * scale;
    debrisChunks.push({
      x: pos[0], y: pos[1], z: pos[2],
      vx: (Math.random() * 2 - 1) * sp,
      vy: (Math.random() * 2 - 1) * sp,
      vz: (Math.random() * 2 - 1) * sp + 70,
      ax: Math.random() * 6.28, ay: Math.random() * 6.28, az: Math.random() * 6.28,
      sx: (Math.random() * 2 - 1) * 9, sy: (Math.random() * 2 - 1) * 9, sz: (Math.random() * 2 - 1) * 9,
      life: 900 + Math.random() * 500,
      size: 2 + Math.random() * 2.5,
      color,
    });
  }
  if (debrisChunks.length > 120) debrisChunks.splice(0, debrisChunks.length - 120);
}

/** Integrate + collide the debris chunks (called from the particles task). */
function updateDebris(now: number): void {
  if (debrisChunks.length === 0) {
    debrisT = now;
    return;
  }
  if (debrisT === 0) debrisT = now;
  const dt = Math.min(0.05, (now - debrisT) / 1000);
  debrisT = now;
  if (dt <= 0) return;
  for (let i = debrisChunks.length - 1; i >= 0; i--) {
    const d = debrisChunks[i];
    d.life -= dt * 1000;
    if (d.life <= 0) {
      debrisChunks.splice(i, 1);
      continue;
    }
    d.vz -= 800 * dt; // gravity is world −Z (Z-up)
    const nx = d.x + d.vx * dt;
    const ny = d.y + d.vy * dt;
    const nz = d.z + d.vz * dt;
    let hit = false;
    if (sessionId !== null && vfxCollide) {
      const tr = core.session_trace_ray(
        sessionId, d.x, d.y, d.z, nx, ny, nz, d.size,
      ) as unknown as Float32Array;
      if (tr.length >= 5 && tr[4] < 0.5 && tr[0] < 1) {
        const f = tr[0];
        const vn = d.vx * tr[1] + d.vy * tr[2] + d.vz * tr[3];
        d.x += (nx - d.x) * f;
        d.y += (ny - d.y) * f;
        d.z += (nz - d.z) * f;
        d.vx -= 1.4 * vn * tr[1];
        d.vy -= 1.4 * vn * tr[2];
        d.vz -= 1.4 * vn * tr[3];
        d.vx *= 0.5; d.vy *= 0.5; d.vz *= 0.5;
        d.sx *= 0.5; d.sy *= 0.5; d.sz *= 0.5;
        hit = true;
      }
    }
    if (!hit) {
      d.x = nx; d.y = ny; d.z = nz;
    }
    d.ax += d.sx * dt;
    d.ay += d.sy * dt;
    d.az += d.sz * dt;
  }
}
let predHitbox: { scale: number; mins: [number, number, number]; maxs: [number, number, number] } | null = null;
/** Selectable weapon ids in cycle order (Warfork ids). */
const WEAPON_ORDER = [5, 6, 4, 7, 8, 3, 2, 1, 9, 10];
/** Weapon id -> fire sound event id (all weapons have a slot + placeholder). */
const WEAPON_FIRE_SOUND: Record<number, string> = {
  1: "melee_fire",
  2: "machinegun_fire",
  3: "shotgun_fire",
  4: "grenade_fire",
  5: "rocket_fire",
  6: "plasma_fire",
  7: "lightning_fire",
  8: "rail_fire",
  9: "experimental_fire",
};

// Phase 4 tuning knobs + diagnostics (URL params below).
let netStatsEnabled = false;
let simLagMs = 0; // one-way simulated latency (ms), both directions
let simJitterMs = 0; // +/- jitter (ms)
let simLossPct = 0; // dropped-message probability (%)

/**
 * Runtime debug keys pinned by a URL param (`?netstats=1`, `?bloom=0`, `?profile=1`, …).
 * Published defaults arrive asynchronously and must never overwrite a
 * deliberate per-session debug choice.
 */
const urlPinnedRuntimeKeys = new Set<string>();

/** Apply rules to the local session (combat + prediction movement). */
function applyMovementRules(rules: Record<string, number>): void {
  if (sessionId === null) return;
  for (const [k, v] of Object.entries(rules)) {
    core.session_set_rule(sessionId, k, v);
  }
}

/** Apply the movement rules from settings to the local prediction session. */
function applyClientMovementRules(): void {
  // While connected, the server's ruleset is authoritative for prediction;
  // the persisted local rules are only the fallback/offline preference.
  applyMovementRules(serverRules ?? menu?.getRules() ?? settingsStore.value().rules);
}

/** Whether the active mode is a race mode (Race/Freestyle). */
function isRaceMode(): boolean {
  const m = serverMode ?? menu?.getMode() ?? settingsStore.value().mode;
  return m ? m.structure === "race" : true;
}

/**
 * Structure of the **server's** mode (`null` until it sends one). In combat
 * modes the server owns out-of-bounds (void) deaths; in race the client keeps
 * its local reset so the run aborts immediately and prediction stays aligned.
 */
let serverModeStructure: string | null = null;
/** Authoritative server ruleset/mode, kept separate from stored preferences. */
let serverRules: Record<string, number> | null = null;
let serverMode: GameMode | null = null;

/**
 * Live enemy damage hit-volume scale (1.0–3.0). Online: the server's rule;
 * offline/practice: the local rules. Drives the visible proxy + beam clipping.
 */
function currentHitboxScale(): number {
  const r = (net?.connected ? serverRules : null) ?? settingsStore.value().rules;
  const v = r?.hitbox_scale;
  return typeof v === "number" && Number.isFinite(v) ? Math.min(3, Math.max(1, v)) : 1.5;
}
/** Does the server's mode gate the start on ready-up? (duel/team modes, not race) */
let serverWarmupReady = false;

/** Tell the local session who owns void deaths (see `serverModeStructure`). */
function syncVoidAuthority(): void {
  if (sessionId === null) return;
  core.session_set_void_server_owned(sessionId, serverModeStructure !== null && serverModeStructure !== "race");
}

/**
 * Adopt a game mode locally. Match rules are server-authoritative; the client
 * only needs the race flag (prejump/start-line logic is race-only).
 */
function applyGameModeLocal(mode: GameMode): void {
  if (sessionId !== null) {
    core.session_set_prejump_rules(sessionId, mode.structure === "race");
    // The server applies the mode's self-damage scale; mirror it in the local
    // local prediction/combat sim (Freestyle = 0, so rocket jumps push without damage).
    core.session_set_self_damage_scale(sessionId, mode.self_damage_scale);
    // Leaving race/practice: force the local session back to Run so noclip /
    // recall / practice can't linger into an arena match.
    if (mode.structure !== "race" && core.session_mode(sessionId) !== 0) {
      core.session_set_mode(sessionId, 0);
      core.session_reset(sessionId);
    }
  }
}

/** Teleport the local prediction to the server's authoritative spawn (join). */
function applyServerSpawn() {
  if (!serverSpawn || sessionId === null) return;
  const [x, y, z, yaw] = serverSpawn;
  core.session_set_spawn(sessionId, x, y, z, yaw, 0);
}

/** Show/refresh the clickable yes/no prompt for the active vote. */
function updateVoteButtons(vote: { yes?: number[]; no?: number[] }): void {
  voteActionsEl.classList.remove("hidden");
  const mine = mySlot;
  voteYesBtn.classList.toggle("is-voted", mine >= 0 && (vote.yes ?? []).includes(mine));
  voteNoBtn.classList.toggle("is-voted", mine >= 0 && (vote.no ?? []).includes(mine));
}

function hideVoteButtons(): void {
  voteActionsEl.classList.add("hidden");
  voteYesBtn.classList.remove("is-voted");
  voteNoBtn.classList.remove("is-voted");
}

for (const [button, yes] of [
  [voteYesBtn, true],
  [voteNoBtn, false],
] as const) {
  // Keep clicks on the prompt from reaching the canvas/document re-lock handler.
  button.addEventListener("mousedown", (e) => e.stopPropagation());
  button.addEventListener("click", (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (net?.connected) net.sendText(JSON.stringify({ t: "vote", yes }));
  });
}

/**
 * User-initiated map change. The server owns the map, so this is always a vote;
 * a local load only happens through the server's welcome/reload path.
 */
function proposeMap(name: string): void {
  const map = name.trim();
  if (!map) return;
  if (!net?.connected) {
    showAnnounce("connect to a server to change maps", 1800);
    return;
  }
  if (map.toLowerCase() === (net.map ?? "").toLowerCase()) {
    showAnnounce(`already on ${map}`, 1500);
    return;
  }
  net.sendText(JSON.stringify({ t: "callvote", map }));
  showAnnounce(`map vote: ${map} — Y yes / N no`, 2500);
}

/** Update the PvP collision readout (the button text stays "Vote PvP"). */
function updateCollisionUi(): void {
  const state = pvpCollision ? "ON" : "off";
  if (collisionStateEl) {
    collisionStateEl.textContent = `PvP collision: ${state}`;
  }
  if (collisionToggleBtn) {
    collisionToggleBtn.title = `PvP collision is ${state} — vote to turn ${pvpCollision ? "off" : "on"}`;
  }
}

/** Propose a player-vs-player collision on/off vote (multiplayer only). */
function proposeCollision(on: boolean): void {
  if (net?.connected) {
    net.sendText(JSON.stringify({ t: "callvote", collision: on }));
    showAnnounce(`vote: collision ${on ? "ON" : "OFF"} — Y yes / N no`, 2500);
  } else {
    showAnnounce("collision is a multiplayer option", 2000);
  }
}

/** Server-tick stride for the selected downstream rate (sim Hz ÷ Hz). */
function downstreamStride(): number {
  return Math.max(1, Math.round(TICK_RATE / downstreamHz));
}

/**
 * True when a snapshot is on the selected downstream cadence. Snapshots carry
 * the recorded (history) tick, which lags the live tick by
 * `core.snapshot_tick_lag()`; a server at stride N labels ticks ≡ lag-1 (mod N).
 */
function onDownstreamTick(tick: number): boolean {
  const stride = downstreamStride();
  return stride <= 1 || (tick + core.snapshot_tick_lag()) % stride === 0;
}

/**
 * Handle a snapshot (after the simulated downstream delay): buffer it for
 * interpolation and reconcile the local prediction against the server's ack.
 *
 * A lower `downstreamHz` emulates a slower server send rate (Diabotical's
 * "downstream tickrate"): roster/events/beams are still harvested from every
 * arrival so nothing is lost, but only snapshots on the chosen cadence feed
 * interpolation, reconciliation and the netgraph — i.e. the snapshot stream a
 * server sending at `downstreamHz` would deliver. The local prediction is unaffected.
 */
function processSnapshot(snap: NetSnapshot) {
  const now = performance.now();
  if (snap.roster) roster = snap.roster;
  for (const b of snap.beams ?? []) {
    const life = beamTrailMs(b.w);
    // Remote beams start at the shooter's eye; nudge them toward the gun (Q3).
    const start = b.own !== mySlot ? offsetBeamStart(b.a, b.b) : b.a;
    netBeams.push({ ...b, a: start, born: now, die: now + life });
    // Remote players' hitscan shots only reach us here. Queue the impact VFX
    // for the frame phase (never paint/allocate from the socket callback).
    if (b.own !== mySlot) {
      pendingBeamFx.push({ a: start, b: b.b, w: b.w });
      if (pendingBeamFx.length > PENDING_FX_CAP) pendingBeamFx.shift();
    }
  }
  netBeams = netBeams.filter((b) => b.die > now);
  if (snap.ev) pendingCombatEv |= snap.ev;
  if (snap.dmg) {
    for (const d of snap.dmg) pendingDamage.push(d);
    if (pendingDamage.length > 64) pendingDamage.splice(0, pendingDamage.length - 64);
  }
  if (typeof snap.rulesHash === "number" && snap.rulesHash !== lastRulesHash) {
    lastRulesHash = snap.rulesHash;
    menu?.setServerRulesHash(snap.rulesHash);
  }
  if (!onDownstreamTick(snap.tick)) return;
  snapBuffer.push(now, snap);
  netStatsOnSnapshot(snap.tick, now);
  lastSelf = snap.players.find((p) => p.slot === mySlot) ?? null;
  lastPlayers = snap.players;
  if (snap.match) lastMatch = snap.match;
  latestHeals = snap.heals ?? [];
  latestFlags = snap.flags ?? [];
  latestEntities = snap.entities ?? [];
  // NOTE: no per-snapshot HUD/DOM writes here — `updateCombatHud()` runs on the
  // throttled frame cadence in the loop, so snapshots don't touch the DOM.
  if (sessionId !== null && typeof snap.ack === "number" && snap.ps) {
    // Coalesce: keep only the latest ack/ps and run the rollback+replay once per
    // frame (below), so the WebSocket handler stays cheap per snapshot.
    pendingReconcile = { ack: snap.ack, ps: snap.ps };
  }
}
/** Record a snapshot arrival for the netgraph (jitter / gaps / rate). */
function netStatsOnSnapshot(tick: number, now: number): void {
  netSnapCount++;
  if (lastSnapArrival > 0) {
    const dt = now - lastSnapArrival;
    ngSnapHist.push(dt);
    if (ngSnapHist.length > 48) ngSnapHist.shift();
    // Rate-agnostic jitter: EWMA of deviation from the mean interval (no fixed
    // expected rate, so this survives a server rate change).
    snapIntervalMean += 0.15 * (dt - snapIntervalMean);
    netJitterMs += 0.15 * (Math.abs(dt - snapIntervalMean) - netJitterMs);
  }
  lastSnapArrival = now;
  if (lastSnapTick >= 0) {
    const dTick = tick - lastSnapTick;
    if (expectedTickStep === 0) expectedTickStep = dTick;
    if (dTick > expectedTickStep) snapGaps++;
  }
  lastSnapTick = tick;
}

/** p50/p75/p95/p99 of the input-application latency samples (ms). */
function mouseLatencyStats(): { p50: number; p75: number; p95: number; p99: number; n: number } {
  const n = mouseLatencies.length;
  if (n === 0) return { p50: 0, p75: 0, p95: 0, p99: 0, n: 0 };
  const s = [...mouseLatencies].sort((a, b) => a - b);
  const at = (p: number) => s[Math.min(s.length - 1, Math.floor((p / 100) * s.length))];
  return { p50: at(50), p75: at(75), p95: at(95), p99: at(99), n };
}

/** Our server-measured RTT (ms), from the roster. */
function myPing(): number {
  return roster.find((r) => r.slot === mySlot)?.ping ?? 0;
}

/** Expected server rewind for our next shot (ticks), from the core formula. */
function rewindTicks(): number {
  return core.networld_lag_ticks(myPing(), INTERP_MS);
}

/** Render the `?netstats=1` netgraph. Called from the fps block. */
function updateNetGraph(dt: number): void {
  if (!net?.connected) {
    hudRuntime?.setShown("netgraph", false);
    return;
  }
  netSnapRate = dt > 0 ? netSnapCount / dt : 0;
  netSnapCount = 0;
  const blocks = "▁▂▃▄▅▆▇█";
  const spark = (arr: number[], max: number) =>
    arr.length === 0
      ? "·"
      : arr.map((v) => blocks[Math.min(7, Math.max(0, Math.floor((v / max) * 8)))]).join("");
  const snapMax = Math.max(1, ...ngSnapHist);
  const corrTop = Math.max(1, ...ngCorrHist);
  const ml = mouseLatencyStats();
  hudRuntime?.setShown("netgraph", true);
  netGraphEl.textContent =
    `net  ping ${myPing()}ms  jit ${netJitterMs.toFixed(1)}ms  snap ${netSnapRate.toFixed(0)}/s` +
    `  gap ${snapGaps}  rewind ${rewindTicks()}t  interp ${INTERP_MS}ms${interpOverride ? " (pin)" : ""}\n` +
    `input ${ml.p50.toFixed(1)}/${ml.p75.toFixed(1)}/${ml.p95.toFixed(1)}/${ml.p99.toFixed(1)} ms (p50/75/95/99, n=${ml.n})\n` +
    `SyncError ${lastCorr.toFixed(2)}u · Sponge ${Sponge().smoothed.toFixed(2)}u (max ${corrMax.toFixed(1)}u, n=${corrCount})\n` +
    `snap ${spark(ngSnapHist, snapMax)}\n` +
    `corr ${spark(ngCorrHist, corrTop)}`;
}
// ---- Spectating / death cam ----------------------------------------------

function teamModeActive(): boolean {
  return lastPlayers.some((p) => (p.team ?? 0) > 0);
}

function isSpectating(): boolean {
  if (!net?.connected || !lastSelf) return false;
  return !lastSelf.alive || (lastSelf.team === 0 && teamModeActive());
}

function spectatable(): NetPlayerState[] {
  return lastPlayers
    .filter((p) => p.slot !== mySlot && p.alive)
    .sort((a, b) => (a.team ?? 0) - (b.team ?? 0) || a.slot - b.slot);
}

function pickSpectateTarget(prefer = -1): void {
  const list = spectatable();
  if (list.length === 0) {
    spectateTarget = -1;
    return;
  }
  if (prefer >= 0 && list.some((p) => p.slot === prefer)) {
    spectateTarget = prefer;
    return;
  }
  const myTeam = lastSelf?.team ?? 0;
  const mate = list.find((p) => myTeam > 0 && p.team === myTeam);
  spectateTarget = (mate ?? list[0]).slot;
}

function cycleSpectate(dir: number): void {
  const list = spectatable();
  if (list.length === 0) return;
  const i = list.findIndex((p) => p.slot === spectateTarget);
  const next = i < 0 ? 0 : (i + dir + list.length) % list.length;
  spectateTarget = list[next].slot;
  showAnnounce(`spectating ${playerNamePlain(spectateTarget)}`, 1000);
}

function spectatePose(now: number): { eye: Float32Array; yaw: number; pitch: number } | null {
  if (spectateTarget < 0) pickSpectateTarget();
  const states = snapBuffer.remoteStates(now, mySlot);
  const st = states.find((s) => s.slot === spectateTarget);
  if (!st) return null;
  const eye = [st.origin[0], st.origin[1], st.origin[2] + 30];
  if (spectateMode === 1) {
    const cp = Math.cos(st.pitch);
    const fwd = [Math.cos(st.yaw) * cp, Math.sin(st.yaw) * cp, Math.sin(st.pitch)];
    eye[0] -= fwd[0] * 140;
    eye[1] -= fwd[1] * 140;
    eye[2] -= fwd[2] * 140 - 12;
  }
  return {
    eye: new Float32Array(eye),
    yaw: st.yaw,
    pitch: st.pitch,
  };
}

function updateSpectateHud(): void {
  const on = isSpectating();
  hudRuntime?.setShown("spectate-hud", on);
  if (!on) return;
  if (spectateTarget < 0) pickSpectateTarget();
  const text = `spectating ${playerNamePlain(spectateTarget)} · X/Z cycle · V ${spectateMode ? "chase" : "first person"}`;
  if (text !== lastSpectateKey) {
    spectateHudEl.textContent = text;
    lastSpectateKey = text;
  }
}

/** Update the combat HUD (server snapshot, or the local session in practice). */
function updateCombatHud(): void {
  const show = sessionId !== null;
  hudRuntime?.setShown("combat-hud", show);
  if (!show || sessionId === null) return;
  let hp: number, ar: number, wp: number, am: number;
  // While detached (local practice online) the local sim owns our combat state.
  if (net?.connected && lastSelf && !practiceDetached()) {
    ({ hp, ar, wp, am } = lastSelf);
  } else {
    const c = core.session_combat(sessionId) as unknown as Float32Array;
    if (c.length < 4) return;
    hp = c[0];
    ar = c[1];
    wp = c[2];
    am = c[3];
  }
  if (!hudEditorOpen) {
    hudRuntime?.setValue("health", Math.max(0, Math.round(hp)));
    hudRuntime?.setValue("armor", Math.max(0, Math.round(ar)));
    hudRuntime?.setValue("ammo", Math.max(0, Math.round(am)));
  }
  hudHealthEl.textContent = String(Math.max(0, Math.round(hp)));
  hudArmorEl.textContent = String(Math.max(0, Math.round(ar)));
  const extra =
    (net?.connected && lastSelf?.healBall ? `  ·  G ${lastSelf.healBall}` : "") +
    (net?.connected && lastSelf?.siphonator ? "  ·  SIPHON" : "");
  hudWeaponEl.textContent = (wp > 0 ? WEAPON_NAMES[wp] ?? "" : "") + extra;
  hudAmmoEl.textContent = String(Math.max(0, Math.round(am)));
}

// ---- Match HUD / scoreboard / kill feed ---------------------------------

function matchTime(ms: number): string {
  const s = Math.max(0, Math.ceil(ms / 1000));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
}

function playerTeam(slot: number): number {
  return lastPlayers.find((p) => p.slot === slot)?.team ?? 0;
}

function playerName(slot: number): string {
  if (slot < 0 || slot === 255) return "world";
  const r = roster.find((x) => x.slot === slot);
  return r?.nick || `player ${slot}`;
}

/** `playerName` without Quake colour codes, for plain-text HUD slots. */
function playerNamePlain(slot: number): string {
  return stripQ3Colors(playerName(slot));
}

function updateFlagHud(): void {
  const show = (net?.connected ?? false) && latestFlags.some((f) => f.present);
  hudRuntime?.setShown("flag-hud", show);
  if (!show) {
    lastFlagKey = "";
    return;
  }
  let key = "";
  for (const f of latestFlags) {
    key += `${f.present ? 1 : 0}${f.state}${f.carrier}${f.state === 2 ? Math.ceil(f.returnMs / 1000) : 0}|`;
  }
  if (key === lastFlagKey) return;
  lastFlagKey = key;
  const parts: string[] = [];
  for (const f of latestFlags) {
    if (!f.present) continue;
    const cls = f.team === 1 ? "red" : "blue";
    const team = f.team === 1 ? "RED" : "BLU";
    const state =
      f.state === 0
        ? "home"
        : f.state === 1
          ? `carried by ${q3Html(playerName(f.carrier))}`
          : `dropped ${Math.max(0, Math.ceil(f.returnMs / 1000))}s`;
    parts.push(`<span class="${cls}">${team}</span> ${state}`);
  }
  flagHudEl.innerHTML = parts.join(" &nbsp;·&nbsp; ");
}

function updateMatchHud(): void {
  const online = net?.connected ?? false;
  const m = lastMatch;
  // The 15 s match-over screen always shows the results scoreboard.
  const results = m?.phase === "matchover";
  hudRuntime?.setShown("match-hud", online && !!m);
  scoreboardEl.classList.toggle("show", online && (scoreboardVisible || results));
  if (!online || !m) return;
  updateSpectateHud();
  updateFlagHud();

  const teamMode = lastPlayers.some((p) => (p.team ?? 0) > 0);
  if (teamMode) {
    const html = `<span class="red">${m.score[0]}</span> : <span class="blue">${m.score[1]}</span>`;
    if (html !== lastScoreHtml) {
      matchScoreEl.innerHTML = html;
      lastScoreHtml = html;
    }
  } else {
    let best: { name: string; frags: number } | null = null;
    for (const p of lastPlayers) {
      const f = p.frags ?? 0;
      if (!best || f > best.frags) best = { name: playerName(p.slot), frags: f };
    }
    const html = best
      ? `<span class="ffa">${q3Html(best.name)} · ${best.frags}</span>`
      : `<span class="ffa">FFA</span>`;
    if (html !== lastScoreHtml) {
      matchScoreEl.innerHTML = html;
      lastScoreHtml = html;
    }
  }

  let phase = "";
  if (m.phase === "warmup") {
    // Readiness gate: the match only starts once the assigned players ready up.
    const mine = lastSelf?.ready;
    phase =
      mine === false && serverWarmupReady
        ? "warmup — press F3 to ready"
        : "warmup — waiting for players";
  }
  else if (m.phase === "countdown") phase = "starting…";
  else if (m.phase === "live") {
    phase = m.golden ? "golden frag" : m.overtime ? "overtime" : "live";
    if (m.round > 0) phase = `round ${m.round} · ${phase}`;
  } else if (m.phase === "roundover") {
    phase = `round ${m.round} over`;
  } else if (m.phase === "matchover") {
    const winner =
      m.winnerTeam === 1
        ? "red wins"
        : m.winnerTeam === 2
          ? "blue wins"
          : m.winnerSlot >= 0
            ? `${playerNamePlain(m.winnerSlot)} wins`
            : "draw";
    phase = `match over — ${winner}`;
  }
  matchPhaseEl.textContent = phase;
  if (m.round > 0) {
    matchTimerEl.textContent =
      m.phase === "live"
        ? m.timeLeftMs > 0
          ? `${matchTime(m.timeLeftMs)} · round wins`
          : "round wins"
        : m.phase === "roundover"
          ? "round wins"
          : "";
  } else {
    matchTimerEl.textContent =
      m.phase === "live" || m.phase === "countdown"
        ? m.timeLeftMs > 0
          ? matchTime(m.timeLeftMs)
          : "—"
        : "";
  }
  matchHudEl.classList.toggle("golden", m.golden);

  if (!scoreboardVisible && !results) return;
  scoreboardTitleEl.textContent = `${net?.modeLabel || "match"} · ${phase}`;
  const rows = [...lastPlayers].sort(
    (a, b) => (b.frags ?? 0) - (a.frags ?? 0) || (a.deaths ?? 0) - (b.deaths ?? 0),
  );
  scoreboardBodyEl.innerHTML = "";
  for (const p of rows) {
    const tr = document.createElement("tr");
    const team = p.team ?? 0;
    if (team === 1) tr.className = "red";
    else if (team === 2) tr.className = "blue";
    if (p.slot === mySlot) tr.classList.add("me");
    const r = roster.find((x) => x.slot === p.slot);
    tr.innerHTML =
      `<td>${team === 1 ? "RED" : team === 2 ? "BLU" : "—"}</td>` +
      `<td>${q3Html(r?.nick || `player ${p.slot}`)}${p.ready === false ? " <span style=\"color:var(--ui-text-dim)\">(not ready)</span>" : ""}</td>` +
      `<td class="num">${p.frags ?? 0}</td>` +
      `<td class="num">${p.deaths ?? 0}</td>` +
      `<td class="num">${r?.ping ?? 0}</td>`;
    scoreboardBodyEl.append(tr);
  }
}

function addKillFeed(kills: NetKill[]): void {
  for (const k of kills) {
    if (k.victim === mySlot) {
      pickSpectateTarget(k.killer === 255 ? -1 : k.killer);
    }
    const el = document.createElement("div");
    el.className = "kf";
    const victim = q3Html(playerName(k.victim));
    const vt = playerTeam(k.victim);
    const vClass = vt === 1 ? "red" : vt === 2 ? "blue" : "";
    const weapon = WEAPON_NAMES[k.weapon] ?? "?";
    if (k.killer === 255 || k.killer === k.victim) {
      el.innerHTML = `<span class="${vClass}">${victim}</span> died`;
    } else {
      const killer = q3Html(playerName(k.killer));
      const kt = playerTeam(k.killer);
      const kClass = kt === 1 ? "red" : kt === 2 ? "blue" : "";
      el.innerHTML =
        `<span class="${kClass}">${killer}</span> <span class="w">[${weapon}]</span> ` +
        `<span class="${vClass}">${victim}</span>${k.teamKill ? " (team)" : ""}`;
    }
    killFeedEl.append(el);
    while (killFeedEl.children.length > 7) killFeedEl.firstElementChild?.remove();
    window.setTimeout(() => el.remove(), 6000);
  }
}

/** Select a weapon (server-authoritative; local while practice-detached). */
function selectWeapon(w: number, strong = true): void {
  // While detached (local practice online) select locally: the server isn't
  // simulating us, and it would only reset the choice on the next restart.
  if (net?.connected && !practiceDetached()) net.selectWeapon(w, strong);
  else if (sessionId !== null) core.session_select_weapon(sessionId, w, strong);
}

/** The locally-known current weapon (server snapshot; session fallback). */
function currentWeapon(): number | null {
  if (net?.connected && !practiceDetached()) return lastSelf ? lastSelf.wp : null;
  if (sessionId !== null) return core.session_weapon(sessionId);
  return null;
}

/** Global values the weapon profiles inherit from. */
function weaponGlobals() {
  return {
    sensitivity,
    fov,
    hitSounds: true,
    // The slot selected in the crosshair editor is the one in use (per-weapon
    // overrides still win); defaultId is only the post-deletion fallback.
    crosshair: crosshairStore.activeId || crosshairStore.defaultId,
  };
}

/** Push the resolved sensitivity + multipliers into the core (input lives there). */
function pushMouseConfig(): void {
  if (sessionId === null) return;
  const zooming = zoomT > 0.001;
  const sens = zooming ? weaponProfile.zoomSensitivity : weaponProfile.sensitivity;
  const coreAny = core as unknown as {
    session_set_mouse_config?: (id: number, s: number, yaw: number, pitch: number) => void;
  };
  if (coreAny.session_set_mouse_config) {
    coreAny.session_set_mouse_config(sessionId, sens, weaponProfile.yawMultiplier, weaponProfile.pitchMultiplier);
  } else {
    core.session_set_sensitivity(sessionId, sens);
  }
  // Mirror what the sim will actually apply (camera diagnostic).
  diagSens = sens;
  diagYawMult = weaponProfile.yawMultiplier;
  diagPitchMult = weaponProfile.pitchMultiplier;
}

/** Apply the current weapon's resolved profile to the HUD + input. */
function applyWeaponProfile(weapon: number | null): void {
  weaponProfile = resolveWeapon(weaponSettings, weaponGlobals(), weapon);
  crosshairHud?.setConfig(configFor(crosshairStore, weaponProfile.crosshair));
  crosshairHud?.setZoomConfig(
    weaponProfile.zoomCrosshair ? configFor(crosshairStore, weaponProfile.zoomCrosshair) : null,
  );
  audio.setHitSoundsEnabled(weaponProfile.hitSounds);
  pushMouseConfig();
}

/** Drop any zoom state (weapon switch / respawn / profile change). */
function resetZoom(): void {
  zoomHeld = false;
  zoomToggled = false;
  mouse4ZoomEngaged = false;
  zoomT = 0;
  pushMouseConfig();
}

/** The Mouse 4 combo: zoom when this weapon has no secondary of interest. */
function mouse4EngagesZoom(): boolean {
  return weaponProfile.mouse4 === "zoom" && weaponProfile.zoomEnabled;
}

function pressZoom(): void {
  if (!weaponProfile.zoomEnabled) return;
  if (weaponProfile.zoomToggle) zoomToggled = !zoomToggled;
  else zoomHeld = true;
  pushMouseConfig();
}

function releaseZoom(): void {
  if (weaponProfile.zoomToggle) return;
  zoomHeld = false;
  pushMouseConfig();
}

/** Feed combat events into the crosshair animation triggers. */
function crosshairSignals(ev: number): void {
  if (!crosshairHud || !ev) return;
  const now = performance.now();
  if (ev & EV_HIT) crosshairHud.fire("hit", now);
  if (ev & EV_KILL_CONFIRM) crosshairHud.fire("kill", now);
  if (ev & EV_PAIN) crosshairHud.fire("damaged", now);
  if (ev & EV_FIRE) crosshairHud.fire("fire", now);
  if (ev & EV_JUMP) crosshairHud.fire("jump", now);
  if (ev & EV_LAND) crosshairHud.fire("land", now);
}

// ---- Hit feedback: hitmarker + floating damage numbers --------------------

let hitmarkerStart = -1;
let hitmarkerKill = false;

interface FloatingDamage {
  el: HTMLDivElement;
  victim: number;
  value: number;
  x: number;
  y: number;
  born: number;
  dur: number;
  rise: number;
  opacity: number;
  kill: boolean;
}
const floatingDamage: FloatingDamage[] = [];
const pendingDamage: { v: number; a: number; w: number; k?: boolean }[] = [];
const FLOATING_DAMAGE_CAP = 32;

/** Start (or restart) the hitmarker pulse. */
function triggerHitmarker(kill: boolean, now: number): void {
  const fb = settingsStore.value().hitFeedback;
  if (!fb.hitmarker) return;
  hitmarkerStart = now;
  hitmarkerKill = kill;
}

/** Draw/fade the hitmarker canvas. Called once per rendered frame. */
function updateHitmarker(now: number): void {
  const fb = settingsStore.value().hitFeedback;
  const ctx = hitmarkerCanvas.getContext("2d");
  if (!ctx) return;
  const active = fb.hitmarker && hitmarkerStart >= 0 && now - hitmarkerStart < fb.hitmarkerDuration;
  if (!active) {
    if (hitmarkerStart >= 0) {
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.clearRect(0, 0, hitmarkerCanvas.width, hitmarkerCanvas.height);
      hitmarkerCanvas.classList.remove("on");
      hitmarkerStart = -1;
    }
    return;
  }
  const dpr = window.devicePixelRatio || 1;
  const px = Math.round(128 * dpr);
  if (hitmarkerCanvas.width !== px) {
    hitmarkerCanvas.width = px;
    hitmarkerCanvas.height = px;
  }
  const stage = hudStageRect();
  hitmarkerCanvas.style.left = `${stage.x + stage.w / 2}px`;
  hitmarkerCanvas.style.top = `${stage.y + stage.h / 2}px`;
  const t = Math.min(1, (now - hitmarkerStart) / Math.max(1, fb.hitmarkerDuration));
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  ctx.clearRect(0, 0, 128, 128);
  ctx.globalAlpha = Math.max(0, Math.min(1, fb.hitmarkerOpacity * (1 - t)));
  ctx.strokeStyle = hitmarkerKill ? fb.hitmarkerKillColor : fb.hitmarkerColor;
  ctx.fillStyle = ctx.strokeStyle;
  ctx.lineCap = "round";
  ctx.lineWidth = fb.hitmarkerThickness;
  const cx = 64;
  const cy = 64;
  if (fb.hitmarkerStyle === "dot") {
    ctx.beginPath();
    ctx.arc(cx, cy, Math.max(1, fb.hitmarkerThickness), 0, Math.PI * 2);
    ctx.fill();
  } else {
    const angles =
      fb.hitmarkerStyle === "cross"
        ? [0, Math.PI / 2, Math.PI, (3 * Math.PI) / 2]
        : [Math.PI / 4, (3 * Math.PI) / 4, (5 * Math.PI) / 4, (7 * Math.PI) / 4];
    const r0 = fb.hitmarkerSize * 0.22;
    const r1 = fb.hitmarkerSize * 0.5;
    for (const a of angles) {
      ctx.beginPath();
      ctx.moveTo(cx + Math.cos(a) * r0, cy + Math.sin(a) * r0);
      ctx.lineTo(cx + Math.cos(a) * r1, cy + Math.sin(a) * r1);
      ctx.stroke();
    }
  }
  hitmarkerCanvas.classList.add("on");
}

/** Drain queued server damage events into floating screen numbers. */
function spawnDamageNumbers(now: number, vp: Float32Array, w: number, h: number): void {
  if (!pendingDamage.length) return;
  const fb = settingsStore.value().hitFeedback;
  if (fb.damageNumbers) {
    const remotes = snapBuffer.remoteStates(now, mySlot);
    const stage = hudStageRect();
    for (const d of pendingDamage) {
      const rp = remotes.find((r) => r.slot === d.v);
      let x = stage.x + stage.w / 2;
      let y = stage.y + stage.h / 2;
      if (rp) {
        const p = projectToScreen(vp, [rp.origin[0], rp.origin[1], rp.origin[2] + 40], w, h);
        if (p.visible) {
          x = p.x;
          y = p.y;
        }
      }
      // Aggregate rapid hits (LG ticks, splash) into one number per victim so
      // the screen doesn't fill with 1-HP pops.
      const existing = floatingDamage.find((n) => n.victim === d.v && !n.kill);
      if (existing) {
        existing.value += Math.max(0, Math.round(d.a));
        existing.el.textContent = String(existing.value);
        if (d.k) {
          existing.kill = true;
          existing.el.style.color = fb.damageNumberKillColor;
        }
        // Continue the rise from where the number currently is, and keep it
        // alive while damage keeps landing (no jump back to the spawn point).
        const age = Math.min(1, (now - existing.born) / Math.max(1, existing.dur));
        existing.y -= existing.rise * age;
        existing.born = now;
        continue;
      }
      const amount = Math.max(0, Math.round(d.a));
      const el = document.createElement("div");
      el.className = "dmg-num";
      el.textContent = String(amount);
      el.style.color = d.k ? fb.damageNumberKillColor : fb.damageNumberColor;
      el.style.fontSize = `${fb.damageNumberSize}px`;
      el.style.left = `${x.toFixed(1)}px`;
      el.style.top = `${y.toFixed(1)}px`;
      el.style.opacity = String(fb.damageNumberOpacity);
      damageNumbersEl.appendChild(el);
      floatingDamage.push({
        el,
        victim: d.v,
        value: amount,
        x,
        y,
        born: now,
        dur: fb.damageNumberDuration,
        rise: fb.damageNumberRise,
        opacity: fb.damageNumberOpacity,
        kill: !!d.k,
      });
    }
    while (floatingDamage.length > FLOATING_DAMAGE_CAP) floatingDamage.shift()?.el.remove();
  }
  pendingDamage.length = 0;
}

/** Rise + fade active damage numbers. Called once per rendered frame. */
function updateDamageNumbers(now: number): void {
  for (let i = floatingDamage.length - 1; i >= 0; i--) {
    const n = floatingDamage[i];
    const t = (now - n.born) / Math.max(1, n.dur);
    if (t >= 1) {
      n.el.remove();
      floatingDamage.splice(i, 1);
      continue;
    }
    n.el.style.top = `${(n.y - n.rise * t).toFixed(1)}px`;
    n.el.style.opacity = String(n.opacity * (1 - t));
  }
}

/** Advance the zoom blend and feed the projection. */
function updateZoom(dtMs: number): void {
  const canZoom = weaponProfile.zoomEnabled;
  const want = canZoom && (weaponProfile.zoomToggle ? zoomToggled : zoomHeld);
  const target = want ? 1 : 0;
  const before = zoomT;
  const time = weaponProfile.zoomTime;
  if (!canZoom || time <= 0) {
    zoomT = target;
  } else if (target > zoomT) {
    zoomT = Math.min(target, zoomT + dtMs / time);
  } else {
    zoomT = Math.max(target, zoomT - dtMs / time);
  }
  const e = zoomT * zoomT * (3 - 2 * zoomT);
  const zf = Math.min(weaponProfile.zoomFov, weaponProfile.fov - 1);
  renderFov = canZoom ? weaponProfile.fov + (zf - weaponProfile.fov) * e : weaponProfile.fov;
  if ((before > 0.001) !== (zoomT > 0.001)) pushMouseConfig();
  crosshairHud?.setZoomed(zoomT > 0.5);
}

/** Cycle the selected weapon (dir +1 / -1). */
function cycleWeapon(dir: number): void {
  const cur = currentWeapon();
  if (cur === null) return;
  let i = WEAPON_ORDER.indexOf(cur);
  i = i < 0 ? 0 : (i + dir + WEAPON_ORDER.length) % WEAPON_ORDER.length;
  const w = WEAPON_ORDER[i];
  selectWeapon(w, true);
  showAnnounce(WEAPON_NAMES[w] ?? `weapon ${w}`, 900);
}

/** Play local combat sounds from the server event bits (EV_* in core/lib.rs). */
function handleCombatEvents(ev: number): void {
  if (ev & (EV_HIT | EV_KILL_CONFIRM)) {
    triggerHitmarker((ev & EV_KILL_CONFIRM) !== 0, performance.now());
  }
  if (ev & EV_FIRE) {
    const s = WEAPON_FIRE_SOUND[lastSelf?.wp ?? 0];
    if (s) audio.play(s);
  }
  if (ev & EV_DEATH) showAnnounce("you died", 1500);
  audio.handleBits(ev);
  crosshairSignals(ev);
}

/** Drain presentation work queued by the WebSocket callbacks. */
function flushNetworkPresentation(): void {
  if (pendingCombatEv) {
    handleCombatEvents(pendingCombatEv);
    pendingCombatEv = 0;
  }
  for (const b of pendingBeamFx) {
    if (b.w === 8) {
      emitRailEffect(b.a, b.b, b.w);
    } else {
      const dx = b.b[0] - b.a[0];
      const dy = b.b[1] - b.a[1];
      const dz = b.b[2] - b.a[2];
      const m = Math.hypot(dx, dy, dz) || 1;
      emitWeaponParticles(b.w, () =>
        particles?.spark(b.b, [dx / m, dy / m, dz / m], hexToRgb(vfxColorSparks)),
      );
      emitWeaponSmoke(b.w, () => smoke?.wisp(b.b, hexToRgb(vfxColorSmoke)));
    }
  }
  pendingBeamFx.length = 0;
  if (pendingKills.length) {
    const kills = pendingKills;
    pendingKills = [];
    addKillFeed(kills);
  }
  for (const c of pendingCaptures) {
    const who = c.team === 1 ? "red" : "blue";
    showAnnounce(`${who} captured the flag`, 2200);
    const el = document.createElement("div");
    el.className = "kf";
    el.innerHTML = `<span class="${who}">${who}</span> captured the flag`;
    killFeedEl.append(el);
    while (killFeedEl.children.length > 7) killFeedEl.firstElementChild?.remove();
    window.setTimeout(() => el.remove(), 6000);
  }
  pendingCaptures = [];
  for (const c of pendingChat) addChatLine(c.sys ? "" : c.nick, c.text, c.sys);
  pendingChat = [];
  for (const p of pendingPings) addPingMark(p);
  pendingPings = [];
}

// The last finish we submitted, keyed by session + tick count. Submitting with a
// plain boolean was buggy: `loadMap` reset it before the new session existed, so
// the old session's lingering finish got submitted under the *new* map and then
// the real finish was suppressed.
let lastSubmitted: { session: number; ticks: number; map: string } | null = null;
let fov = settingsStore.value().fov; // horizontal FOV (owned by the settings store)
/** Panini warp strength: 0 = rectilinear (persisted; `?proj=` can override). */
let panini = settingsStore.value().panini;
let projectionOverride = false;
/** Current mouse sensitivity (mirrors the settings store; for the console `sens`). */
let sensitivity = settingsStore.value().sensitivity;
// Layered crosshair + per-weapon settings (mirrored from the store).
let crosshairStore: CrosshairStore = settingsStore.value().crosshair;
let weaponSettings: WeaponSettings = settingsStore.value().weaponSettings;
let crosshairHud: CrosshairHud | null = null;
let weaponProfile = resolveWeapon(
  weaponSettings,
  { sensitivity, fov, hitSounds: true, crosshair: crosshairStore.activeId },
  null,
);

// Per-weapon VFX resolution, cached until the weapon-settings object changes.
// Effects for remotes/impacts need any weapon id, not just the held one.
let vfxCacheSrc: WeaponSettings | null = null;
const vfxCache = new Map<number, ResolvedWeapon>();
function weaponVfx(weapon: number): ResolvedWeapon {
  if (vfxCacheSrc !== weaponSettings) {
    vfxCache.clear();
    vfxCacheSrc = weaponSettings;
  }
  let r = vfxCache.get(weapon);
  if (!r) {
    r = resolveWeapon(weaponSettings, weaponGlobals(), weapon);
    vfxCache.set(weapon, r);
  }
  return r;
}
let appliedWeapon: number | null | undefined = undefined;
let zoomHeld = false;
let zoomToggled = false;
let mouse4ZoomEngaged = false;
let zoomT = 0;
let renderFov = fov;
let lastFrameMs = 0;
let hudSettings: HudSettings = settingsStore.value().hud;
const crosshairCanvas = document.getElementById("crosshair") as HTMLCanvasElement | null;
if (crosshairCanvas) {
  crosshairHud = new CrosshairHud(crosshairCanvas, configFor(crosshairStore, crosshairStore.defaultId));
}

// HUD layout + editor state.
let hudLayout: HudLayoutStore = settingsStore.value().hudLayout;
let hudRuntime: HudLayoutRuntime | null = null;
let hudEditorOpen = false;
let lastSpectate = false;
hudRuntime = new HudLayoutRuntime(hudLayout.playing);
// Game-state visibility starts off; the HUD update functions switch it on. The
// layout runtime is the single owner of whether a HUD element is drawn.
for (const id of [
  "combat-hud",
  "match-hud",
  "spectate-hud",
  "flag-hud",
  "timer",
  "recall-hud",
  "vote-hud",
  "netgraph",
]) {
  hudRuntime.setShown(id, false);
}

/** Apply the correct layout document (player or spectate). */
function applyHudDoc(): void {
  if (!hudRuntime) return;
  const spectating = isSpectating();
  const doc = spectating && !hudLayout.spectateUsePlayer ? hudLayout.spectate : hudLayout.playing;
  hudRuntime.setDoc(doc);
}

/** Current master volume 0..1 (mirrors the settings; for the console). */
let wireframe = false; // debug wireframe rendering
/** Presentation-only wireframe overlay while a menu/palette is open. */
let wireframePreview = false;
const wireframePreviewOwners = new Set<string>();

/** The renderer shows wireframe when the user enabled it OR a UI preview owns it. */
function applyWireframe(): void {
  renderer?.setWireframe(wireframe || wireframePreview);
}

/** Add/remove a preview owner (menu, palette). Does not touch the user's setting. */
function previewWireframe(owner: string, on: boolean): void {
  if (on) wireframePreviewOwners.add(owner);
  else wireframePreviewOwners.delete(owner);
  wireframePreview = wireframePreviewOwners.size > 0;
  applyWireframe();
}
let textureMode: TextureMode = "anisotropic"; // texture filtering quality
let profiler: Profiler | null = null; // in-app frame profiler
let profileEnabled = false; // profiler overlay on/off
let fpsCap = 0; // 0 = uncapped; else target frames/sec (trace-friendly runs)
let pvsEnabled = true; // PVS (LUMP_VISIBILITY) world culling; `?pvs=0` disables
// View-frustum culling on top of PVS (per-face AABB test in the core);
// `?frustum=0` disables.
let frustumEnabled = true;
// Cel-outline shells (`q3map_invert` + `q3map_offset`) are compiler-baked
// duplicate surfaces that cover the world when drawn as normal geometry; skip
// them only when face culling is off (they need culling to render as outlines).
// `?inkshell=0` forces them off.
let showOutlineShells = true;
// Face culling for world materials (`?cull=0` disables for A/B screenshots).
let cullEnabled = true;
// Debug override for every world material's cull mode (`?cullmode=front|back|none`).
let cullOverride: CullMode | null = null;
// Perspective near/far. `near` is Q3's default (4); `far` is derived per map
// from the world bounds so depth precision isn't wasted on a 200k range.
const CAMERA_NEAR = 4;
let cameraFar = 16384;
// MSAA (`antialias: true`): thin trims/edges alias badly without it; `?msaa=0`
// disables it (some low-end GPUs).
let msaaEnabled = true;
// `?lightmap=0` disables baked lightmaps (debug: isolates lightmap seams).
let lightmapEnabled = true;
// `?diag=1` prints the renderer/GPU/map diagnostics and opens the console
// once the first map has loaded (bug reports from other machines).
let diagOnLoad = false;
let frustumPlanes = new Float32Array(24); // reusable, reused each frame

/** Point-in-frustum test (planes filled by `extractFrustum`). */
function pointInFrustum(p: [number, number, number], margin = 0): boolean {
  for (let i = 0; i < 6; i++) {
    const a = frustumPlanes[i * 4];
    const b = frustumPlanes[i * 4 + 1];
    const c = frustumPlanes[i * 4 + 2];
    const d = frustumPlanes[i * 4 + 3];
    if (a * p[0] + b * p[1] + c * p[2] + d < -margin) return false;
  }
  return true;
}
const EMPTY_F32 = new Float32Array(0); // disables the core frustum test
// Pointer lock asks for `unadjustedMovement` (raw input, no OS acceleration) by
// default; toggleable for mice that apply their own driver-level curve.
let rawMouseInput = true;
let lowPowerGpu = false; // `?gpu=low`: let the browser pick the adapter
let visRuns: Uint32Array | null = null; // reusable per-frame visible-run buffer
let visScratch: Uint32Array | null = null; // reusable PVS-only buffer (debug A/B)
let lastEye = new Float32Array(3); // eye of the last rendered frame (debug A/B)
// Canvas CSS size, cached on resize. Reading `canvas.clientWidth` every frame
// forces a layout flush (trace: 284ms / 3% on 13octo).
let canvasCssW = 0;
let canvasCssH = 0;
// Fog parameters of each shader that defines a Q3 fog volume (shader id -> parms).
let fogByShader = new Map<number, FogParms>();
let lastFrustumRuns = -1; // PVS+frustum run count of the last rendered frame
let menu: Menu | null = null;
// Recall keyframe transition: a short camera interpolation between keyframes
// (view-only; the sim stays frozen).
let recallAnim: {
  fromEye: [number, number, number];
  fromYaw: number;
  fromPitch: number;
  toEye: [number, number, number];
  toYaw: number;
  toPitch: number;
  start: number;
} | null = null;
const RECALL_ANIM_MS = 180;
let binds: BindMap = settingsStore.value().binds;
let actionByCode = codeToAction(binds);

/** Persist console bind changes through the menu's settings store. */
function persistBinds(): void {
  menu?.applyExternalBinds(binds);
}
const moveHud = new MovementHud();
// Sound: maps gameplay event bits + menu assignments to files under `snd/`.
const audio = new SoundManager();
audio.setHitVolume(settingsStore.value().hitFeedback.hitSoundVolume);
// Browsers require a user gesture before audio can start; unlock on the first
// pointer/key anywhere (covers the Play button, canvas pointer-lock, previews).
const unlockAudio = () => audio.unlock();
window.addEventListener("pointerdown", unlockAudio, { capture: true });
window.addEventListener("keydown", unlockAudio, { capture: true });
// Persist the last recorded run's ghost when leaving the page.
window.addEventListener("beforeunload", () => {
  if (activeMap) saveRecording(activeMap);
});
// Pool for per-mover model matrices (reused each frame to avoid GC churn).
const moverMatrixPool: Float32Array[] = [];
// Per-frame render arrays, cleared + reused each frame to avoid allocation churn.
const frameMovers: Float32Array[] = [];
const frameInstances: ModelInstance[] = [];

// Runtime MD3 models (map objects now; items/weapons later). The debug hook
// below populates this for testing; game code would spawn models the same way.
interface ModelEntity {
  model: number;
  origin: [number, number, number];
  angles: [number, number, number];
  scale: number;
  animFps: number;
  numFrames: number;
  color: [number, number, number];
  alpha: number;
  /** Debug/verification: force a fixed frame blend instead of animating. */
  forceFrame?: { frame0: number; frame1: number; lerp: number };
}
const modelEntities: ModelEntity[] = [];

/** Lightgrid ambient + half-diffuse tint at a point (models have no lightmap). */
function lightgridTint(origin: [number, number, number]): [number, number, number] {
  if (currentMapId < 0) return [1, 1, 1];
  const out = new Float32Array(6);
  if (core.bsp_light_at(currentMapId, origin[0], origin[1], origin[2], out) !== 0) {
    return [1, 1, 1];
  }
  const s = 1 / 255;
  return [
    Math.min(1, (out[0] + out[3] * 0.5) * s),
    Math.min(1, (out[1] + out[4] * 0.5) * s),
    Math.min(1, (out[2] + out[5] * 0.5) * s),
  ];
}

interface SpawnModelOpts {
  pitch?: number;
  roll?: number;
  scale?: number;
  animFps?: number;
  skin?: string;
  alpha?: number;
}

/** Load + spawn an MD3 runtime model. Returns its entity index (or -1). */
async function spawnModel(
  path: string,
  x: number,
  y: number,
  z: number,
  yaw = 0,
  opts: SpawnModelOpts = {},
): Promise<number> {
  if (!renderer) return -1;
  const r = renderer;
  const fetchPath = (p: string) =>
    api("/model/" + p.split("/").filter(Boolean).map(encodeURIComponent).join("/"));
  const res = await fetch(fetchPath(path));
  if (!res.ok) throw new Error(`model ${path}: ${res.status}`);
  const md3 = parseMd3(await res.arrayBuffer());
  let skin: Map<string, string> | null = null;
  if (opts.skin) {
    const sres = await fetch(fetchPath(opts.skin));
    if (sres.ok) skin = parseSkin(await sres.text());
  }
  // MD3 shader names often carry an image extension (`...teleporter.tga`); the
  // texture resolver expects an extension-less name (Q3 strips it too).
  const stripExt = (n: string) => n.replace(/\.(tga|jpg|jpeg|png|webp)$/i, "");
  const texIds: (number | null)[] = [];
  for (const s of md3.surfaces) {
    const raw = skin?.get(s.name.toLowerCase()) ?? s.shaderNames[0];
    if (!raw) {
      texIds.push(null);
      continue;
    }
    const img = await loadTexture(stripExt(raw));
    texIds.push(img ? r.registerTexture(img) : null);
  }
  const model = r.uploadModel(md3, texIds);
  const origin: [number, number, number] = [x, y, z];
  modelEntities.push({
    model,
    origin,
    angles: [opts.pitch ?? 0, yaw, opts.roll ?? 0],
    scale: opts.scale ?? 1,
    animFps: opts.animFps ?? 0,
    numFrames: md3.numFrames,
    color: lightgridTint(origin),
    alpha: opts.alpha ?? 1,
  });
  return modelEntities.length - 1;
}

// Headless-test hook: toggle PVS without reloading so a single build can be
// A/B compared (see the /tmp/opencode scripts).
(window as unknown as { __webrace: Record<string, unknown> }).__webrace = {
  setPvs: (v: unknown) => {
    pvsEnabled = !!v;
  },
  // Slice-1 worker-sim channel + determinism proof.
  mapDiag: () => buildMapDiag(),
  fogInfo: () => ({
    at: currentMapId >= 0 ? core.bsp_fog_at(currentMapId, lastEye) : -1,
    eye: Array.from(lastEye),
    volumes: [...fogByShader.entries()].map(([s, f]) => ({
      shader: s,
      name: core.bsp_shader_name(currentMapId, s),
      ...f,
    })),
  }),
  // Debug: runtime map-model instances (origin/angles/scale) and local items.
  modelsInfo: () => modelEntities.map((e) => ({
    model: e.model,
    origin: e.origin,
    angles: e.angles,
    scale: e.scale,
    alpha: e.alpha,
  })),
  // Debug/headless tests: live player-trail counts, heads and colours.
  trailInfo: () => playerTrails?.debugInfo() ?? null,
  // Debug/headless tests: rocket-dash script state.
  rocketDashInfo: () => ({
    active: rocketDashActive(),
    held: rocketDashHeld,
    attack2: keys.attack2,
    weapon: currentWeapon(),
  }),
  // Debug/headless tests: zoom state + the resolved Mouse-4 action.
  zoomInfo: () => ({    enabled: weaponProfile.zoomEnabled,
    mouse4: weaponProfile.mouse4,
    toggle: weaponProfile.zoomToggle,
    t: zoomT,
    fov: renderFov,
    zoomFov: weaponProfile.zoomFov,
  }),
  // Debug/headless tests: the sensitivity the engine actually receives.
  weaponSensitivity: () => ({    global: sensitivity,
    resolved: weaponProfile.sensitivity,
    weapon: currentWeapon(),
    overrides: weaponSettings.overrides,
    defaults: weaponSettings.defaults,
  }),
  // Debug/headless tests: what the first-person viewmodel resolves to.
  viewModelInfo: () => {    if (sessionId === null) return null;
    const e = core.session_eye(sessionId) as unknown as Float32Array;
    const a = core.session_angles(sessionId) as unknown as Float32Array;
    const w = currentWeapon();
    const inst = viewModelInstance(e, a[0], a[1], w);
    const p = weaponProfile.weaponPosition;
    return {
      weapon: w,
      model: inst?.model ?? null,
      placeholderId: viewModelId,
      hidden: hideViewModel || isSpectating(),
      pos: { preset: p.preset, x: p.x, y: p.y, z: p.z, size: p.size },
    };
  },
  // Debug/headless tests: what the renderer actually holds for the viewmodel.
  renderViewModel: () => renderer?.viewModelDebug() ?? null,
  // Debug/headless: visible player-proxy scale and the damage hit volume vs the
  // movement collision box (should differ: hit volume is deliberately larger).
  playerVisual: () => {
    const scale = currentHitboxScale();
    const hit = Array.from(core.player_hit_box_bounds(scale) as unknown as Float32Array);
    const col = Array.from(core.player_box_bounds() as unknown as Float32Array);
    return {
      scale,
      hitMins: hit.slice(0, 3),
      hitMaxs: hit.slice(3, 6),
      collisionMins: col.slice(0, 3),
      collisionMaxs: col.slice(3, 6),
    };
  },
  // Debug/headless: fire the hit feedback without waiting for a server hit.
  testHit: (amount = 25, kill = false) => {
    triggerHitmarker(kill, performance.now());
    pendingDamage.push({ v: -1, a: amount, w: 0, k: kill });
    return true;
  },
  // Debug/headless: the enemy proxy instances for a synthetic pose (proves the
  // enemyVisual settings reach the renderer).
  enemyPreview: () => {
    const m = modelMatrix([0, 0, 0], [0, 0, 0], 1);
    return playerInstances(m, [1, 0.5, 0.2], 1).map((i) => ({
      model: i.model,
      alpha: i.alpha,
      glow: !!i.glow,
      color: i.color,
      scale: i.matrix[0],
    }));
  },
  itemsInfo: () => {
    if (sessionId === null) return [];
    const n = core.session_item_count(sessionId);
    const out = [];
    const s = new Float32Array(8);
    for (let i = 0; i < n; i++) {
      core.session_item(sessionId, i, s);
      out.push({ o: [s[0], s[1], s[2]], kind: s[3] | 0, weapon: s[4] | 0, amount: s[5] });
    }
    return out;
  },
  diag: () => buildDiag(),
  syncError: () => SyncError(),
  sponge: () => Sponge(),
  hopper: () => lastCaughtBug(),
  printDiag: () => {
    printDiag();
    return true;
  },
  // Frame profiler sample counts (headless checks: gpu samples must be one per
  // completed query, not one per frame).
  profilerStats: () => profiler?.stats() ?? null,
  getPvs: () => pvsEnabled,
  setFrustum: (v: unknown) => {
    frustumEnabled = !!v;
  },
  getFrustum: () => frustumEnabled,
  // PVS vs PVS+frustum run counts for the current eye (A/B proof).
  visStats: () => {
    if (currentMapId < 0 || !visRuns) return null;
    if (!visScratch || visScratch.length < visRuns.length) {
      visScratch = new Uint32Array(visRuns.length);
    }
    const pvsRuns = core.bsp_visible_runs(currentMapId, lastEye, EMPTY_F32, visScratch);
    const tris = (buf: Uint32Array, n: number) => {
      let t = 0;
      for (let i = 0; i < n; i++) t += buf[i * 3 + 2];
      return t / 3;
    };
    const pvsTris = pvsRuns > 0 ? tris(visScratch, pvsRuns) : 0;
    const frustumTris = lastFrustumRuns > 0 ? tris(visRuns, lastFrustumRuns) : 0;
    return { pvsRuns, pvsTris, frustumRuns: lastFrustumRuns, frustumTris };
  },
  // Debug: per-stage glGetError trace (set globalThis.__glTrace = true first).
  glTrace: () => renderer?.glTraceDump() ?? [],
  // CPU submission time split of the last renderer frame (portal/world/models/…).
  drawTimes: () => renderer?.drawTimes() ?? null,
  /** Debug/perf: portal reflection refresh rate (Hz; 0 = every frame). */
  setPortalHz: (hz: number) => {
    renderer?.setPortalHz(Number(hz) || 0);
    return true;
  },
  // Cross-origin isolation status (Phase 3).
  crossOriginIsolated: () => crossOriginIsolated,
  // Debug: bloom post-process state.
  bloomInfo: () => {
    const gl = renderer?.gl;
    const bl = renderer?.getBloom();
    return gl && bl
      ? { enabled: bl.enabled, intensity: bl.intensity, threshold: bl.threshold }
      : null;
  },
  // Test hook: burst particles in front of the camera (validates instanced VFX).
  testParticles: () => {
    if (!particles || sessionId === null) return false;
    const e = core.session_eye(sessionId) as unknown as Float32Array;
    const a = core.session_angles(sessionId) as unknown as Float32Array;
    const { fwd } = camBasis(a[0], a[1]);
    const pos: [number, number, number] = [e[0] + fwd[0] * 120, e[1] + fwd[1] * 120, e[2] + fwd[2] * 120];
    particles.explosion(pos, 1, hexToRgb(vfxColorExplosion), hexToRgb(vfxColorSparks));
    smoke?.puff(pos, 1.2, hexToRgb(vfxColorSmoke));
    emitDebris(pos, 1, hexToRgb(vfxColorSmoke));
    return true;
  },
  // Debug/test: teleport the local session (practice/debug) and set view angles.
  teleport: (x: number, y: number, z: number, yaw = 0, pitch = 0) => {
    if (sessionId === null) return false;
    core.session_teleport(sessionId, x, y, z, yaw, pitch);
    return true;
  },
  mapId: () => currentMapId,
  // Debug: ray-pick the render face under the crosshair (structured, no log).
  faceProbe: () => computeFaceProbe(),
  setFaceProbe: (v: unknown) => {
    faceProbeOn = !!v;
  },
  // Debug: what render face is at canvas pixel (x, y)? Uses the current camera
  // + FOV, so it maps a screenshot coordinate straight to a face.
  pickScreen: (x: number, y: number) => computeFaceProbeAtPixel(x, y),
  // Debug: what solid geometry the player's box is embedded in (the "stuck" probe).
  stuckProbe: () => probeStuck("hook"),
  // Test hook: load a map without a page reload (exercises per-map caches).
  loadMap: (name: string) => {
    void loadMap(name);
    return true;
  },
  // Remote players' interpolated pose (headless tests / debugging).
  remotes: () =>
    snapBuffer.remoteStates(performance.now(), mySlot).map((r) => ({
      slot: r.slot,
      origin: r.origin,
      yaw: r.yaw,
    })),
  // Multiplayer map items (headless tests / debugging): presence, respawn
  // delay and the local clock when each was taken.
  netItems: () => (net?.items ?? []).map((it) => ({
    o: it.o,
    k: it.k,
    w: it.w,
    a: it.a,
    p: it.p,
    r: it.r ?? 0,
    hiddenAt: it.hiddenAt ?? null,
  })),
  // Multiplayer status (headless tests / debugging).
  mp: () => ({
    connected: net?.connected ?? false,
    slot: mySlot,
    map: currentMap,
    serverMap: net?.map ?? null,
    snapshots: snapBuffer.length,
    players: snapBuffer.latest()?.players.length ?? 0,
    cmdSeq: currentMapId >= 0 && sessionId !== null ? core.session_next_seq(sessionId) : 0,
    lastAck,
    reconcileCount,
    fastReconciles: sessionId !== null ? core.session_fast_reconciles(sessionId) : 0,
    lastCorr,
    corrCount,
    corrMax,
    reconcileMs: Math.round(lastReconcileMs * 100) / 100,
    mouseLatency: mouseLatencyStats(),
    voidServerOwned: sessionId !== null ? core.session_void_server_owned(sessionId) : false,
    ping: myPing(),
    rewindTicks: rewindTicks(),
    netJitterMs: Math.round(netJitterMs * 10) / 10,
    snapRate: Math.round(netSnapRate),
    downstreamHz,
    snapGaps,
    cgTrueLightning: CG_TRUELIGHTNING,
    netIn: Math.round(net?.downBytesPerSec(performance.now()) ?? 0),
    netOut: Math.round(net?.upBytesPerSec(performance.now()) ?? 0),
    particles: (particles?.count() ?? 0) + (smoke?.count() ?? 0),
    interpMs: INTERP_MS,
    smoothMs: RECONCILE_MS,
    lagMs: simLagMs,
    jitterMs: simJitterMs,
    lossPct: simLossPct,
    collision: pvpCollision,
    origin: sessionId !== null ? Array.from(core.session_origin(sessionId)) : null,
    velocity: sessionId !== null ? Array.from(core.session_velocity(sessionId)) : null,
    onGround: sessionId !== null ? core.session_on_ground(sessionId) : null,
    raceRunning: sessionId !== null ? core.session_race_running(sessionId) : null,
    mode: sessionId !== null ? core.session_mode(sessionId) : null,
  }),
  // Live, non-destructive camera/input diagnostic (same fields the playtest
  // telemetry reports each second; `takeCamDiag` resets its window).
  camDiag: () => ({
    sens: diagSens,
    yawMult: diagYawMult,
    pitchMult: diagPitchMult,
    camDYawMax: +diagCamDyawMax.toFixed(2),
    camDPitchMax: +diagCamDpitchMax.toFixed(2),
    camMismatchMax: +diagCamMismatchMax.toFixed(2),
    camMismatchFrames: diagMismatchFrames,
    camDeyeMax: +diagCamDeyeMax.toFixed(1),
    camMouseMax: diagMouseMax,
    camDtMax: +diagFrameDtMax.toFixed(1),
    camReconFrames: diagReconFrames,
    worst: diagWorst,
    topDeltas: diagTopDeltas,
    mouseEvents: diagMouseEvents,
    mouseGapMax: +diagMouseGapMax.toFixed(1),
    mouseNearBtn: diagMouseMaxNearButton,
  }),
  // Test hook: trigger position save / restart (the Mouse-3 + key-4 actions,
  // including the multiplayer server messages).
  mpPositionSave: () => {
    if (sessionId === null) return false;
    dispatchAction("position_save");
    return true;
  },
  mpRestart: () => {
    if (sessionId !== null) dispatchAction("restart");
  },
  // Test hooks for the map vote.
  mpProposeMap: (map: string) => proposeMap(map),
  mpVote: (yes: boolean) => {
    if (net?.connected && activeVote) {
      net.sendText(JSON.stringify({ t: "vote", yes }));
    }
  },
  mpProposeCollision: (on: boolean) => proposeCollision(on),
  // Ruleset debug: the client's canonical rules dump.
  rulesDump: () => (menu ? dumpRules(menu.getRules()) : ""),
  // Audio test hook: play (or synthesize) a sound event by id.
  soundTest: (id: string) => {
    audio.play(id);
    return true;
  },
  // Combat test hooks (headless verification).
  selectWeapon: (w: number, strong = true) => {
    if (!net?.connected && sessionId === null) return false;
    selectWeapon(w, strong);
    return true;
  },
  // Local combat state [health, armor, weapon, ammo, strong, alive], or null.
  localCombat: () =>
    sessionId !== null ? Array.from(core.session_combat(sessionId) as unknown as Float32Array) : null,
  // Movement HUD hint (velocity/wishdir/speed/branch/accel), for headless
  // verification of the strafe/bunny helpers.
  moveHint: () =>
    sessionId !== null
      ? Array.from(core.session_movement_hint(sessionId) as unknown as Float32Array)
      : [],
  // Raw input status / whether unadjusted movement was granted.
  rawInput: () => rawInputStatus(),
  // Presentation view angles [yaw, pitch] (radians), for headless checks.
  viewAngles: () =>
    sessionId !== null
      ? Array.from(core.session_angles(sessionId) as unknown as Float32Array)
      : [],
  // Sampled cgaz profile (241 gains, -120°..+120°), for headless verification.
  cgazProfile: () =>
    sessionId !== null
      ? Array.from(core.session_cgaz_profile(sessionId) as unknown as Float32Array)
      : [],
  // Number of live local projectiles (12 f32 per entity).
  localEntities: () =>
    sessionId !== null ? (core.session_entities(sessionId) as unknown as Float32Array).length / 12 : 0,
  // Flat local hitscan beams (8 floats each: owner, weapon, start xyz, end xyz).
  localBeams: () =>
    sessionId !== null ? Array.from(core.session_beams(sessionId) as unknown as Float32Array) : [],
  // Live replicated beam count (multiplayer).
  netBeams: () => netBeams.length,
  // Live replicated projectiles: count + interpolated sample (multiplayer).
  netEntities: () => latestEntities.length,
  netEntitySample: () =>
    snapBuffer
      .interpolatedEntities(performance.now(), latestEntities, !!net?.connected)
      .slice(0, 3),
  // Live locally-predicted beams (multiplayer fire prediction).
  predictedBeams: () => predictedBeams.length,
  predictedProjectiles: () => predictedProjectiles.length,
  // Test hook: the current weapon's predicted beam `[sx,sy,sz, ex,ey,ez, reload]`.
  predictBeam: (strong: boolean) => {
    if (sessionId === null) return [];
    const w = currentWeapon() ?? core.session_weapon(sessionId);
    return Array.from(core.session_predict_beam(sessionId, w, strong) as unknown as Float32Array);
  },
  // First few replicated beams (multiplayer), for debugging.
  netBeamSample: () => netBeams.slice(0, 3),
  // Resolved per-weapon VFX profile (Settings -> Weapons -> Effects).
  vfxProfile: (w: number) => {
    const v = weaponVfx(w);
    return {
      smoke: v.vfxSmoke,
      particles: v.vfxParticles,
      trail: v.vfxTrailMode,
      smokeAmount: v.vfxSmokeAmount,
      smokeOpacity: v.vfxSmokeOpacity,
      smokeSize: v.vfxSmokeSize,
      particleAmount: v.vfxParticleAmount,
      particleSize: v.vfxParticleSize,
      beamTrailMs: v.vfxBeamTrailMs,
      trailMs: v.vfxTrailMs,
      smokeTrailMs: v.vfxSmokeTrailMs,
    };
  },
  // Client beam history (fixed world-space trails; rail lasts longer).
  beamTrailInfo: () => ({
    now: performance.now(),
    predicted: predictedBeams.map((b) => ({
      w: b.weapon,
      a: b.start,
      b: b.end,
      life: Math.round(b.die - b.born),
      born: b.born,
      die: b.die,
    })),
    local: localBeams.map((b) => ({
      w: b.w,
      a: b.a,
      b: b.b,
      life: Math.round(b.die - b.born),
      born: b.born,
      die: b.die,
    })),
    net: netBeams.map((b) => ({
      w: b.w,
      a: b.a,
      b: b.b,
      life: Math.round(b.die - b.born),
      born: b.born,
      die: b.die,
    })),
    rings: railRings?.count() ?? 0,
  }),
  // Uploaded synthetic model ids (>= 0 once a map is loaded).
  modelIds: () => ({
    ghost: ghostModelId,
    ghostOutline: ghostOutlineModelId,
    ghostHalo: ghostHaloModelId,
    playerGear: playerGearModelId,
    playerGlow: playerGlowModelId,
    projectile: projectileModelId,
    beam: beamModelId,
    view: viewModelId,
  }),
  // Runtime 3D asset pack status (models/wr/manifest.json + uploaded models).
  assets: () => ({ ready: packReady(), uploaded: assetGpuCount(), keys: assetKeys() }),
  // First-person viewmodel instance for the current camera (or null).
  viewModel: () => {
    if (sessionId === null) return null;
    const e = core.session_eye(sessionId) as unknown as Float32Array;
    const a = core.session_angles(sessionId) as unknown as Float32Array;
    const inst = viewModelInstance(e, a[0], a[1], currentWeapon());
    return inst ? { model: inst.model, matrix: Array.from(inst.matrix) } : null;
  },
  combat: () => ({
    connected: net?.connected ?? false,
    self: lastSelf
      ? { hp: lastSelf.hp, ar: lastSelf.ar, wp: lastSelf.wp, am: lastSelf.am, alive: lastSelf.alive }
      : null,
    entities: latestEntities.length,
    weapon: lastSelf?.wp ?? 0,
  }),
  // Test hook: displace the local prediction to exercise reconciliation.
  mpDisplace: (dx = 100) => {
    if (sessionId === null) return false;
    const o = core.session_origin(sessionId) as unknown as Float32Array;
    const a = core.session_angles(sessionId) as unknown as Float32Array;
    core.session_teleport(sessionId, o[0] + dx, o[1], o[2], a[0] ?? 0, a[1] ?? 0);
    return true;
  },
  // Test hook: hold movement keys (drive the player under simulated latency).
  mpKeys: (f = false, b = false, l = false, r = false, jump = false) => {
    keys.forward = f;
    keys.back = b;
    keys.moveleft = l;
    keys.moveright = r;
    keys.jump = jump;
  },
  // Test hook: set the quantized view (yaw/pitch 16-bit bits).
  mpSetView: (yawBits: number, pitchBits: number) => {
    if (sessionId !== null) core.session_set_view_bits(sessionId, yawBits, pitchBits);
  },
  // Test hook: teleport the local player (x,y,z + view bits).
  mpTeleport: (x: number, y: number, z: number, yawBits = 32768, pitchBits = 0) => {
    if (sessionId === null) return false;
    core.session_teleport(sessionId, x, y, z, 0, 0);
    core.session_set_view_bits(sessionId, yawBits, pitchBits);
    return true;
  },
  // Trilinear lightgrid sample at a world point (raw 0..255 ambient/diffuse),
  // or null if the map has no usable lightgrid. Groundwork for entity lighting.
  lightAt: (x: number, y: number, z: number) => {
    if (currentMapId < 0) return null;
    const out = new Float32Array(6);
    if (core.bsp_light_at(currentMapId, x, y, z, out) !== 0) return null;
    return { ambient: [out[0], out[1], out[2]], diffuse: [out[3], out[4], out[5]] };
  },
  // Spawn an MD3 runtime model; opts = {pitch,roll,scale,animFps,skin,alpha}.
  spawnModel: (
    path: string,
    x: number,
    y: number,
    z: number,
    yaw = 0,
    opts?: Record<string, unknown>,
  ) => spawnModel(path, x, y, z, yaw, opts as SpawnModelOpts),
  clearModels: () => {
    modelEntities.length = 0;
    renderer?.clearModels();
  },
  modelCount: () => modelEntities.length,
  // Force a fixed frame blend on a spawned model (animation verification).
  setModelFrame: (i: number, frame0: number, frame1: number, lerp: number) => {
    const e = modelEntities[i];
    if (!e) return false;
    e.forceFrame = { frame0, frame1, lerp };
    return true;
  },
  // Debug/verification: parsed MD3 surface metadata.
  modelDebug: (i: number) => {
    const e = modelEntities[i];
    if (!e) return null;
    return { numFrames: e.numFrames, surfaces: renderer?.modelDebug(e.model) ?? null };
  },
  modelFrames: () => renderer?.lastModelFrames() ?? null,
};

// Key state (held actions).
const keys: Record<string, boolean> = {
  forward: false, back: false, moveleft: false, moveright: false,
  jump: false, crouch: false, special: false, attack: false, attack2: false,
};

/**
 * Rocket secondary fire is the Warfork "rocket dash" script:
 * `+back; +movedown; +special; +attack` (movedown maps to crouch, the closest
 * input we have). While the secondary bind is held we OR those actions into the
 * sim input, and force a strong rocket instead of the weak alt-fire.
 */
const ROCKET_WEAPON = 5;
let rocketDashHeld = false;
function rocketDashActive(): boolean {
  return currentWeapon() === ROCKET_WEAPON && (rocketDashHeld || keys.attack2);
}

let announceTimer: number | undefined;
/** Show a transient centered announcement (e.g. after a prejump respawn). */
function showAnnounce(text: string, ms: number) {
  announceEl.textContent = text;
  announceEl.classList.add("show");
  if (announceTimer !== undefined) clearTimeout(announceTimer);
  announceTimer = window.setTimeout(() => {
    announceEl.classList.remove("show");
    announceTimer = undefined;
  }, ms);
}

function setStatus(s: string) {
  statusEl.textContent = s;
}
function log(s: string) {
  logEl.textContent += s + "\n";
}

// ---- Temporary in-game chat + Quake-style console --------------------------
//
// Chat is deliberately minimal (a `say` message the server broadcasts to all)
// so we can talk during testing — it'll be replaced by the real chat later.
// The console is a client-side command line: every command maps onto an
// existing client action, and any **bind action** can be run by name.

const chatLogEl = document.getElementById("chatlog")!;
const chatBarEl = document.getElementById("chatbar")!;
const chatInputEl = document.getElementById("chatinput") as HTMLInputElement;
const consoleEl = document.getElementById("console")!;
const consoleLogEl = document.getElementById("consolelog")!;
const consoleInputEl = document.getElementById("consoleinput") as HTMLInputElement;

interface ChatLine {
  /** Monotonic id so the console never prints the same line twice. */
  seq: number;
  nick: string;
  text: string;
  sys: boolean;
  at: number;
}
let chatLines: ChatLine[] = [];
let chatSeq = 0;
/** Seq of the newest chat line already written to the console scrollback. */
let chatPrintedSeq = 0;
let chatOpen = false;
let consoleOpen = false;
const CHAT_VISIBLE_MS = 12000;

/** `nick:` with Quake colour codes honoured (plain nicks keep the blue style). */
function chatNickEl(nick: string): HTMLElement {
  const n = document.createElement("span");
  n.className = "nick";
  if (hasQ3Colors(nick)) appendQ3Text(n, nick);
  else n.textContent = nick;
  n.append(document.createTextNode(":"));
  return n;
}

/** Message body with Quake colour codes honoured. */
function chatTextEl(text: string): HTMLElement {
  const t = document.createElement("span");
  appendQ3Text(t, text);
  return t;
}

function renderChat(): void {
  const now = performance.now();
  const visible = chatLines.filter((l) => now - l.at < CHAT_VISIBLE_MS).slice(-9);
  chatLogEl.textContent = "";
  for (const l of visible) {
    const div = document.createElement("div");
    div.className = "line";
    if (l.sys) {
      const s = document.createElement("span");
      s.className = "sys";
      s.textContent = stripQ3Colors(l.text);
      div.append(s);
    } else {
      div.append(chatNickEl(l.nick), document.createTextNode(" "), chatTextEl(l.text));
    }
    chatLogEl.append(div);
  }
}

function addChatLine(nick: string, text: string, sys = false): void {
  const line: ChatLine = { seq: ++chatSeq, nick, text, sys, at: performance.now() };
  chatLines.push(line);
  if (chatLines.length > 60) chatLines = chatLines.slice(-60);
  renderChat();
  // Chat is part of the console scrollback too, whether or not it is open.
  printChatToConsole(line);
}

function openChat(): void {
  if (consoleOpen) return;
  chatOpen = true;
  for (const k of Object.keys(keys)) keys[k] = false;
  chatBarEl.classList.remove("hidden");
  chatInputEl.value = "";
  chatInputEl.focus();
}

function closeChat(): void {
  chatOpen = false;
  chatBarEl.classList.add("hidden");
  chatInputEl.blur();
}

function sendChat(text: string): void {
  const t = text.trim();
  if (!t) return;
  // No server to broadcast to: echo locally.
  if (net?.connected) net.say(t);
  else addChatLine("you", t);
}

chatInputEl.addEventListener("keydown", (e) => {
  if (e.key === "Enter") {
    e.preventDefault();
    sendChat(chatInputEl.value);
    closeChat();
  } else if (e.key === "Escape") {
    e.preventDefault();
    closeChat();
  }
});

// ---- Console ----

const consoleHistory: string[] = [];
let consoleHistIdx = 0;
let consoleHintShown = false;
const CONSOLE_MAX_LINES = 300;

function consolePrint(text: string, cls = ""): void {
  const div = document.createElement("div");
  div.className = cls ? `line ${cls}` : "line";
  div.textContent = text;
  appendConsoleLine(div);
}

/** Append one prepared line, trim the scrollback and keep it pinned to the bottom. */
function appendConsoleLine(div: HTMLElement): void {
  consoleLogEl.append(div);
  while (consoleLogEl.childElementCount > CONSOLE_MAX_LINES) {
    consoleLogEl.removeChild(consoleLogEl.firstChild!);
  }
  consoleLogEl.scrollTop = consoleLogEl.scrollHeight;
}

/** Write a chat line into the console scrollback (once per line). */
function printChatToConsole(l: ChatLine): void {
  if (l.seq <= chatPrintedSeq) return;
  chatPrintedSeq = l.seq;
  const div = document.createElement("div");
  if (l.sys) {
    div.className = "line sys";
    div.textContent = stripQ3Colors(l.text);
  } else {
    div.className = "line chat";
    div.append(chatNickEl(l.nick), document.createTextNode(" "), chatTextEl(l.text));
  }
  appendConsoleLine(div);
}

function toggleConsole(open?: boolean): void {
  consoleOpen = open ?? !consoleOpen;
  if (consoleOpen) {
    if (chatOpen) closeChat();
    for (const k of Object.keys(keys)) keys[k] = false;
    consoleEl.classList.remove("hidden");
    consoleInputEl.focus();
    // Chat is part of the scrollback, so the log may already hold lines before
    // the first open — print the hint once, regardless.
    if (!consoleHintShown) {
      consoleHintShown = true;
      consolePrint("webrace console — type `help` for commands (anything else is sent as chat)");
    }
  } else {
    consoleEl.classList.add("hidden");
    consoleInputEl.blur();
  }
}

/** Friendly key name -> `KeyboardEvent.code` (for the `bind` command). */
function keyNameToCode(name: string): string {
  const n = name.toLowerCase();
  if (n.startsWith("mouse")) return "Mouse" + n.slice(5);
  const named: Record<string, string> = {
    space: "Space", shift: "ShiftLeft", lshift: "ShiftLeft", ctrl: "ControlLeft",
    lctrl: "ControlLeft", alt: "AltLeft", esc: "Escape", escape: "Escape",
    enter: "Enter", return: "Enter", tab: "Tab", "`": "Backquote", "~": "Backquote",
    tilde: "Backquote", "§": "Backquote", "[": "BracketLeft", "]": "BracketRight",
  };
  if (named[n]) return named[n];
  if (/^f\d{1,2}$/.test(n)) return n.toUpperCase();
  if (n.length === 1) {
    const c = n.toUpperCase();
    if (c >= "A" && c <= "Z") return `Key${c}`;
    if (c >= "0" && c <= "9") return `Digit${c}`;
  }
  return "";
}

/** Console command metadata: description, usage, and (for value commands) the
 *  current + default value, printed when the command is run with no arguments. */
interface CmdHelp {
  desc: string;
  usage?: string;
  def?: string;
  value?: () => string;
  /** A bare invocation RUNS (instead of just printing info). */
  bare?: boolean;
}
const CONSOLE_HELP: Record<string, CmdHelp> = {
  help: { desc: "list settings/tools, or `help <category|command|word>`", bare: true },
  find: { desc: "search settings and commands (alias of `help <word>`)", usage: "find <word>" },
  clear: { desc: "clear the console scrollback", bare: true },
  close: { desc: "close the console (alias: quit)", bare: true },
  connect: { desc: "connect to a game server (default: the live server)", usage: "connect [ws://host:port]", bare: true },
  diag: { desc: "print GPU/renderer/map diagnostics (for bug reports)", bare: true },
  disconnect: { desc: "leave the current server (alias: -server)", bare: true },
  reconnect: { desc: "disconnect and rejoin the default server", bare: true },
  map: { desc: "propose a map vote (same as `vote map`)", usage: "map <name|random [keyword]>" },
  restart: { desc: "restart the race (start line or saved position)", bare: true },
  name: { desc: "set your nickname (leaderboards + chat)", usage: "name <nick>", def: "(empty)", value: () => getIdentity()?.nickname ?? "" },
  collision: { desc: "player-vs-player collision (alias of `vote collision`)", usage: "collision <on|off>" },
  rule: { desc: "set a gameplay rule (alias of `sv rule`)", usage: "sv rule <key> <value>" },
  rules: { desc: "print a summary of the active ruleset (alias of `sv rules`)", bare: true },
  admin: { desc: "set the admin token for live ruleset tuning (blank shows status)", usage: "admin <token>", bare: true },
  preset: { desc: "load a rules preset into the editor (alias of `sv preset`)" , usage: "sv preset <id>" },
  tp: { desc: "teleport to a world position (practice/debug)", usage: "tp <x> <y> <z>" },
  say: { desc: "send a chat message", usage: "say <text>" },
  team: { desc: "switch teams (playtest; self or another player)", usage: "team [slot|nick] red|blue|auto|spec" },
  mark: { desc: "mark the current moment in the playtest log", usage: "mark [note]", bare: true },
  binds: { desc: "list every key binding", bare: true },
  bind: { desc: "bind a key to an action", usage: "bind <key> <action>" },
  unbind: { desc: "remove a key binding", usage: "unbind <key>" },
  status: { desc: "show map, connection and snapshot rate", bare: true },
  frame: { desc: "frame pacing + mouse-input latency percentiles", bare: true },
  reload: { desc: "reload the page", bare: true },
  syncerror: { desc: "client prediction error in world units — `syncerror reset` clears the peak", bare: true },
  sponge: { desc: "smoothed sync error (spikes soaked up) — `sponge reset` clears it", bare: true },
  hopper: { desc: "show the last caught bug (alias: bug) — `hopper clear` hides it", bare: true },
};
/** Alias -> canonical tool command name (client settings are resolved by the catalog). */
const CONSOLE_ALIASES: Record<string, string> = {
  quit: "close", rate: "snap", teleport: "tp",
  "+server": "connect", "-server": "disconnect",
  // Named for Michael Abrash — the patron saint of profiling.
  abrash: "profiler",
};

/** `cmd = <value>, default cmd = <default>` (value commands only). */
function printCmdValue(cmd: string): void {
  const h = CONSOLE_HELP[cmd];
  if (h?.value) consolePrint(`${cmd} = ${h.value()}, default ${cmd} = ${h.def ?? "?"}`);
}
/** Value/default + description (what a bare command prints). */
function printCmdInfo(cmd: string): void {
  const h = CONSOLE_HELP[cmd];
  if (!h) return;
  printCmdValue(cmd);
  consolePrint(`  ${h.desc}${h.usage ? `  — usage: ${h.usage}` : ""}`);
}

// ---- Client settings from the registry -----------------------------------

/** Post-commit side effects that the menu's `applyAll()` does not cover. */
const SETTING_APPLY_HOOKS: Record<string, (value: unknown) => void> = {
  // The downstream rate also has to reach the live transport.
  "advanced.downstreamHz": (value) => applyDebug("downstream", Number(value)),
};

function consoleCommands(): ClientCommand[] {
  return clientCommands(currentRole);
}

function consoleValue(cmd: ClientCommand): unknown {
  return readCommand(cmd, settingsStore.value(), debugState());
}

/** Bare client command: value, default, explanation, category/access/usage. */
function printClientCommand(cmd: ClientCommand): void {
  consolePrint(commandSummary(cmd, consoleValue(cmd)));
  if (cmd.name !== cmd.def.id) consolePrint(`  id: ${cmd.def.id}`);
  consolePrint(`  ${cmd.def.help ?? cmd.def.label}`);
  const scope = cmd.runtime ? "session only" : "saved";
  consolePrint(
    `  ${cmd.pathLabel} · ${cmd.def.access} · ${scope} · usage: ${usageFor(cmd)}`,
  );
}

/** `name value` for a client setting (clamped/validated, persisted). */
function setClientCommand(cmd: ClientCommand, args: readonly string[]): void {
  const current = consoleValue(cmd);
  const parsed = parseCommandValue(cmd, args, current);
  if (!parsed.ok) {
    consolePrint(parsed.error, "err");
    return;
  }
  const def = cmd.def;
  if (def.runtimeKey) {
    applyDebug(def.runtimeKey, parsed.value as number | boolean);
    markExplicit(def.id);
  } else {
    setSettingValue(def, settingsStore.value(), parsed.value);
    markExplicit(def.id);
    // Side effects that the menu's applyAll() cannot infer must run first so
    // the UI/transport see the new value when the store notifies.
    SETTING_APPLY_HOOKS[def.id]?.(parsed.value);
    settingsStore.commit();
  }
  printClientCommand(cmd);
}

function resetClientCommand(cmd: ClientCommand): void {
  const def = cmd.def;
  if (def.runtimeKey) {
    applyDebug(def.runtimeKey, def.shipped as number | boolean);
    clearExplicit(def.id);
  } else {
    resetSetting(settingsStore.value(), def, loadCachedGlobalDefaults());
    SETTING_APPLY_HOOKS[def.id]?.(effectiveValue(def));
    settingsStore.commit();
  }
  consolePrint(`reset ${cmd.name} to default`);
  printClientCommand(cmd);
}

// ---- Help ----------------------------------------------------------------

const CATEGORY_ORDER = ["video", "audio", "controls", "weapons", "hud", "advanced"];

const SERVER_TOOL_ALIASES = new Set(["map", "collision", "rule", "rules", "preset"]);

function printToolsHelp(): void {
  consolePrint("tools:");
  for (const [name, h] of Object.entries(CONSOLE_HELP)) {
    if (name === "help" || name === "find" || SERVER_TOOL_ALIASES.has(name)) continue;
    consolePrint(`  ${name.padEnd(12)} ${h.desc}`);
  }
}

/** Tool/command search for `find` and `help <word>`. */
function printToolSearch(q: string): boolean {
  const lower = q.toLowerCase();
  const hits = Object.entries(CONSOLE_HELP).filter(
    ([name, h]) => name.includes(lower) || h.desc.toLowerCase().includes(lower),
  );
  if (hits.length === 0) return false;
  consolePrint("tools:");
  for (const [name, h] of hits.slice(0, 10)) {
    consolePrint(`  ${name.padEnd(12)} ${h.desc}`);
  }
  return true;
}

function printServerHelp(): void {
  consolePrint("server settings (shared, authoritative):");
  consolePrint("  sv rules                   print every active rule");
  consolePrint("  sv rule <key> <value>      admin: set a live rule");
  consolePrint("  sv preset <id>             load a rules preset + propose it");
  consolePrint("  sv mode <id>               load a mode preset + propose it");
  consolePrint("votes (everyone):");
  consolePrint("  vote map <name|random [keyword]>");
  consolePrint("  vote mode <id>  |  vote rules <id>");
  consolePrint("  vote collision <on|off>  |  vote yes  |  vote no");
  consolePrint("  `help sv rule` lists rule keys; `help sv mode` lists mode presets");
}

function printVoteHelp(): void {
  printServerHelp();
}

function printClientCategory(cmds: readonly ClientCommand[], q: string): boolean {
  const lower = q.toLowerCase();
  const matches = cmds.filter((c) => {
    const id = c.def.category.join(".").toLowerCase();
    return id === lower || id.startsWith(`${lower}.`);
  });
  if (matches.length === 0) return false;
  matches.sort((a, b) => a.def.id.localeCompare(b.def.id));
  const label = matches[0].pathLabel;
  consolePrint(`${label} — ${matches.length} setting${matches.length === 1 ? "" : "s"}`);
  for (const cmd of matches) {
    const value = formatSettingValue(cmd.def, consoleValue(cmd));
    consolePrint(`  ${cmd.name.padEnd(18)} ${value.padEnd(10)} ${cmd.def.label}`);
  }
  return true;
}

function printClientSearch(cmds: readonly ClientCommand[], q: string): boolean {
  const hits = searchSettings(cmds.map((c) => c.def), q).slice(0, 20);
  if (hits.length === 0) return false;
  consolePrint(`matches for “${q}”:`);
  for (const def of hits) {
    const cmd = findClientCommand(cmds, def.id);
    if (!cmd) continue;
    const value = formatSettingValue(def, consoleValue(cmd));
    consolePrint(`  ${cmd.name.padEnd(18)} ${value.padEnd(10)} ${def.label}`);
  }
  if (hits.length === 20) consolePrint("  … narrow the search for more");
  return true;
}

/** Wrap a list of short tokens into aligned console lines. */
function printColumns(items: readonly string[], width = 96): void {
  let line = "  ";
  for (const item of items) {
    if (line.length + item.length + 2 > width && line.trim()) {
      consolePrint(line);
      line = "  ";
    }
    line += item + "  ";
  }
  if (line.trim()) consolePrint(line);
}

/** A setting exists but the current role cannot see/use it. */
function printRoleBlocked(name: string): boolean {
  const hidden = findClientCommand(clientCommands("admin"), name);
  if (!hidden) return false;
  consolePrint(
    `“${name}” is a ${hidden.def.access}-only setting (your role: ${currentRole})`,
    "err",
  );
  return true;
}

function consoleHelp(args: readonly string[]): void {
  const q = args.join(" ").trim();
  const cmds = consoleCommands();
  if (!q) {
    const counts = new Map<string, number>();
    for (const c of cmds) counts.set(c.category, (counts.get(c.category) ?? 0) + 1);
    const rank = (key: string) => {
      const i = CATEGORY_ORDER.indexOf(key);
      return i < 0 ? CATEGORY_ORDER.length : i;
    };
    const ordered = [...counts.entries()].sort(
      (a, b) => rank(a[0]) - rank(b[0]) || a[0].localeCompare(b[0]),
    );
    consolePrint("client settings (yours, saved):");
    consolePrint("  " + ordered.map(([k, n]) => `${k} (${n})`).join("   "));
    consolePrint("  bare = show · <name> <value> = set · `reset <name>` = default");
    printServerHelp();
    printToolsHelp();
    consolePrint("type `help <category|command|word>` — e.g. `help video`, `help trail`, `help fov`");
    return;
  }
  const lower = q.toLowerCase();
  if (lower === "sv" || lower === "server") {
    printServerHelp();
    return;
  }
  if (lower === "tools") {
    printToolsHelp();
    return;
  }
  if (lower === "binds") {
    consolePrint("bind actions (run one by name, or use `bind <key> <action>`):");
    consolePrint(`  ${[...ACTION_LABELS.keys()].join("  ")}`);
    return;
  }
  const exact = findClientCommand(cmds, q);
  if (exact) {
    printClientCommand(exact);
    return;
  }
  if (!q.includes(" ") && printRoleBlocked(q)) return;
  if (
    ["sv rules", "sv rule", "sv preset", "sv mode", "vote mode", "vote rules"].includes(lower)
  ) {
    printServerHelp();
    if (lower === "sv rule") {
      const rules = menu?.getRules() ?? {};
      consolePrint(`rule keys (${Object.keys(rules).length}):`);
      printColumns(Object.keys(rules).sort());
    }
    if (lower === "sv preset" || lower === "vote rules") {
      consolePrint("rules presets:");
      for (const p of PRESETS) consolePrint(`  ${p.id.padEnd(16)} ${p.label}`);
    }
    if (lower === "sv mode" || lower === "vote mode") {
      consolePrint(`mode presets (${serverFamily() ?? "any"} server):`);
      for (const p of allowedModes()) consolePrint(`  ${p.id.padEnd(14)} ${p.label}`);
    }
    return;
  }
  if (printClientCategory(cmds, q)) return;
  if (printClientSearch(cmds, q)) return;
  if (printToolSearch(q)) return;
  consolePrint(`no setting, command or category matches “${q}”`, "err");
  consolePrint("try `help`, `help video`, or a word like `trail`, `volume`, `fov`.", "err");
}

function consoleFind(args: readonly string[]): void {
  const q = args.join(" ").trim();
  if (!q) {
    consolePrint("usage: find <word>");
    return;
  }
  const cmds = consoleCommands();
  const settings = printClientSearch(cmds, q);
  const tools = printToolSearch(q);
  if (!settings && !tools) consolePrint(`no setting or command matches “${q}”`, "err");
}

// ---- Server / vote scopes -------------------------------------------------

/** The server's mode family (race/freestyle vs combat), or null if unknown. */
function serverFamily(): "race" | "combat" | null {
  return serverMode ? (serverMode.structure === "race" ? "race" : "combat") : null;
}

/** Mode presets valid on the current server. */
function allowedModes(): typeof MODE_PRESETS {
  const family = serverFamily();
  return MODE_PRESETS.filter((p) => {
    if (!family) return true;
    const isRace = p.mode.structure === "race";
    return family === "race" ? isRace : !isRace;
  });
}

function parseOnOff(raw: string): boolean | null {
  const v = raw.toLowerCase();
  if (["on", "1", "true", "yes", "enable", "enabled"].includes(v)) return true;
  if (["off", "0", "false", "no", "disable", "disabled"].includes(v)) return false;
  return null;
}

function printAllRules(): void {
  const rules = menu?.getRules() ?? {};
  const entries = Object.entries(rules).sort(([a], [b]) => a.localeCompare(b));
  consolePrint(`${entries.length} rules:`);
  let line = "  ";
  for (const [k, v] of entries) {
    const pair = `${k}=${v}`;
    if (line.length + pair.length > 96) {
      consolePrint(line);
      line = "  ";
    }
    line += pair + "  ";
  }
  if (line.trim()) consolePrint(line);
}

function consoleServer(args: readonly string[]): void {
  const sub = (args[0] ?? "").toLowerCase();
  const rest = args.slice(1);
  if (!sub || sub === "help") {
    printServerHelp();
    return;
  }
  if (sub === "rules") {
    printAllRules();
    return;
  }
  if (sub === "rule") {
    if (rest.length < 2) {
      consolePrint("usage: sv rule <key> <value>  (run `sv rules` for keys)", "err");
      return;
    }
    const key = rest[0];
    const value = Number(rest[1]);
    if (!Number.isFinite(value)) {
      consolePrint(`not a number: ${rest[1]}`, "err");
      return;
    }
    consoleRule(key, value);
    return;
  }
  if (sub === "preset") {
    const id = rest.join(" ").trim();
    if (!id) {
      consolePrint("usage: sv preset <id>", "err");
      return;
    }
    const label = menu?.votePreset(id) ?? null;
    if (!label) {
      consolePrint(`unknown rules preset: ${id}`, "err");
      return;
    }
    consolePrint(`rules vote: ${label}`);
    return;
  }
  if (sub === "mode") {
    const id = rest.join(" ").trim();
    if (!id) {
      consolePrint("usage: sv mode <id>", "err");
      return;
    }
    voteModePreset(id, id);
    return;
  }
  consolePrint(`unknown server command: sv ${sub}`, "err");
  printServerHelp();
}

function consoleRule(key: string, value: number): void {
  // Same path as the old `rule` tool: live tuning needs server admin.
  if (net?.connected) {
    if (!net.admin) {
      consolePrint("note: live ruleset tuning needs the admin token (console: admin <token>)", "err");
      return;
    }
    net.sendText(JSON.stringify({ t: "rule", k: key, v: value }));
    serverRules = { ...(serverRules ?? {}), [key]: value };
    if (sessionId !== null) core.session_set_rule(sessionId, key, value);
  } else {
    commitSettings((s) => { s.rules[key] = value; });
    if (sessionId !== null) core.session_set_rule(sessionId, key, value);
  }
  consolePrint(`${key} = ${value}`);
}

function voteMapInput(input: string): void {
  const q = input.trim();
  if (!net?.connected) {
    consolePrint("not connected", "err");
    showAnnounce("connect to a server to vote", 1800);
    return;
  }
  if (!q) {
    consolePrint("usage: vote map <name|random [keyword]>", "err");
    return;
  }
  if (!menu) {
    consolePrint("menu not ready", "err");
    return;
  }
  void menu.voteMap(q).then((map) => {
    consolePrint(map ? `map vote: ${map}` : `no map matched: ${q}`, map ? "" : "err");
    if (!map) showAnnounce(`no map matched: ${q}`, 1800);
  });
}

function consoleVote(args: readonly string[]): void {
  const sub = (args[0] ?? "").toLowerCase();
  const rest = args.slice(1);
  const restStr = rest.join(" ").trim();
  if (!sub || sub === "help") {
    printVoteHelp();
    return;
  }
  if (sub === "yes" || sub === "no") {
    if (!net?.connected) {
      consolePrint("not connected", "err");
      showAnnounce("connect to a server to vote", 1800);
      return;
    }
    net.sendText(JSON.stringify({ t: "vote", yes: sub === "yes" }));
    consolePrint(`vote ${sub}`);
    return;
  }
  if (sub === "collision") {
    const on = parseOnOff(restStr);
    if (on === null) {
      consolePrint("usage: vote collision <on|off>", "err");
      return;
    }
    proposeCollision(on);
    consolePrint(`collision vote: ${on ? "on" : "off"}`);
    return;
  }
  if (sub === "map") {
    voteMapInput(restStr);
    return;
  }
  if (sub === "random") {
    voteMapInput(`random ${restStr}`.trim());
    return;
  }
  if (sub === "mode") {
    if (!restStr) {
      consolePrint("usage: vote mode <id>", "err");
      return;
    }
    voteModePreset(restStr, restStr);
    return;
  }
  if (sub === "rules") {
    if (!restStr) {
      consolePrint("usage: vote rules <id>", "err");
      return;
    }
    const label = menu?.votePreset(restStr) ?? null;
    if (!label) {
      consolePrint(`unknown rules preset: ${restStr}`, "err");
      return;
    }
    consolePrint(`rules vote: ${label}`);
    return;
  }
  // A bare mode name: `vote freestyle`, `vote wipeout`, `vote clan arena`.
  const text = [sub, restStr].filter(Boolean).join(" ");
  const lowerText = text.toLowerCase();
  const modes = allowedModes();
  const exactMode = modes.find(
    (p) => p.id.toLowerCase() === lowerText || p.label.toLowerCase() === lowerText,
  );
  if (exactMode) {
    voteModePreset(exactMode.id, exactMode.label);
    return;
  }
  const modeHits = modes.filter(
    (p) =>
      p.id.toLowerCase().startsWith(lowerText) || p.label.toLowerCase().startsWith(lowerText),
  );
  if (modeHits.length === 1) {
    voteModePreset(modeHits[0].id, modeHits[0].label);
    return;
  }
  if (modeHits.length > 1) {
    consolePrint(
      `did you mean: ${modeHits.map((p) => p.id).join(", ")}?`,
      "err",
    );
    return;
  }
  const blocked = MODE_PRESETS.find(
    (p) =>
      p.id.toLowerCase() === lowerText || p.label.toLowerCase() === lowerText,
  );
  if (blocked) {
    consolePrint(`mode not available on this server: ${blocked.label}`, "err");
    return;
  }
  // Bare map name: `vote deck16` feels natural too.
  voteMapInput(text);
}

/** Apply + vote a mode preset, printing the outcome. */
function voteModePreset(id: string, label: string): void {
  const allowed = allowedModes().some((p) => p.id === id);
  if (!allowed) {
    consolePrint(`mode not available on this server: ${label}`, "err");
    return;
  }
  const applied = menu?.voteModePreset(id) ?? null;
  if (!applied) {
    consolePrint(`unknown mode preset: ${id}`, "err");
    return;
  }
  consolePrint(`mode vote: ${applied}`);
}

function consoleReset(args: readonly string[]): void {
  const name = args.join(" ").trim();
  if (!name) {
    consolePrint("usage: reset <setting>", "err");
    return;
  }
  const cmd = findClientCommand(consoleCommands(), name);
  if (!cmd) {
    if (printRoleBlocked(name)) return;
    consolePrint(`unknown setting: ${name}`, "err");
    printClientSearch(consoleCommands(), name);
    return;
  }
  resetClientCommand(cmd);
}

/** Suggest close setting names for a typo'd command line. */
/** Small edit-distance check for tool-name typos (`statuz` -> `status`). */
function closeWord(a: string, b: string): boolean {
  if (Math.abs(a.length - b.length) > 2) return false;
  let prev = Array.from({ length: b.length + 1 }, (_, i) => i);
  for (let i = 1; i <= a.length; i++) {
    const cur = [i];
    for (let j = 1; j <= b.length; j++) {
      cur[j] = Math.min(prev[j] + 1, cur[j - 1] + 1, prev[j - 1] + (a[i - 1] === b[j - 1] ? 0 : 1));
    }
    prev = cur;
  }
  return prev[b.length] <= (a.length >= 7 ? 2 : 1);
}

function consoleSuggest(word: string): string[] {
  if (!word) return [];
  const out: string[] = [];
  // Short names/aliases first (a typo should suggest `trail`, not a long id).
  for (const cmd of consoleCommands()) {
    if (out.length >= 3) break;
    if (
      cmd.name.startsWith(word) ||
      closeWord(word, cmd.name) ||
      cmd.aliases.some((a) => a.startsWith(word) || closeWord(word, a))
    ) {
      out.push(cmd.name);
    }
  }
  for (const def of searchSettings(consoleCommands().map((c) => c.def), word).slice(0, 3)) {
    if (out.length >= 3) break;
    const cmd = findClientCommand(consoleCommands(), def.id);
    if (cmd) out.push(cmd.name);
  }
  for (const name of Object.keys(CONSOLE_HELP)) {
    if (out.length >= 3) break;
    if (name.startsWith(word) || word.startsWith(name) || closeWord(word, name)) out.push(name);
  }
  return [...new Set(out)].slice(0, 3);
}

function consoleExec(raw: string): void {
  const line = raw.trim();
  if (!line) return;
  consoleHistory.push(line);
  if (consoleHistory.length > 100) consoleHistory.shift();
  consoleHistIdx = consoleHistory.length;
  consolePrint(`] ${line}`, "echo");
  const parts = line.split(/\s+/);
  const cmd = parts[0].toLowerCase();
  const args = parts.slice(1);
  const argStr = args.join(" ");
  const numArg = (i: number): number | null => {
    const v = Number(args[i]);
    return Number.isFinite(v) ? v : null;
  };
  const err = (m: string) => consolePrint(m, "err");
  const canon = CONSOLE_ALIASES[cmd] ?? cmd;

  // Help / find first (they take words, not values).
  if (canon === "help") {
    consoleHelp(args);
    return;
  }
  if (canon === "find") {
    consoleFind(args);
    return;
  }
  // Client setting generated from the registry (id, curated alias or short name).
  const clientCmd = findClientCommand(consoleCommands(), canon);
  if (clientCmd) {
    if (args.length === 0) printClientCommand(clientCmd);
    else setClientCommand(clientCmd, args);
    return;
  }
  // A known setting hidden by the role ladder: say so instead of chatting.
  if (printRoleBlocked(canon)) return;
  if (canon === "reset") {
    consoleReset(args);
    return;
  }
  if (canon === "sv") {
    consoleServer(args);
    return;
  }
  if (canon === "vote") {
    consoleVote(args);
    return;
  }

  const help = CONSOLE_HELP[canon];
  // Bare value command -> print current value, default and what it does.
  if (help && !help.bare && args.length === 0) {
    printCmdInfo(canon);
    return;
  }

  switch (canon) {
    case "clear":
      consoleLogEl.textContent = "";
      break;
    case "diag":
      printDiag();
      break;
    case "close":
    case "quit":
      toggleConsole(false);
      break;
    case "connect":
    case "+server":
      connectToServer(argStr || defaultServerUrl());
      consolePrint(`connecting to ${argStr || defaultServerUrl()}…`);
      break;
    case "disconnect":
    case "-server":
      net?.close();
      consolePrint("disconnected");
      break;
    case "reconnect":
      net?.close();
      net = null;
      connectToServer(defaultServerUrl());
      consolePrint("reconnecting…");
      break;
    case "map":
      voteMapInput(argStr);
      break;
    case "restart":
      dispatchAction("restart");
      consolePrint("restart");
      break;
    case "name":
      nicknameInput.value = argStr.slice(0, 20);
      announceNickname();
      printCmdValue("name");
      break;
    case "collision":
      consoleVote(["collision", argStr]);
      break;
    case "rule":
      consoleServer(["rule", ...args]);
      break;
    case "admin": {
      if (!argStr) {
        consolePrint(`admin token ${loadAdminToken() ? "set" : "not set"}`);
        consolePrint(`  server admin: ${net?.connected ? (net.admin ? "yes" : "no") : "not connected"}`);
        break;
      }
      saveAdminToken(argStr);
      // Re-send hello so the server can validate the new token.
      announceNickname();
      consolePrint("admin token saved");
      break;
    }
    case "rules":
      printAllRules();
      break;
    case "preset":
      consoleServer(["preset", argStr]);
      break;
    case "tp":
    case "teleport": {
      const x = numArg(0), y = numArg(1), z = numArg(2);
      if (sessionId === null || x === null || y === null || z === null) {
        err("usage: tp <x> <y> <z> (practice/debug)");
        break;
      }
      core.session_teleport(sessionId, x, y, z, 0, 0);
      consolePrint(`teleported to ${x} ${y} ${z}`);
      break;
    }
    case "say":
      sendChat(argStr);
      break;
    case "binds": {
      const entries = Object.entries(binds).sort((a, b) => a[0].localeCompare(b[0]));
      for (const [code, act] of entries) consolePrint(`  ${code.padEnd(14)} ${act}`);
      consolePrint(`${entries.length} binds`);
      break;
    }
    case "bind": {
      const act = (args[1] ?? "").toLowerCase();
      if (!args[0] || !act) { err("usage: bind <key> <action>"); break; }
      if (!ACTION_LABELS.has(act)) { err(`unknown action: ${act}`); break; }
      const code = keyNameToCode(args[0]);
      if (!code) { err(`unknown key: ${args[0]}`); break; }
      // One code per action (same rule as the menu editor).
      for (const [c, a] of Object.entries(binds)) if (a === act) delete binds[c];
      binds[code] = act;
      actionByCode = codeToAction(binds);
      persistBinds();
      consolePrint(`${code} -> ${act}`);
      break;
    }
    case "unbind": {
      const code = keyNameToCode(args[0] ?? "");
      if (!code || !(code in binds)) { err("usage: unbind <key>"); break; }
      delete binds[code];
      actionByCode = codeToAction(binds);
      persistBinds();
      consolePrint(`unbound ${code}`);
      break;
    }
    case "status":
      consolePrint(
        `map ${currentMap || "-"}  ${net?.connected ? `online slot ${mySlot}` : "disconnected"}  snap ${downstreamHz} Hz`,
      );
      break;
    case "frame": {
      const f = playtestFrameStats();
      const m = mouseLatencyStats();
      consolePrint(
        `frame p50 ${f.p50.toFixed(2)} ms  p99 ${f.p99.toFixed(2)} ms  ~${f.fps} fps  hitches ${f.hitches}/s`,
      );
      consolePrint(
        `mouse input p50 ${m.p50.toFixed(1)} ms  p75 ${m.p75.toFixed(1)}  p95 ${m.p95.toFixed(1)}  p99 ${m.p99.toFixed(1)}  (n=${m.n})`,
      );
      break;
    }
    case "reload":
      location.reload();
      break;
    case "syncerror": {
      if (argStr.toLowerCase() === "reset") {
        resetSyncError();
        consolePrint("SyncError reset");
        break;
      }
      const e = SyncError();
      const age = e.at > 0 ? `${((performance.now() - e.at) / 1000).toFixed(1)}s ago` : "none yet";
      consolePrint(
        `SyncError ${e.units.toFixed(2)}u  (${e.x.toFixed(2)} ${e.y.toFixed(2)} ${e.z.toFixed(2)})  last ${age}`,
      );
      consolePrint(
        `  peak ${e.peak.toFixed(2)}u  mean ${e.mean.toFixed(2)}u  samples ${e.samples}`,
      );
      const sp = Sponge();
      consolePrint(
        `  Sponge ${sp.smoothed.toFixed(2)}u smoothed (window peak ${sp.windowPeak.toFixed(2)}u, n=${sp.samples}/${sp.window})`,
      );
      break;
    }
    case "hopper":
    case "bug": {
      if (argStr.toLowerCase() === "clear") {
        hideHopper();
        consolePrint("hopper cleared");
        break;
      }
      const b = lastCaughtBug();
      if (!b) {
        consolePrint("Hopper: no bugs caught yet");
        break;
      }
      showHopper();
      consolePrint(`hopper: ${b.message} (${b.source})`);
      break;
    }
    case "sponge": {
      if (argStr.toLowerCase() === "reset") {
        resetSyncError();
        consolePrint("SyncError/Sponge reset");
        break;
      }
      const sp = Sponge();
      consolePrint(
        `Sponge ${sp.smoothed.toFixed(2)}u smoothed  (window peak ${sp.windowPeak.toFixed(2)}u, n=${sp.samples}/${sp.window})`,
      );
      break;
    }
    case "team": {
      if (!net?.connected) {
        err("not connected");
        break;
      }
      if (args.length === 0) {
        err("usage: team [slot|nick] red|blue|auto|spec");
        break;
      }
      let slot: number | undefined;
      let value = args[0].toLowerCase();
      if (args.length >= 2) {
        const target = args[0];
        const n = Number(target);
        if (Number.isInteger(n) && n >= 0) {
          slot = n;
        } else {
          const hit = roster.find((r) => (r.nick || "").toLowerCase() === target.toLowerCase());
          if (!hit) {
            err(`no player "${target}"`);
            break;
          }
          slot = hit.slot;
        }
        value = args[1].toLowerCase();
      }
      if (!["red", "blue", "auto", "spec", "spectate", "1", "2", "0"].includes(value)) {
        err("usage: team [slot|nick] red|blue|auto|spec");
        break;
      }
      net.sendText(JSON.stringify({ t: "team", team: value, ...(slot !== undefined ? { slot } : {}) }));
      consolePrint(`team -> ${value}${slot !== undefined ? ` (slot ${slot})` : ""}`);
      break;
    }
    case "mark":
      if (!net?.connected) {
        err("not connected");
        break;
      }
      net.sendText(JSON.stringify({ t: "mark", note: argStr.slice(0, 200) }));
      consolePrint(`mark sent${argStr ? ` (${argStr})` : ""}`);
      break;
    default: {
      // A bare bind action runs by name (e.g. `noclip`, `weapon_ball`); with
      // extra words the line is chat instead (so "restart is broken" doesn't
      // restart a race).
      if (args.length === 0 && ACTION_LABELS.has(canon)) {
        dispatchAction(canon as Action);
        consolePrint(`> ${cmd}`);
      } else {
        // A typo'd command gets a suggestion instead of silently becoming chat.
        const suggestions = consoleSuggest(cmd);
        if (suggestions.length > 0) {
          consolePrint(
            `unknown command “${cmd}” — did you mean: ${suggestions.join(", ")}?`,
            "err",
          );
          consolePrint("type `help` for settings/tools, or `say <text>` for chat.", "err");
        } else {
          // Not a command: send the line as chat (Quake-style).
          sendChat(line);
        }
      }
    }
  }
}

consoleInputEl.addEventListener("keydown", (e) => {
  if (e.key === "Enter") {
    e.preventDefault();
    const line = consoleInputEl.value;
    consoleInputEl.value = "";
    consoleExec(line);
  } else if (e.key === "Escape" || e.code === "Backquote") {
    e.preventDefault();
    toggleConsole(false);
  } else if (e.key === "ArrowUp") {
    e.preventDefault();
    if (consoleHistIdx > 0) {
      consoleHistIdx--;
      consoleInputEl.value = consoleHistory[consoleHistIdx] ?? "";
    }
  } else if (e.key === "ArrowDown") {
    e.preventDefault();
    if (consoleHistIdx < consoleHistory.length) {
      consoleHistIdx++;
      consoleInputEl.value = consoleHistory[consoleHistIdx] ?? "";
    }
  }
});


/**
 * Surface a debug probe line where the user can actually read/copy it: the
 * in-game log, the DevTools console, and the clipboard (best-effort).
 */
function reportProbe(s: string) {
  log(s);
  // eslint-disable-next-line no-console
  console.log("[webrace] " + s);
  try {
    void navigator.clipboard?.writeText(s);
  } catch {
    /* clipboard may be unavailable */
  }
}

/** Keyframe readout shown while in recall (frozen time-travel). */
function updateRecallHud(): void {
  if (sessionId === null) {
    recallHudEl.textContent = "";
    return;
  }
  const idx = core.session_recall_index(sessionId) + 1;
  const count = core.session_recall_count(sessionId);
  const ticks = core.session_race_ticks(sessionId);
  const ms = Math.round(ticksToMs(ticks));
  const m = Math.floor(ms / 60000);
  const s = Math.floor((ms % 60000) / 1000);
  const milli = ms % 1000;
  const time =
    `${m.toString().padStart(2, "0")}:` +
    `${s.toString().padStart(2, "0")}.` +
    `${milli.toString().padStart(3, "0")}`;
  const cp = (core.session_race_splits(sessionId) as unknown as Uint32Array).length;
  const cpTotal = core.session_race_total_checkpoints(sessionId);
  const speed = Math.round(core.session_speed(sessionId));
  recallHudEl.innerHTML =
    `<span class="rk">recall</span> ${idx}/${count}  ` +
    `<span class="rk">t</span> ${time}  ` +
    `<span class="rk">cp</span> ${cp}/${cpTotal}  ` +
    `${speed}ups`;
}

/**
 * Run a recall action and animate the camera from the previous view to the new
 * keyframe's view. The sim is frozen in recall, so this is purely a visual
 * transition between keyframes.
 */
function recallTransition(run: () => void): void {
  if (sessionId === null) {
    run();
    return;
  }
  const e0 = core.session_eye(sessionId) as unknown as Float32Array;
  const a0 = core.session_angles(sessionId) as unknown as Float32Array;
  const fromEye: [number, number, number] = [e0[0], e0[1], e0[2]];
  const fromYaw = a0[0];
  const fromPitch = a0[1];
  run();
  if (sessionId === null) return;
  const e1 = core.session_eye(sessionId) as unknown as Float32Array;
  const a1 = core.session_angles(sessionId) as unknown as Float32Array;
  recallAnim = {
    fromEye,
    fromYaw,
    fromPitch,
    toEye: [e1[0], e1[1], e1[2]],
    toYaw: a1[0],
    toPitch: a1[1],
    start: performance.now(),
  };
}

/**
 * True while connected to a server but running a **local** practice session —
 * practice / noclip / recall, or a run started from a recalled keyframe.
 *
 * The authoritative server knows nothing about those modes (there is no
 * online `practicemode`), so it would override them: a `restart` would send us
 * back to *its* saved spawn and its rollback+replay would undo a recall, and
 * noclip free-fly would be snapped back by gravity. While **detached** we stop
 * sending input commands and stop applying server corrections, so the local
 * sim runs exactly like offline. Leaving practice resyncs (local reset +
 * `restart`), which respawns us at the run spawn. Other players just see us
 * standing still where practice began.
 */
function practiceDetached(): boolean {
  if (sessionId === null || !(net?.connected ?? false)) return false;
  return core.session_is_practice(sessionId) || core.session_is_recalled(sessionId);
}

/** Format a tick count as `m:ss.cc` at the simulation tick rate. */
function formatTicks(ticks: number): string {
  const ms = ticksToMs(ticks);
  const m = Math.floor(ms / 60000);
  const s = Math.floor((ms % 60000) / 1000);
  const cs = Math.floor((ms % 1000) / 10);
  return `${m}:${s.toString().padStart(2, "0")}.${cs.toString().padStart(2, "0")}`;
}

/** Synthetic player-sized box used to draw ghosts (no player MD3 assets ship). */
/**
 * A procedural player capsule: radius `radius`, from `z0` to `z1`, with rounded
 * caps. Outward CCW winding so the inflated neon hull can cull front faces.
 * The AABB collision box remains `-16..16 / -24..40`; the capsule is visual.
 */
function makeCapsuleModel(
  radius: number,
  z0: number,
  z1: number,
  segments = 16,
  capSteps = 4,
): Md3Model {
  const positions: number[] = [];
  const st: number[] = [];
  const indices: number[] = [];
  const rings: { r: number; z: number }[] = [];
  // Bottom hemisphere, then top hemisphere. The cylinder between the two
  // equators is implicit by connecting the last bottom ring to the first top.
  for (let i = 0; i <= capSteps; i++) {
    const phi = -Math.PI / 2 + (Math.PI / 2) * (i / capSteps);
    rings.push({ r: radius * Math.cos(phi), z: z0 + radius + radius * Math.sin(phi) });
  }
  for (let i = 0; i <= capSteps; i++) {
    const phi = (Math.PI / 2) * (i / capSteps);
    rings.push({ r: radius * Math.cos(phi), z: z1 - radius + radius * Math.sin(phi) });
  }
  const rowStride = segments + 1;
  for (let r = 0; r < rings.length; r++) {
    const ring = rings[r];
    const v = r / (rings.length - 1);
    for (let s = 0; s <= segments; s++) {
      const th = (s / segments) * Math.PI * 2;
      positions.push(Math.cos(th) * ring.r, Math.sin(th) * ring.r, ring.z);
      st.push(s / segments, v);
    }
  }
  for (let r = 0; r < rings.length - 1; r++) {
    for (let s = 0; s < segments; s++) {
      const a = r * rowStride + s;
      const b = a + 1;
      const c = (r + 1) * rowStride + s + 1;
      const d = (r + 1) * rowStride + s;
      indices.push(a, b, c, a, c, d);
    }
  }
  return {
    name: "playercapsule",
    numFrames: 1,
    frames: [],
    tags: [],
    surfaces: [
      {
        name: "playercapsule",
        shaderNames: [],
        numVerts: positions.length / 3,
        numFrames: 1,
        indices: new Uint32Array(indices),
        st: new Float32Array(st),
        positions: new Float32Array(positions),
      },
    ],
  };
}

/** The glowing accents on the player proxy (visor, chest core, antenna tip, shoulder lights). */
function makePlayerGlowModel(): Md3Model {
  const positions: number[] = [];
  const st: number[] = [];
  const indices: number[] = [];
  // Visor slit.
  appendBox(positions, st, indices, 15.2, 0, 20, 1.4, 5.6, 1.1);
  // Chest core.
  appendBox(positions, st, indices, 15.2, 0, 13, 1.2, 2.2, 2.2);
  // Antenna tip.
  appendBox(positions, st, indices, -14, 6, 36, 2.0, 2.0, 2.0);
  // Shoulder lights.
  appendBox(positions, st, indices, 0, 15.5, 24, 1.2, 1.2, 1.2);
  appendBox(positions, st, indices, 0, -15.5, 24, 1.2, 1.2, 1.2);
  return {
    name: "playerglow",
    numFrames: 1,
    frames: [],
    tags: [],
    surfaces: [
      {
        name: "playerglow",
        shaderNames: [],
        numVerts: positions.length / 3,
        numFrames: 1,
        indices: new Uint32Array(indices),
        st: new Float32Array(st),
        positions: new Float32Array(positions),
      },
    ],
  };
}

/**
 * Dark gear attached to the capsule: a chunky gun in the right hand, a visor
 * frame, shoulder pads, a backpack and an antenna. Authored in player-local
 * space (+X forward, +Y left, +Z up), so it follows the player's yaw.
 */
function makePlayerGearModel(): Md3Model {
  const positions: number[] = [];
  const st: number[] = [];
  const indices: number[] = [];
  // Gun: receiver, barrel, grip, on the right (-Y).
  appendBox(positions, st, indices, 10, -11, 8, 8, 2.2, 2.8);
  appendBox(positions, st, indices, 20, -11, 8, 8, 1.3, 1.3);
  appendBox(positions, st, indices, 2, -11, 4.5, 2.2, 1.8, 3.4);
  // Visor frame across the front of the head.
  appendBox(positions, st, indices, 14.2, 0, 20, 2.0, 7.5, 3.2);
  // Shoulder pads.
  appendBox(positions, st, indices, 0, 12.5, 24, 3.5, 3.0, 3.0);
  appendBox(positions, st, indices, 0, -12.5, 24, 3.5, 3.0, 3.0);
  // Backpack + antenna mast.
  appendBox(positions, st, indices, -13, 0, 16, 4.0, 7.0, 7.0);
  appendBox(positions, st, indices, -14, 6, 28, 1.2, 1.2, 7.0);
  return {
    name: "playergear",
    numFrames: 1,
    frames: [],
    tags: [],
    surfaces: [
      {
        name: "playergear",
        shaderNames: [],
        numVerts: positions.length / 3,
        numFrames: 1,
        indices: new Uint32Array(indices),
        st: new Float32Array(st),
        positions: new Float32Array(positions),
      },
    ],
  };
}


/** A low-poly UV sphere (radius 1) used to draw physics balls/orbs. */
function makeSphereModel(seg = 18, rings = 12): Md3Model {
  const positions: number[] = [];
  const indices: number[] = [];
  const st: number[] = [];
  for (let r = 0; r <= rings; r++) {
    const v = r / rings;
    const phi = v * Math.PI;
    for (let s = 0; s <= seg; s++) {
      const u = s / seg;
      const theta = u * Math.PI * 2;
      positions.push(Math.sin(phi) * Math.cos(theta), Math.sin(phi) * Math.sin(theta), Math.cos(phi));
      st.push(u, v);
    }
  }
  for (let r = 0; r < rings; r++) {
    for (let s = 0; s < seg; s++) {
      const a = r * (seg + 1) + s;
      const b = a + seg + 1;
      indices.push(a, b, a + 1, a + 1, b, b + 1);
    }
  }
  return {
    name: "sphere",
    numFrames: 1,
    frames: [],
    tags: [],
    surfaces: [
      {
        name: "sphere",
        shaderNames: [],
        numVerts: positions.length / 3,
        numFrames: 1,
        indices: new Uint32Array(indices),
        st: new Float32Array(st),
        positions: new Float32Array(positions),
      },
    ],
  };
}

/** A low-poly octahedron ("diamond") used for teleporter destination markers. */
function makeDiamondModel(r = 1): Md3Model {
  const positions = new Float32Array([r, 0, 0, -r, 0, 0, 0, r, 0, 0, -r, 0, 0, 0, r, 0, 0, -r]);
  const indices = new Uint32Array([
    0, 2, 4, 2, 1, 4, 1, 3, 4, 3, 0, 4,
    2, 0, 5, 1, 2, 5, 3, 1, 5, 0, 3, 5,
  ]);
  const st = new Float32Array(6 * 2);
  return {
    name: "diamond",
    numFrames: 1,
    frames: [],
    tags: [],
    surfaces: [
      { name: "diamond", shaderNames: [], numVerts: 6, numFrames: 1, indices, st, positions },
    ],
  };
}

/** A small cube model used to draw projectiles (no weapon assets shipped). */
function makeCubeModel(half: number): Md3Model {
  const h = half;
  const positions = new Float32Array([
    -h, -h, -h, h, -h, -h, h, h, -h, -h, h, -h,
    -h, -h, h, h, -h, h, h, h, h, -h, h, h,
  ]);
  const indices = new Uint32Array([
    0, 1, 2, 0, 2, 3,
    4, 6, 5, 4, 7, 6,
    0, 4, 5, 0, 5, 1,
    1, 5, 6, 1, 6, 2,
    2, 6, 7, 2, 7, 3,
    3, 7, 4, 3, 4, 0,
  ]);
  const st = new Float32Array(8 * 2);
  return {
    name: "cube",
    numFrames: 1,
    frames: [],
    tags: [],
    surfaces: [
      { name: "cube", shaderNames: [], numVerts: 8, numFrames: 1, indices, st, positions },
    ],
  };
}

/** Default projectile hex per weapon id (grenade / rocket / plasma). */
const DEFAULT_PROJECTILE_HEX: Record<number, string> = {
  4: "#80ff66",
  5: "#ff8019",
  6: "#66b3ff",
  10: "#ffd24d",
};

/** Projectile tint per weapon id, honouring the user's colour override. */
function projectileColor(w: number): [number, number, number] {
  const hex = projectileColorOverride[w] ?? DEFAULT_PROJECTILE_HEX[w];
  return hex ? hexToRgb(hex) : [0.9, 0.9, 0.9];
}

/** Default tracer look per weapon id (colour overridable). */
const DEFAULT_BEAM: Record<
  number,
  { color: [number, number, number]; thickness: number; alpha: number }
> = {
  7: { color: [0.55, 0.9, 1.0], thickness: 2.6, alpha: 0.85 }, // Lightning Gun
  8: { color: [0.75, 0.85, 1.0], thickness: 2.2, alpha: 0.85 }, // Railgun
  2: { color: [1.0, 0.98, 0.55], thickness: 1.2, alpha: 0.7 }, // Machinegun
  3: { color: [1.0, 0.72, 0.3], thickness: 1.0, alpha: 0.7 }, // Shotgun
  1: { color: [0.85, 0.9, 1.0], thickness: 1.4, alpha: 0.6 }, // Melee (melee swipe)
};

/** Tracer look per weapon id: colour (overridable), thickness, blend alpha. */
function beamStyle(w: number): { color: [number, number, number]; thickness: number; alpha: number } {
  const base =
    DEFAULT_BEAM[w] ?? { color: [1, 1, 1] as [number, number, number], thickness: 1.5, alpha: 0.8 };
  const color = projectileColorOverride[w] ? hexToRgb(projectileColorOverride[w]) : base.color;
  return { color, thickness: base.thickness, alpha: base.alpha };
}

/** Per-weapon beam linger time (ms) — rail defaults to 400, others ~100. */
function beamTrailMs(w: number): number {
  return Math.max(0, Math.round(weaponVfx(w).vfxBeamTrailMs));
}

/** Stable key for a local beam so it is ingested exactly once. */
function localBeamKey(b: NetBeam): string {
  const f = (n: number) => n.toFixed(2);
  return (
    `${b.own}|${b.w}|` +
    `${f(b.a[0])},${f(b.a[1])},${f(b.a[2])}|` +
    `${f(b.b[0])},${f(b.b[1])},${f(b.b[2])}`
  );
}

/** Fade a fixed beam over its lifetime (smoothstep). */
function beamFade(born: number, die: number, now: number): number {
  const life = die - born;
  if (life <= 0) return 0;
  const t = Math.min(1, Math.max(0, (die - now) / life));
  return t * t * (3 - 2 * t);
}

/** Q3 nudges the visual rail start toward the gun: +4 right, -1 up. */
function offsetBeamStart(
  a: [number, number, number],
  b: [number, number, number],
): [number, number, number] {
  let dx = b[0] - a[0], dy = b[1] - a[1], dz = b[2] - a[2];
  const len = Math.hypot(dx, dy, dz);
  if (len < 1e-4) return a;
  dx /= len; dy /= len; dz /= len;
  const refX = 0;
  const refY = Math.abs(dz) > 0.99 ? 1 : 0;
  const refZ = Math.abs(dz) > 0.99 ? 0 : 1;
  let rx = dy * refZ - dz * refY;
  let ry = dz * refX - dx * refZ;
  let rz = dx * refY - dy * refX;
  const rl = Math.hypot(rx, ry, rz) || 1;
  rx /= rl; ry /= rl; rz /= rl;
  const ux = ry * dz - rz * dy;
  const uy = rz * dx - rx * dz;
  const uz = rx * dy - ry * dx;
  return [a[0] + rx * 4 - ux, a[1] + ry * 4 - uy, a[2] + rz * 4 - uz];
}

/** Run a rail-ring emit with this weapon's multipliers. */
function withRailRingVfx<T>(weapon: number, fn: () => T): T {
  const r = railRings;
  const v = weaponVfx(weapon);
  if (!r) return fn();
  const density = r.emitDensity;
  const size = r.emitSize;
  const opacity = r.emitOpacity;
  r.emitDensity = v.vfxParticleAmount;
  r.emitSize = v.vfxParticleSize;
  r.emitOpacity = 1;
  try {
    return fn();
  } finally {
    r.emitDensity = density;
    r.emitSize = size;
    r.emitOpacity = opacity;
  }
}

/**
 * Q3-style rail spiral: ring sprites spaced along the fixed beam, drifting
 * outward. Capped at 64 (Q3's uncapped 5 u spacing can spawn 1600+ on a long
 * shot, which is too much for a browser).
 */
function emitRailSpiral(
  start: [number, number, number],
  end: [number, number, number],
  weapon = 8,
): void {
  if (!railRings || !weaponVfx(weapon).vfxParticles) return;
  let dx = end[0] - start[0], dy = end[1] - start[1], dz = end[2] - start[2];
  const len = Math.hypot(dx, dy, dz);
  if (len < 16) return;
  dx /= len; dy /= len; dz /= len;
  const refX = 0;
  const refY = Math.abs(dz) > 0.99 ? 1 : 0;
  const refZ = Math.abs(dz) > 0.99 ? 0 : 1;
  let rx = dy * refZ - dz * refY;
  let ry = dz * refX - dx * refZ;
  let rz = dx * refY - dy * refX;
  const rl = Math.hypot(rx, ry, rz) || 1;
  rx /= rl; ry /= rl; rz /= rl;
  const ux = ry * dz - rz * dy;
  const uy = rz * dx - rx * dz;
  const uz = rx * dy - ry * dx;
  const color = beamStyle(weapon).color;
  // Don't spawn rings at the camera end: the first one would fill the screen.
  // Q3 starts its spiral 20 units along the beam; use a little more.
  const startOffset = Math.min(24, len * 0.25);
  const usable = len - startOffset;
  if (usable < 16) return;
  const count = Math.min(64, Math.max(8, Math.round(usable / 22)));
  withRailRingVfx(weapon, () => {
    for (let i = 0; i < count; i++) {
      const t = (i + 0.5) / count;
      const dist = startOffset + usable * t;
      const ang = i * 2.4;
      const ca = Math.cos(ang);
      const sa = Math.sin(ang);
      const ox = rx * ca + ux * sa;
      const oy = ry * ca + uy * sa;
      const oz = rz * ca + uz * sa;
      const px = start[0] + dx * dist + ox * 4;
      const py = start[1] + dy * dist + oy * 4;
      const pz = start[2] + dz * dist + oz * 4;
      railRings!.emit({
        count: 1,
        pos: [px, py, pz],
        vel: [ox * 8, oy * 8, oz * 8],
        size: 4.5,
        sizeJitter: 0.25,
        color,
        colorJitter: 0.1,
        lifeMs: 380 + t * 320,
        lifeJitter: 0.2,
        gravity: 0,
        drag: 0.1,
        alpha: 0.7,
      });
    }
  });
}

/** Rail impact: a short burst of rings + spark at the fixed hit point. */
function emitRailImpact(
  end: [number, number, number],
  dir: [number, number, number],
  weapon = 8,
): void {
  if (!railRings || !weaponVfx(weapon).vfxParticles) return;
  const color = beamStyle(weapon).color;
  withRailRingVfx(weapon, () =>
    railRings!.emit({
      count: 3,
      pos: end,
      spread: 12,
      size: 6,
      sizeJitter: 0.3,
      color,
      colorJitter: 0.15,
      lifeMs: 220,
      lifeJitter: 0.3,
      gravity: 0,
      drag: 1.0,
      alpha: 0.75,
    }),
  );
  emitWeaponParticles(weapon, () =>
    particles?.spark(end, dir, hexToRgb(vfxColorSparks)),
  );
  emitWeaponSmoke(weapon, () => smoke?.wisp(end, hexToRgb(vfxColorSmoke)));
}

/** One-shot rail trail VFX (spiral + impact). Call when a beam is first seen. */
function emitRailEffect(
  start: [number, number, number],
  end: [number, number, number],
  weapon = 8,
): void {
  let dx = end[0] - start[0], dy = end[1] - start[1], dz = end[2] - start[2];
  const len = Math.hypot(dx, dy, dz) || 1;
  dx /= len; dy /= len; dz /= len;
  emitRailSpiral(start, end, weapon);
  emitRailImpact(end, [dx, dy, dz], weapon);
}

/**
 * Copy the core's live local beams into a client-side history so they can
 * linger for the per-weapon trail time. The local beam starts at the eye; the
 * rendered muzzle is used instead so the world-space trail leaves the barrel.
 */
function ingestLocalBeams(
  bf: Float32Array,
  now: number,
  eye: Float32Array,
  yaw: number,
  pitch: number,
): void {
  for (let i = 0; i + 8 <= bf.length; i += 8) {
    const b: NetBeam = {
      own: bf[i] | 0,
      w: bf[i + 1] | 0,
      a: [bf[i + 2], bf[i + 3], bf[i + 4]],
      b: [bf[i + 5], bf[i + 6], bf[i + 7]],
    };
    const key = localBeamKey(b);
    if ((localSeenBeams.get(key) ?? 0) > now) continue;
    // Keep the dedupe key only as long as the core beam itself (~100 ms); the
    // visual trail record below lives for its own per-weapon lifetime.
    const life = beamTrailMs(b.w);
    localSeenBeams.set(key, now + 110);
    if (b.own === 0) {
      const m = viewModelMuzzle(eye, yaw, pitch, b.w) ?? placeholderMuzzle(eye, yaw, pitch);
      if (m) b.a = m;
    }
    if (life > 0) localBeams.push({ ...b, key, born: now, die: now + life });
    if (b.w === 8) emitRailEffect(b.a, b.b, b.w);
  }
  localBeams = localBeams.filter((b) => b.die > now);
  for (const [k, until] of localSeenBeams) {
    if (until <= now) localSeenBeams.delete(k);
  }
}

/**
 * Column-major matrix that stretches a unit cube along `a -> b`: model +X maps
 * to the beam direction (scaled by its length), +Y/+Z to a perpendicular frame
 * (scaled by `thickness`), translated to the midpoint.
 */
function beamMatrix(
  a: [number, number, number],
  b: [number, number, number],
  thickness: number,
): Float32Array {
  const ox = b[0] - a[0], oy = b[1] - a[1], oz = b[2] - a[2];
  // Q_rsqrt (see render/fastmath.ts): a ~0.17 % direction error is invisible
  // on a tracer, and nothing here feeds the deterministic simulation.
  const inv = fastInvSqrt(ox * ox + oy * oy + oz * oz);
  const s = Number.isFinite(inv) ? inv : 0;
  const dx = ox * s, dy = oy * s, dz = oz * s;
  // Reference axis not (nearly) parallel to the beam direction.
  let rx = 0, ry = 0, rz = 1;
  if (Math.abs(dz) > 0.99) { rx = 1; ry = 0; rz = 0; }
  // right = normalize(cross(dir, ref)); up = cross(right, dir).
  let cx = dy * rz - dz * ry, cy = dz * rx - dx * rz, cz = dx * ry - dy * rx;
  const cinv = fastInvSqrt(cx * cx + cy * cy + cz * cz);
  const cs = Number.isFinite(cinv) ? cinv : 0;
  cx *= cs; cy *= cs; cz *= cs;
  const ux = cy * dz - cz * dy, uy = cz * dx - cx * dz, uz = cx * dy - cy * dx;
  const m = new Float32Array(16);
  m[0] = ox; m[1] = oy; m[2] = oz; m[3] = 0;
  m[4] = cx * thickness; m[5] = cy * thickness; m[6] = cz * thickness; m[7] = 0;
  m[8] = ux * thickness; m[9] = uy * thickness; m[10] = uz * thickness; m[11] = 0;
  m[12] = (a[0] + b[0]) / 2; m[13] = (a[1] + b[1]) / 2; m[14] = (a[2] + b[2]) / 2; m[15] = 1;
  return m;
}

/** Append an axis-aligned box (8 verts, 36 indices) to a merged model mesh. */
function appendBox(
  positions: number[],
  st: number[],
  indices: number[],
  cx: number, cy: number, cz: number,
  hx: number, hy: number, hz: number,
): void {
  const base = positions.length / 3;
  // Corner order must match `faces` below: for each z side, (-x,-y),(+x,-y),(+x,+y),(-x,+y).
  const pair: [number, number][] = [[-1, -1], [1, -1], [1, 1], [-1, 1]];
  for (const sz of [-1, 1]) for (const [sx, sy] of pair) {
    positions.push(cx + sx * hx, cy + sy * hy, cz + sz * hz);
    st.push(0, 0);
  }
  const faces = [
    0, 1, 2, 0, 2, 3,
    4, 6, 5, 4, 7, 6,
    0, 4, 5, 0, 5, 1,
    1, 5, 6, 1, 6, 2,
    2, 6, 7, 2, 7, 3,
    3, 7, 4, 3, 4, 0,
  ];
  for (const i of faces) indices.push(base + i);
}

/**
 * A blocky placeholder first-person weapon. Authored with +X = forward,
 * +Y = right, +Z = up so `viewModelMatrix` can map it onto the camera basis.
 * Swap for real MD3 viewmodels when assets exist.
 */
function makeGunModel(): Md3Model {
  const positions: number[] = [];
  const st: number[] = [];
  const indices: number[] = [];
  appendBox(positions, st, indices, 2, 0, 0, 5, 2.0, 2.2);   // receiver/body
  appendBox(positions, st, indices, 9, 0, 0.6, 6, 1.1, 1.1); // barrel
  appendBox(positions, st, indices, -1.5, 0, -4.5, 1.6, 1.8, 3.0); // grip
  return {
    name: "viewgun",
    numFrames: 1,
    frames: [],
    tags: [],
    surfaces: [
      {
        name: "viewgun",
        shaderNames: [],
        numVerts: positions.length / 3,
        numFrames: 1,
        indices: new Uint32Array(indices),
        st: new Float32Array(st),
        positions: new Float32Array(positions),
      },
    ],
  };
}

/**
 * Camera basis for `yaw`/`pitch` (radians). Matches `lookAt`: `fwd` points
 * where you look, `right` is screen-right, `up` is screen-up.
 */
function camBasis(yaw: number, pitch: number): {
  fwd: [number, number, number];
  right: [number, number, number];
  up: [number, number, number];
} {
  const cy = Math.cos(yaw), sy = Math.sin(yaw);
  const cp = Math.cos(pitch), sp = Math.sin(pitch);
  const fwd: [number, number, number] = [cy * cp, sy * cp, sp];
  const right: [number, number, number] = [sy, -cy, 0];
  const up: [number, number, number] = [
    right[1] * fwd[2] - right[2] * fwd[1],
    right[2] * fwd[0] - right[0] * fwd[2],
    right[0] * fwd[1] - right[1] * fwd[0],
  ];
  return { fwd, right, up };
}

/**
 * Clip a beam's near end so it doesn't engulf the camera when it originates at
 * the eye. Returns null if the whole beam is closer than `minNear`.
 */
function clipBeamNear(
  a: [number, number, number],
  b: [number, number, number],
  eye: [number, number, number] | Float32Array,
  minNear: number,
): [[number, number, number], [number, number, number]] | null {
  const abx = b[0] - a[0], aby = b[1] - a[1], abz = b[2] - a[2];
  const len = Math.hypot(abx, aby, abz) || 1;
  const ex = a[0] - eye[0], ey = a[1] - eye[1], ez = a[2] - eye[2];
  const d = Math.hypot(ex, ey, ez);
  if (d >= minNear) return [a, b];
  const advance = minNear - d;
  if (advance >= len) return null;
  const ux = abx / len, uy = aby / len, uz = abz / len;
  return [
    [a[0] + ux * advance, a[1] + uy * advance, a[2] + uz * advance],
    b,
  ];
}

/** Slab-method ray vs AABB: entry distance along a unit `dir` (>= 0) or null. */
function rayBoxHit(
  origin: [number, number, number],
  dir: [number, number, number],
  mins: [number, number, number],
  maxs: [number, number, number],
): number | null {
  let tmin = 0;
  let tmax = Infinity;
  for (let a = 0; a < 3; a++) {
    if (Math.abs(dir[a]) < 1e-6) {
      if (origin[a] < mins[a] || origin[a] > maxs[a]) return null;
      continue;
    }
    const inv = 1 / dir[a];
    let t1 = (mins[a] - origin[a]) * inv;
    let t2 = (maxs[a] - origin[a]) * inv;
    if (t1 > t2) [t1, t2] = [t2, t1];
    tmin = Math.max(tmin, t1);
    tmax = Math.min(tmax, t2);
    if (tmin > tmax) return null;
  }
  return tmin;
}

/** Whether weapon `w` refires continuously while held (the LG). */
function isSmoothWeapon(w: number): boolean {
  if (sessionId === null || w <= 0) return false;
  return (core.session_weapon_smooth(sessionId, w, true) as number) > 0.5;
}

/** Clip a beam's far end to the nearest interpolated remote player box. */
function clipBeamToRemotes(
  start: [number, number, number],
  end: [number, number, number],
  now: number,
): [number, number, number] {
  const scale = currentHitboxScale();
  if (!predHitbox || predHitbox.scale !== scale) {
    const b = core.player_hit_box_bounds(scale) as unknown as Float32Array;
    predHitbox = { scale, mins: [b[0], b[1], b[2]], maxs: [b[3], b[4], b[5]] };
  }
  const box = predHitbox;
  const dx = end[0] - start[0], dy = end[1] - start[1], dz = end[2] - start[2];
  const len = Math.hypot(dx, dy, dz) || 1;
  const ux = dx / len, uy = dy / len, uz = dz / len;
  let best = len;
  for (const rp of snapBuffer.remoteStates(now, mySlot)) {
    const mins: [number, number, number] = [
      rp.origin[0] + box.mins[0],
      rp.origin[1] + box.mins[1],
      rp.origin[2] + box.mins[2],
    ];
    const maxs: [number, number, number] = [
      rp.origin[0] + box.maxs[0],
      rp.origin[1] + box.maxs[1],
      rp.origin[2] + box.maxs[2],
    ];
    const t = rayBoxHit(start, [ux, uy, uz], mins, maxs);
    if (t !== null && t < best) best = t;
  }
  return best < len ? [start[0] + ux * best, start[1] + uy * best, start[2] + uz * best] : end;
}

/**
 * Client fire prediction: when you fire a hitscan/melee weapon online, spawn an
 * instant local beam (muzzle -> local impact, clipped to the interpolated
 * remotes you can see). Damage stays server-authoritative; your own server beam
 * is skipped (below) since this replaces it.
 */
function tryPredictFire(now: number): void {
  if (!net?.connected || sessionId === null) return;
  const dash = rocketDashActive();
  if (!keys.attack && !keys.attack2 && !dash) return;
  if (now < predNextFire) return;
  const st = core.session_combat(sessionId) as unknown as Float32Array;
  if (st.length < 6 || st[5] <= 0.5) return; // dead
  const strong = dash || !keys.attack2 || keys.attack;
  const weapon = currentWeapon() ?? core.session_weapon(sessionId);
  const beam = core.session_predict_beam(sessionId, weapon, strong) as unknown as Float32Array;
  if (beam.length < 7) {
    // Projectile weapon: predict the spawn locally so it appears immediately.
    const p = core.session_predict_projectile(
      sessionId,
      weapon,
      strong,
    ) as unknown as Float32Array;
    if (p.length >= 9) {
      predictedProjectiles.push({
        start: [p[0], p[1], p[2]],
        vel: [p[3], p[4], p[5]],
        grenade: p[6] > 0.5,
        gravity: p[7],
        weapon,
        spawn: now,
        die: now + 3000,
        replaced: false,
      });
      predNextFire = now + Math.max(30, p[8]);
      emitMuzzleFlash(weapon);
    }
    return;
  }
  // Every hitscan fire flashes at the muzzle (including smooth weapons).
  emitMuzzleFlash(weapon);
  // Smooth weapons (the LG) draw a live, crosshair-anchored beam every frame
  // (see `cg_truelightning`); don't queue a per-refire segment for them.
  if (isSmoothWeapon(weapon)) {
    predNextFire = now + Math.max(30, beam[6]);
    return;
  }
  const eyeStart: [number, number, number] = [beam[0], beam[1], beam[2]];
  const end = clipBeamToRemotes(eyeStart, [beam[3], beam[4], beam[5]], now);
  // Visual start: the viewmodel muzzle at fire time. It is stored with the beam
  // and never re-anchored, so the trail is left in the world (Q3 rail trail).
  const eye = core.session_eye(sessionId) as unknown as Float32Array;
  const ang = core.session_angles(sessionId) as unknown as Float32Array;
  const visualStart: [number, number, number] =
    viewModelMuzzle(eye, ang[0], ang[1], weapon) ??
    placeholderMuzzle(eye, ang[0], ang[1]) ??
    eyeStart;
  const life = beamTrailMs(weapon);
  predictedBeams.push({ start: visualStart, end, weapon, born: now, die: now + life });
  if (weapon === 8) {
    emitRailEffect(visualStart, end, weapon);
  } else {
    const dx = end[0] - visualStart[0];
    const dy = end[1] - visualStart[1];
    const dz = end[2] - visualStart[2];
    const m = Math.hypot(dx, dy, dz) || 1;
    emitWeaponParticles(weapon, () =>
      particles?.spark(end, [dx / m, dy / m, dz / m], hexToRgb(vfxColorSparks)),
    );
  }
  // Gate the next predicted shot by the weapon's refire (the server remains the
  // authority on actual fire rate/ammo).
  predNextFire = now + Math.max(30, beam[6]);
}

/** Push the bloom tuning knobs into the renderer. */
function applyBloom(): void {
  const bl = renderer?.getBloom();
  if (!bl) return;
  bl.enabled = bloomEnabled;
  bl.intensity = bloomIntensity;
  bl.threshold = bloomThreshold;
}

let decompressor: Worker | null = null;
function decompressorWorker(): Worker {
  if (!decompressor) {
    decompressor = new Worker(new URL("./engine.worker.ts", import.meta.url), { type: "module" });
  }
  return decompressor;
}

/**
 * Fetch a `.bsp` with the worker expanding the LZMA-compressed lumps (off the
 * main thread), falling back to a direct fetch when the worker is unavailable.
 * The main thread's `bsp_parse` then skips LZMA, so the map-load stall moves
 * off-thread.
 */
async function fetchBspPlain(name: string): Promise<Uint8Array> {
  const url = bspUrl(name);
  if (typeof Worker === "undefined") return fetchBsp(url);
  try {
    const w = decompressorWorker();
    const out = await new Promise<ArrayBuffer>((res, rej) => {
      const h = (e: MessageEvent) => {
        const d = e.data as { t?: string; buf?: ArrayBuffer; error?: string };
        if (d?.t !== "bsp") return;
        w.removeEventListener("message", h);
        if (d.error || !d.buf) rej(new Error(d.error || "no buffer"));
        else res(d.buf);
      };
      w.addEventListener("message", h);
      w.postMessage({ t: "decompress", name, url });
    });
    return new Uint8Array(out);
  } catch (e) {
    log(`worker decompress failed (${(e as Error).message}); direct fetch`);
    return fetchBsp(url);
  }
}

// ---- Map-load diagnostics (see __webrace.mapDiag) ----
type DiagInfo = { tex: number[]; overlays: unknown[]; lit: boolean; blend: BlendMode; portal: boolean };
let diagMap = "";
let diagUniqueShaders: number[] = [];
let diagShaderNames: string[] = [];
let diagShaderIdx: number[] = [];
let diagInfo: Map<number, DiagInfo | null> | null = null;
let diagTex: string[] = [];

function buildMapDiag(): unknown {
  if (!diagInfo) return { map: diagMap, note: "no map loaded" };
  const info = diagInfo;
  const shaders = diagUniqueShaders.map((s, i) => {
    const ci = info.get(s);
    return {
      shader: s,
      name: diagShaderNames[i],
      missing: !info.has(s),
      noDraw: ci === null,
      tex: ci ? ci.tex : null,
      lit: ci ? ci.lit : null,
      blend: ci ? ci.blend : null,
      portal: ci ? ci.portal : null,
      overlays: ci ? ci.overlays.length : null,
    };
  });
  return {
    map: diagMap,
    uniqueShaders: diagUniqueShaders.length,
    shaderIdxUsed: [...new Set(diagShaderIdx)],
    texRequested: diagTex,
    texRequestedCount: diagTex.length,
    shaders,
  };
}

/**
 * Copy-pasteable renderer/GPU/map diagnostics (console `diag`, `?diag=1`,
 * `__webrace.diag()`): WebGL2 + adapter, context state, renderer toggles,
 * per-map shader/texture gaps and the current GL error. Used to debug
 * "everything is black" reports from other machines.
 */
function buildDiag(): Record<string, unknown> {
  const gl = renderer?.gl ?? null;
  const dbg = gl
    ? (gl.getExtension("WEBGL_debug_renderer_info") as {
        UNMASKED_VENDOR_WEBGL: number;
        UNMASKED_RENDERER_WEBGL: number;
      } | null)
    : null;
  const param = (p: number | undefined) => (gl && p !== undefined ? String(gl.getParameter(p)) : "n/a");
  const d = buildMapDiag() as {
    map?: string;
    uniqueShaders?: number;
    texRequestedCount?: number;
    shaders?: { name: string; missing: boolean; noDraw: boolean; tex: number | null }[];
  };
  const shaders = d.shaders ?? [];
  const missing = shaders.filter((s) => s.missing).map((s) => s.name);
  const noTex = shaders.filter((s) => !s.noDraw && s.tex === null).map((s) => s.name);
  const bl = renderer?.getBloom();
  return {
    ua: navigator.userAgent,
    webgl2: !!gl,
    contextLost: gl ? gl.isContextLost() : null,
    vendor: dbg ? param(dbg.UNMASKED_VENDOR_WEBGL) : param(gl?.VENDOR),
    gpu: dbg ? param(dbg.UNMASKED_RENDERER_WEBGL) : param(gl?.RENDERER),
    glVersion: param(gl?.VERSION),
    maxTexture: gl ? gl.getParameter(gl.MAX_TEXTURE_SIZE) : null,
    canvas: gl ? [gl.drawingBufferWidth, gl.drawingBufferHeight] : null,
    canvasCss: [canvas.clientWidth, canvas.clientHeight],
    dpr: window.devicePixelRatio,
    crossOriginIsolated,
    map: currentMap,
    mapId: currentMapId,
    session: sessionId,
    status: document.getElementById("status")?.textContent ?? "",
    tris: currentMapId >= 0 ? core.bsp_triangle_count(currentMapId) : 0,
    brushes: currentMapId >= 0 ? core.bsp_brush_count(currentMapId) : 0,
    uniqueShaders: d.uniqueShaders ?? 0,
    texRequested: d.texRequestedCount ?? 0,
    missingShaders: missing.length,
    missingShaderNames: missing.slice(0, 12),
    noTextureShaders: noTex.length,
    noTextureNames: noTex.slice(0, 12),
    bloom: bl
      ? { enabled: bl.enabled, intensity: bl.intensity, threshold: bl.threshold, panini: bl.panini }
      : null,
    msaa: msaaEnabled,
    textureMode,
    projection: panini > 0 ? "panini" : "rectilinear",
    panini,
    fov: renderFov,
    pvs: pvsEnabled,
    frustum: frustumEnabled,
    cull: cullEnabled,
    multiDraw: renderer?.multiDrawActive ?? false,
    lightmaps: lightmapEnabled,
    lightmap: renderer?.getLightmapInfo() ?? null,
    texUploadFailed: renderer?.getTexFailed() ?? 0,
    // Last world pass submission: `-1` = draw-all fallback (no PVS run list),
    // `0` = PVS/frustum culled everything (nothing drawn), `> 0` = runs
    // submitted. A black screen with runs > 0 means the surfaces were drawn
    // but shaded black (lightmap/texture).
    world: (() => {
      const d = renderer?.drawTimes() ?? {};
      return { runs: d.worldRuns ?? 0, groups: d.worldGroups ?? 0, ms: d.world ?? 0 };
    })(),
    wireframe,
    glError: gl ? gl.getError() : null,
    syncError: SyncError(),
    sponge: Sponge(),
  };
}

function printDiag(): void {
  consolePrint("— diag (copy this for a bug report) —");
  for (const [k, v] of Object.entries(buildDiag())) {
    consolePrint(`${k}: ${v !== null && typeof v === "object" ? JSON.stringify(v) : String(v)}`);
  }
}

/** Current VFX colour (hex) by key (see VFX_COLOR_KEYS). */
function getVfxColor(key: string): string {  switch (key) {
    case "explosion": return vfxColorExplosion;
    case "sparks": return vfxColorSparks;
    case "muzzle": return vfxColorMuzzle;
    case "smoke": return vfxColorSmoke;
    default: return "#ffffff";
  }
}

/** Set a VFX colour (hex) live. */
function setVfxColor(key: string, hex: string): void {
  if (!/^#[0-9a-f]{6}$/i.test(hex)) return;
  if (key === "explosion") vfxColorExplosion = hex;
  else if (key === "sparks") vfxColorSparks = hex;
  else if (key === "muzzle") vfxColorMuzzle = hex;
  else if (key === "smoke") vfxColorSmoke = hex;
}

/** Current projectile/pellet colour (hex) for a weapon id (override or default). */
function getProjectileColorHex(w: number): string {
  return projectileColorOverride[w] ?? DEFAULT_PROJECTILE_HEX[w] ?? "#e6e6e6";
}

/** Set a weapon's projectile/pellet colour (hex) live. */
function setProjectileColor(w: number, hex: string): void {
  if (/^#[0-9a-f]{6}$/i.test(hex)) projectileColorOverride[w] = hex;
}

/** Current projectile/orb opacity (0..1) for a weapon id. */
function getProjectileAlpha(w: number): number {
  return projectileAlphaOverride[w] ?? 1;
}

/** Set a weapon's projectile/orb opacity (0..1) live. */
function setProjectileAlpha(w: number, a: number): void {
  if (Number.isFinite(a)) projectileAlphaOverride[w] = Math.min(1, Math.max(0, a));
}

/** Push the VFX tuning knobs into the particle layers. */
function applyVfx(): void {
  if (particles) {
    particles.density = vfxAmount;
    particles.sizeScale = vfxSize;
    particles.opacity = 1;
  }
  if (smoke) {
    smoke.density = vfxSmokeDensity;
    smoke.sizeScale = vfxSize;
    smoke.opacity = vfxSmokeOpacity;
  }
  if (railRings) {
    railRings.density = vfxAmount;
    railRings.sizeScale = vfxSize;
    railRings.opacity = 1;
  }
}

/**
 * Run one glow-particle emit with this weapon's multipliers layered on top of
 * the global Video knobs. Restores the system values afterwards, so the global
 * state stays authoritative for other callers.
 */
function withParticleVfx<T>(weapon: number, fn: () => T): T {
  const p = particles;
  const v = weaponVfx(weapon);
  if (!p) return fn();
  const density = p.emitDensity;
  const size = p.emitSize;
  const opacity = p.emitOpacity;
  p.emitDensity = v.vfxParticleAmount;
  p.emitSize = v.vfxParticleSize;
  p.emitOpacity = 1;
  try {
    return fn();
  } finally {
    p.emitDensity = density;
    p.emitSize = size;
    p.emitOpacity = opacity;
  }
}

/** As `withParticleVfx`, for the alpha smoke layer. */
function withSmokeVfx<T>(weapon: number, fn: () => T): T {
  const s = smoke;
  const v = weaponVfx(weapon);
  if (!s) return fn();
  const density = s.emitDensity;
  const size = s.emitSize;
  const opacity = s.emitOpacity;
  s.emitDensity = v.vfxSmokeAmount;
  s.emitSize = v.vfxSmokeSize;
  s.emitOpacity = v.vfxSmokeOpacity;
  try {
    return fn();
  } finally {
    s.emitDensity = density;
    s.emitSize = size;
    s.emitOpacity = opacity;
  }
}

/** Per-weapon gate + multiplier for a glow emit (no-op when disabled). */
function emitWeaponParticles(weapon: number, fn: () => void): void {
  if (!weaponVfx(weapon).vfxParticles) return;
  withParticleVfx(weapon, fn);
}

/** Per-weapon gate + multiplier for a smoke emit (no-op when disabled). */
function emitWeaponSmoke(weapon: number, fn: () => void): void {
  if (!weaponVfx(weapon).vfxSmoke) return;
  withSmokeVfx(weapon, fn);
}

/** Emit a client-local muzzle flash (glow + a little smoke) at the muzzle. */
function emitMuzzleFlash(weapon: number | null = currentWeapon()): void {
  if (sessionId === null) return;
  const w = weapon ?? currentWeapon();
  if (w == null) return;
  const eye = core.session_eye(sessionId) as unknown as Float32Array;
  const ang = core.session_angles(sessionId) as unknown as Float32Array;
  const { fwd } = camBasis(ang[0], ang[1]);
  const pos: [number, number, number] =
    viewModelMuzzle(eye, ang[0], ang[1], w) ?? placeholderMuzzle(eye, ang[0], ang[1]);
  emitWeaponParticles(w, () =>
    particles?.muzzle(pos, [fwd[0], fwd[1], fwd[2]], hexToRgb(vfxColorMuzzle)),
  );
  // Small, short, pushed forward — a full-size wisp at the muzzle drifts up
  // over the crosshair (worst on rapid-fire hitscan like the LG).
  emitWeaponSmoke(w, () =>
    smoke?.wisp(
      [pos[0] + fwd[0] * 10, pos[1] + fwd[1] * 10, pos[2] + fwd[2] * 10],
      hexToRgb(vfxColorSmoke),
      3,
      300,
    ),
  );
}

/**
 * First-person viewmodel instance for the current camera + weapon, or null.
 * Placed in world space relative to the eye (the renderer clears depth before
 * drawing it, so it never clips into geometry).
 */
/**
 * Default viewmodel placement `[forward, right, up, pitch, roll, yaw]` for a
 * real (metre-scale, grip-at-origin) asset. Overridable per weapon via the
 * manifest `handOffset`. The placeholder `VIEWMODEL_*` constants are separate.
 */
const VIEWMODEL_HAND_DEFAULT: [number, number, number, number, number, number] = [
  9.0, // forward (closer = bigger on screen)
  15.0, // right
  -15.0, // up (below the eye — grip near the bottom edge)
  -1.0, // pitch — keep the barrel ~parallel to the view so beams leave it straight
  0, // roll
  3.0, // yaw (a touch inward; large yaw makes the barrel point away from the beam)
];
/** Extra scale for real viewmodels (world-FOV framing). */
const VIEWMODEL_ASSET_SCALE = 0.52;
/**
 * Projectiles are drawn larger than life so they're easy to track (the sim
 * size is unchanged — this is presentation only).
 */
const PROJECTILE_VISUAL_SCALE = 1.8;


/** Rotate vector `v` about unit axis `a` by `ang` (Rodrigues). */
function rotateAbout(
  v: [number, number, number],
  a: [number, number, number],
  ang: number,
): [number, number, number] {
  const c = Math.cos(ang), s = Math.sin(ang);
  const d = v[0] * a[0] + v[1] * a[1] + v[2] * a[2];
  return [
    v[0] * c + (a[1] * v[2] - a[2] * v[1]) * s + a[0] * d * (1 - c),
    v[1] * c + (a[2] * v[0] - a[0] * v[2]) * s + a[1] * d * (1 - c),
    v[2] * c + (a[0] * v[1] - a[1] * v[0]) * s + a[2] * d * (1 - c),
  ];
}

interface ViewPlacement {
  hidden: boolean;
  /** Free offsets in view units: forward / right / up. */
  fwd: number;
  right: number;
  up: number;
  /** Model scale multiplier. */
  size: number;
  /** 1 = right, 0 = centre, -1 = left. */
  mirror: number;
}

/** The active weapon's viewmodel placement (preset + free offsets + size). */
function viewPlacement(): ViewPlacement {
  const p = weaponProfile.weaponPosition;
  return {
    hidden: p.preset === "hidden",
    fwd: p.x,
    right: p.y,
    up: p.z,
    size: p.size,
    mirror: p.preset === "left" ? -1 : p.preset === "center" ? 0 : 1,
  };
}

/** Muzzle (barrel tip) of the procedural placeholder gun, with placement. */
function placeholderMuzzle(
  eye: [number, number, number] | Float32Array,
  yaw: number,
  pitch: number,
): [number, number, number] {
  const { fwd, right, up } = camBasis(yaw, pitch);
  const pl = viewPlacement();
  const mf = VIEWMODEL_FWD + pl.fwd + VIEWMODEL_TIP_X * VIEWMODEL_SCALE * pl.size;
  const mr = VIEWMODEL_RIGHT * pl.mirror + pl.right;
  const mu = VIEWMODEL_DOWN + pl.up;
  return [
    eye[0] + fwd[0] * mf + right[0] * mr + up[0] * mu,
    eye[1] + fwd[1] * mf + right[1] * mr + up[1] * mu,
    eye[2] + fwd[2] * mf + right[2] * mr + up[2] * mu,
  ];
}

function viewModelInstance(
  eye: [number, number, number] | Float32Array,
  yaw: number,
  pitch: number,
  weapon: number | null,
): ModelInstance | null {
  const { fwd, right, up } = camBasis(yaw, pitch);
  const pl = viewPlacement();
  if (pl.hidden) return null;

  // Real MD3 viewmodel from the asset pack, when one is loaded for this weapon.
  const key = weapon != null ? `weapon.${weapon}.view` : "";
  const asset = key ? assetModelId(key) : -1;
  if (asset >= 0) {
    const e = assetEntry(key);
    const [hx, hy, hz, hp, hr, hyaw] = e?.handOffset ?? VIEWMODEL_HAND_DEFAULT;
    // Aim the model: roll about forward, then yaw about up, then pitch about right.
    let X: [number, number, number] = [fwd[0], fwd[1], fwd[2]];
    let Y: [number, number, number] = [right[0], right[1], right[2]];
    let Z: [number, number, number] = [up[0], up[1], up[2]];
    const R = (hr * Math.PI) / 180, Yw = ((hyaw * pl.mirror) * Math.PI) / 180, P = (hp * Math.PI) / 180;
    if (R) { Y = rotateAbout(Y, X, R); Z = rotateAbout(Z, X, R); }
    if (Yw) { X = rotateAbout(X, Z, Yw); Y = rotateAbout(Y, Z, Yw); }
    if (P) { X = rotateAbout(X, Y, P); Z = rotateAbout(Z, Y, P); }
    const s = VIEWMODEL_ASSET_SCALE * (e?.scale ?? 1) * pl.size;
    const m = new Float32Array(16);
    m[0] = X[0] * s; m[1] = X[1] * s; m[2] = X[2] * s; m[3] = 0;
    m[4] = Y[0] * s; m[5] = Y[1] * s; m[6] = Y[2] * s; m[7] = 0;
    m[8] = Z[0] * s; m[9] = Z[1] * s; m[10] = Z[2] * s; m[11] = 0;
    const px = hx + pl.fwd;
    const py = hy * pl.mirror + pl.right;
    const pz = hz + pl.up;
    m[12] = eye[0] + fwd[0] * px + right[0] * py + up[0] * pz;
    m[13] = eye[1] + fwd[1] * px + right[1] * py + up[1] * pz;
    m[14] = eye[2] + fwd[2] * px + right[2] * py + up[2] * pz;
    m[15] = 1;
    return { model: asset, matrix: m, color: [1, 1, 1], alpha: 1, frame0: 0, frame1: 0, lerp: 0 };
  }

  // Fallback: the procedural placeholder gun.
  if (viewModelId < 0) return null;
  const pf = VIEWMODEL_FWD + pl.fwd;
  const pr = VIEWMODEL_RIGHT * pl.mirror + pl.right;
  const pu = VIEWMODEL_DOWN + pl.up;
  const ox = eye[0] + fwd[0] * pf + right[0] * pr + up[0] * pu;
  const oy = eye[1] + fwd[1] * pf + right[1] * pr + up[1] * pu;
  const oz = eye[2] + fwd[2] * pf + right[2] * pr + up[2] * pu;
  const m = new Float32Array(16);
  const s = VIEWMODEL_SCALE * pl.size;
  m[0] = fwd[0] * s; m[1] = fwd[1] * s; m[2] = fwd[2] * s; m[3] = 0;
  m[4] = right[0] * s; m[5] = right[1] * s; m[6] = right[2] * s; m[7] = 0;
  m[8] = up[0] * s; m[9] = up[1] * s; m[10] = up[2] * s; m[11] = 0;
  m[12] = ox; m[13] = oy; m[14] = oz; m[15] = 1;
  return {
    model: viewModelId,
    matrix: m,
    color: VIEWMODEL_COLOR,
    alpha: VIEWMODEL_ALPHA,
    frame0: 0,
    frame1: 0,
    lerp: 0,
  };
}

/**
 * World-space muzzle (barrel tip) for the current viewmodel, or null when the
 * placeholder gun is in use. Transforms the asset's model-space `muzzle` point
 * by the viewmodel matrix, so muzzle flashes and beams leave the actual barrel
 * instead of a fixed offset tuned for the old placeholder.
 */
function viewModelMuzzle(
  eye: [number, number, number] | Float32Array,
  yaw: number,
  pitch: number,
  weapon: number | null,
): [number, number, number] | null {
  const key = weapon != null ? `weapon.${weapon}.view` : "";
  const e = key ? assetEntry(key) : null;
  if (!e?.muzzle) return null;
  const inst = viewModelInstance(eye, yaw, pitch, weapon);
  if (!inst) return null;
  const m = inst.matrix;
  const [x, y, z] = e.muzzle;
  return [
    m[0] * x + m[4] * y + m[8] * z + m[12],
    m[1] * x + m[5] * y + m[9] * z + m[13],
    m[2] * x + m[6] * y + m[10] * z + m[14],
  ];
}

/** The ghost's model instance for this frame, or null when there is no ghost. */
function ghostInstance(): ModelInstance | null {
  if (sessionId === null || ghostModelId < 0) return null;
  if (ghostSource === "off") return null;
  if (!core.session_race_running(sessionId)) return null;
  const rt = core.session_race_ticks(sessionId);
  const pose = core.session_ghost_at(sessionId, rt) as unknown as Float32Array;
  if (pose.length < 5) return null;
  const color: [number, number, number] =
    ghostSource === "wr"
      ? [1.0, 0.8, 0.2]
      : ghostSource === "pr"
        ? [0.4, 1.0, 0.4]
        : [0.35, 0.85, 1.0];
  return {
    model: ghostModelId,
    matrix: modelMatrix([pose[0], pose[1], pose[2]], [0, pose[3], 0], currentHitboxScale()),
    color,
    alpha: 0.45,
    frame0: 0,
    frame1: 0,
    lerp: 0,
  };
}

/** Push the selected ghost source into the live session. */
function applyGhostSource(): void {
  if (sessionId === null) return;
  if (ghostSource === "off") {
    core.session_ghost_clear(sessionId);
    return;
  }
  const buf = ghostSource === "pr" ? ghostPR : ghostSource === "wr" ? ghostWR : ghostLast;
  if (buf && buf.length > 0) core.session_ghost_import(sessionId, buf);
}

/** Cycle the ghost source last -> PR -> WR -> off -> last. */
function cycleGhostSource(): void {
  ghostSource =
    ghostSource === "last"
      ? "pr"
      : ghostSource === "pr"
        ? "wr"
        : ghostSource === "wr"
          ? "off"
          : "last";
  applyGhostSource();
  menu?.refreshRecall();
  const label =
    ghostSource === "off"
      ? "off"
      : ghostSource === "last"
        ? "last run"
        : ghostSource === "pr"
          ? "personal record"
          : "world record";
  showAnnounce(`ghost: ${label}`, 1400);
}

/** Fetch the map's WR + your PR ghosts (if any) from the leaderboard. */
async function refreshGhosts(map: string): Promise<void> {
  const identity = getIdentity();
  const [wr, pr] = await Promise.all([
    fetchGhost(map, "wr"),
    identity ? fetchGhost(map, "pr", identity.token) : Promise.resolve(null),
  ]);
  ghostWR = wr;
  ghostPR = pr;
}

/**
 * Fetch leaderboard checkpoint splits + the caller's PB for the checkpoint HUD.
 * Runs once per map and again after a finish (the PB may have just moved).
 */
async function refreshRaceBoard(map: string): Promise<void> {
  const req = ++raceBoardReq;
  const identity = getIdentity();
  const board = await fetchLeaderboardBoard(map, identity?.token);
  if (req !== raceBoardReq) return; // a newer map load won
  raceBoard = board;
  raceBoardSerial++;
  checkpointSig = ""; // force a redraw if a checkpoint is already on screen
}

function resetCheckpointHud(): void {
  checkpointSig = "";
  checkpointHudEl.innerHTML = "";
  hudRuntime?.setShown("checkpoint-hud", false);
}

/** Placeholder row: no board yet, or the run has not reached CP1. */
function renderCheckpointPlaceholder(cpCount: number, totalCp: number, rank: number | null): void {
  const rankClass = rank === 1 ? "cp-rank is-top rank-1 is-first" : "cp-rank";
  const rankText = rank === null ? "#\u2014" : `#${rank}`;
  checkpointHudEl.innerHTML =
    `<span class="cp-label">CP ${cpCount}/${totalCp}</span>` +
    `<span class="${rankClass}">${rankText}</span>` +
    `<span class="cp-pb is-none">PB \u2014</span>` +
    `<span class="cp-gaps"><span>next <b>\u2014</b></span><span>WR <b>\u2014</b></span></span>`;
  hudRuntime?.setShown("checkpoint-hud", true);
}

/** One compact line: CP 3/12 · #2 · PB -0.069s · next +0.312 · WR +1.204 */
function renderCheckpointHud(splits: Uint32Array, cpCount: number, totalCp: number): void {
  if (!raceBoard) {
    // The board request failed or is still in flight. Keep the panel visible
    // (with #1, as requested when there is no time) instead of hiding it.
    renderCheckpointPlaceholder(cpCount, totalCp, 1);
    return;
  }
  if (cpCount === 0) {
    renderCheckpointPlaceholder(0, totalCp, null);
    return;
  }
  const readout = computeCheckpointReadout(
    splits[cpCount - 1],
    cpCount - 1,
    raceBoard.entries,
    raceBoard.me,
  );
  const rank = readout.rank;
  const rankClass =
    rank <= 5 ? `cp-rank is-top rank-${rank}${rank === 1 ? " is-first" : ""}` : "cp-rank";
  const signed = (ms: number, suffix = ""): string => {
    const sign = ms > 0 ? "+" : ms < 0 ? "\u2212" : "\u00b1";
    return `${sign}${(Math.abs(ms) / 1000).toFixed(3)}${suffix}`;
  };
  const pb =
    readout.pbDeltaMs === null
      ? `<span class="cp-pb is-none">PB \u2014</span>`
      : `<span class="cp-pb ${
          readout.pbDeltaMs < 0 ? "is-faster" : readout.pbDeltaMs > 0 ? "is-slower" : "is-even"
        }">PB ${signed(readout.pbDeltaMs, "s")}</span>`;
  const next =
    readout.nextGapMs === null
      ? `<span>next <b>\u2014</b></span>`
      : `<span>next <b>${signed(readout.nextGapMs)}</b></span>`;
  const wr =
    readout.wrGapMs === null
      ? `<span>WR <b>\u2014</b></span>`
      : `<span class="${readout.wrGapMs < 0 ? "is-faster" : ""}">WR <b>${signed(readout.wrGapMs)}</b></span>`;
  checkpointHudEl.innerHTML =
    `<span class="cp-label">CP ${cpCount}/${totalCp}</span>` +
    `<span class="${rankClass}">#${rank}</span>` +
    pb +
    `<span class="cp-gaps">${next}${wr}</span>`;
  hudRuntime?.setShown("checkpoint-hud", true);
}

/** Show/refresh the checkpoint panel after a checkpoint pass. */
function updateCheckpointHud(
  running: boolean,
  finished: boolean,
  splits: Uint32Array,
  totalCp: number,
  raceMode: boolean,
): void {
  const cpCount = splits.length;
  const show =
    sessionId !== null &&
    raceMode &&
    running &&
    !finished &&
    core.session_mode(sessionId) !== 3 &&
    totalCp > 0;
  if (!show) {
    if (checkpointSig !== "") resetCheckpointHud();
    return;
  }
  const sig = `${sessionId}:${cpCount}:${totalCp}:${raceBoardSerial}`;
  if (sig === checkpointSig) return;
  checkpointSig = sig;
  renderCheckpointHud(splits, cpCount, totalCp);
}

// ---- Remote players (multiplayer) ----

const SLOT_COLORS: [number, number, number][] = [
  [1.0, 0.35, 0.35],
  [0.4, 0.7, 1.0],
  [0.5, 1.0, 0.5],
  [1.0, 0.8, 0.2],
  [0.8, 0.45, 1.0],
  [1.0, 0.6, 0.2],
];

function slotColor(slot: number): [number, number, number] {
  return SLOT_COLORS[slot % SLOT_COLORS.length];
}

/** Push a colour toward a bright neon tint for an outline shell. */
function neonColor(c: [number, number, number]): [number, number, number] {
  return [Math.min(1, c[0] * 1.15), Math.min(1, c[1] * 1.15), Math.min(1, c[2] * 1.15)];
}

/**
 * A darker, hue-matched version of a colour — the palette-generator idea
 * (`oklch(from c calc(l * n) … h)`) applied per player colour. Keeps the hue,
 * lowers lightness, so the body matches the outline without competing with it.
 */
function shadeColor(
  c: [number, number, number],
  lightFactor: number,
  satFactor = 1,
): [number, number, number] {
  const r = c[0], g = c[1], b = c[2];
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const l = (max + min) / 2;
  const d = max - min;
  let h = 0;
  let s = 0;
  if (d > 1e-6) {
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    if (max === r) h = (g - b) / d + (g < b ? 6 : 0);
    else if (max === g) h = (b - r) / d + 2;
    else h = (r - g) / d + 4;
    h /= 6;
  }
  const L = Math.min(0.92, Math.max(0.07, l * lightFactor));
  const S = Math.min(1, Math.max(0, s * satFactor));
  if (S <= 1e-6) return [L, L, L];
  const q = L < 0.5 ? L * (1 + S) : L + S - L * S;
  const p = 2 * L - q;
  const rgb = (t: number): number => {
    let u = t;
    if (u < 0) u += 1;
    if (u > 1) u -= 1;
    if (u < 1 / 6) return p + (q - p) * 6 * u;
    if (u < 1 / 2) return q;
    if (u < 2 / 3) return p + (q - p) * (2 / 3 - u) * 6;
    return p;
  };
  return [rgb(h + 1 / 3), rgb(h), rgb(h - 1 / 3)];
}

/** An inflated additive hull around a model instance, used for the neon outline. */
function outlineInstance(
  body: ModelInstance,
  outlineModel: number,
  alpha = 0.45,
): ModelInstance | null {
  if (outlineModel < 0) return null;
  return {
    ...body,
    model: outlineModel,
    color: neonColor(body.color),
    alpha,
    glow: true,
    cull: "front",
  };
}

// ---- Neon player trails ----

/** Bright, well-separated hues for "random per player" trail colors. */
const TRAIL_RANDOM_PALETTE: [number, number, number][] = [
  [1.0, 0.32, 0.12], // orange
  [1.0, 0.78, 0.16], // amber
  [0.62, 1.0, 0.24], // lime
  [0.2, 1.0, 0.62], // mint
  [0.18, 0.82, 1.0], // cyan
  [0.35, 0.5, 1.0], // blue
  [0.72, 0.36, 1.0], // violet
  [1.0, 0.32, 0.78], // magenta
];
/** Per-load seed so "random per player" is stable for the session. */
const TRAIL_RANDOM_SEED = (Math.random() * 0xffffffff) >>> 0;

function hash32(x: number): number {
  let h = (x | 0) ^ TRAIL_RANDOM_SEED;
  h = Math.imul(h ^ (h >>> 16), 0x7feb352d);
  h = Math.imul(h ^ (h >>> 15), 0x846ca68b);
  return (h ^ (h >>> 16)) >>> 0;
}

/** Trail colour for a player slot: self/allies vs enemies, per the settings. */
function trailColorForSlot(slot: number): [number, number, number] {
  const myTeam = lastSelf?.team ?? 0;
  const team = slot === mySlot ? myTeam : playerTeam(slot);
  const mine = mySlot < 0 || slot === mySlot || (myTeam > 0 && team === myTeam);
  const mode = mine ? trailSelfMode : trailEnemyMode;
  if (mode === "random") {
    return TRAIL_RANDOM_PALETTE[hash32(slot + 1) % TRAIL_RANDOM_PALETTE.length];
  }
  if (mode === "team") {
    if (team > 0) return hexToRgb(mine ? trailSelfTeamColor : trailEnemyTeamColor);
    // FFA / no team: your own colour for you, per-slot colours for enemies.
    return mine ? hexToRgb(trailSelfColor) : slotColor(slot);
  }
  return hexToRgb(mine ? trailSelfColor : trailEnemyColor);
}

/** Push the persisted trail settings into the live trail renderer. */
function applyPlayerTrail(): void {
  if (!playerTrails) return;
  playerTrails.seconds = Math.min(3, Math.max(0, trailSeconds));
  playerTrails.width = Math.min(8, Math.max(0.5, trailWidth));
  playerTrails.intensity = Math.min(2, Math.max(0.1, trailIntensity));
}

function remoteInstances(now: number): ModelInstance[] {
  if (ghostModelId < 0) return [];
  const out: ModelInstance[] = [];
  for (const s of snapBuffer.remoteStates(now, mySlot)) {
    // Match the enemy glow to the same colour the player picked for trails.
    out.push(...playerInstances(modelMatrix(s.origin, [0, s.yaw, 0], currentHitboxScale()), trailColorForSlot(s.slot), 1));
  }
  return out;
}

/**
 * Build the visible player proxy for one pose: a hue-matched body (darker than
 * the outline), a wider dim halo, a bright outline, dark gear, and glowing
 * accents. The AABB hitbox is unchanged; this is the presentation layer.
 */
function playerInstances(
  matrix: Float32Array,
  glowColor: [number, number, number],
  alpha: number,
): ModelInstance[] {
  const out: ModelInstance[] = [];
  const ev = settingsStore.value().enemyVisual;
  const outlineBright = Math.min(2, Math.max(0, ev.outlineBrightness ?? 1));
  const haloBright = Math.min(2, Math.max(0, ev.haloBrightness ?? 1));
  // Body: same hue as the outline, darker but still bright enough to spot.
  const bodyColor = shadeColor(glowColor, 0.4, 0.9);
  const gearColor = shadeColor(glowColor, 0.22, 0.8);
  const body: ModelInstance = {
    model: ghostModelId,
    matrix,
    color: bodyColor,
    alpha,
    frame0: 0,
    frame1: 0,
    lerp: 0,
  };
  // Wider, dim outer halo first: a soft falloff that bloom turns into glow.
  if (ghostHaloModelId >= 0) {
    out.push({
      ...body,
      matrix: scaleMatrix(matrix, ev.haloSize ?? 1),
      model: ghostHaloModelId,
      color: scaleColor(glowColor, haloBright),
      alpha: (alpha < 1 ? 0.16 : 0.22) * Math.min(1, haloBright),
      glow: true,
      cull: "front",
    });
  }
  const outline = outlineInstance(
    { ...body, color: glowColor, matrix: scaleMatrix(matrix, ev.outlineSize ?? 1) },
    ghostOutlineModelId,
    (alpha < 1 ? 0.5 : 0.75) * Math.min(1, outlineBright),
  );
  if (outline) {
    if (outlineBright > 1) outline.color = scaleColor(outline.color, outlineBright);
    out.push(outline);
  }
  out.push(body);
  if (playerGearModelId >= 0) {
    out.push({
      model: playerGearModelId,
      matrix,
      color: gearColor,
      alpha,
      frame0: 0,
      frame1: 0,
      lerp: 0,
    });
  }
  if (playerGlowModelId >= 0) {
    out.push({
      model: playerGlowModelId,
      matrix,
      color: neonColor(glowColor),
      alpha: alpha < 1 ? 0.75 : 0.95,
      frame0: 0,
      frame1: 0,
      lerp: 0,
      glow: true,
    });
  }
  return out;
}

/** Scale a model matrix's basis vectors about its origin (translation kept). */
function scaleMatrix(m: Float32Array, s: number): Float32Array {
  if (s === 1) return m;
  const out = new Float32Array(m);
  out[0] *= s; out[1] *= s; out[2] *= s;
  out[4] *= s; out[5] *= s; out[6] *= s;
  out[8] *= s; out[9] *= s; out[10] *= s;
  return out;
}

/** Multiply a colour (values may exceed 1 to feed bloom). */
function scaleColor(c: [number, number, number], k: number): [number, number, number] {
  return [c[0] * k, c[1] * k, c[2] * k];
}

// ---- Floating player name tags (world -> screen) ----
const nametagsEl = document.getElementById("nametags")!;
const nameTags = new Map<number, HTMLDivElement>();

function projectToScreen(
  vp: Float32Array,
  p: [number, number, number],
  w: number,
  h: number,
): { x: number; y: number; visible: boolean } {
  const cx = vp[0] * p[0] + vp[4] * p[1] + vp[8] * p[2] + vp[12];
  const cy = vp[1] * p[0] + vp[5] * p[1] + vp[9] * p[2] + vp[13];
  const cw = vp[3] * p[0] + vp[7] * p[1] + vp[11] * p[2] + vp[15];
  if (cw <= 1e-4) return { x: 0, y: 0, visible: false };
  let nx = cx / cw;
  let ny = cy / cw;
  if (panini > 0) {
    // Forward generalized Pannini: rectilinear NDC -> panini NDC. This mirrors
    // the composite shader so DOM overlays (name tags, pings) line up with the
    // warped 3D scene.
    const d = panini;
    const hh = ((renderFov * Math.PI) / 180) * 0.5;
    const hv = Math.atan(Math.tan(hh) / (canvas.width / canvas.height));
    const tanH = Math.tan(hh), tanV = Math.tan(hv);
    const cosHh = Math.cos(hh);
    const hmax = ((d + 1) * Math.sin(hh)) / (d + cosHh);
    const vmax = (tanV * hmax) / tanH;
    const phi = Math.atan(nx * tanH);
    const cosPhi = Math.cos(phi);
    const theta = Math.atan(ny * cosPhi * tanV);
    nx = (((d + 1) * Math.sin(phi)) / (d + cosPhi)) / hmax;
    ny = (((d + 1) * Math.tan(theta)) / (d + cosPhi)) / vmax;
  }
  return {
    x: (nx * 0.5 + 0.5) * w,
    y: (1 - (ny * 0.5 + 0.5)) * h,
    visible: true,
  };
}

function updateNameTags(
  states: { slot: number; origin: [number, number, number]; yaw: number }[],
  vp: Float32Array,
  w: number,
  h: number,
): void {
  const seen = new Set<number>();
  for (const s of states) {
    seen.add(s.slot);
    let el = nameTags.get(s.slot);
    if (!el) {
      el = document.createElement("div");
      el.className = "nametag";
      nametagsEl.appendChild(el);
      nameTags.set(s.slot, el);
    }
    // Head of the player box (maxs.z = 32) + a little margin.
    const nick = roster.find((r) => r.slot === s.slot)?.nick ?? "";
    const html = q3Html(nick || `player ${s.slot}`);
    if (el.dataset.nick !== html) {
      el.innerHTML = html;
      el.dataset.nick = html;
    }
    const head: [number, number, number] = [s.origin[0], s.origin[1], s.origin[2] + 44];
    const { x, y, visible } = projectToScreen(vp, head, w, h);
    if (!visible || x < -120 || x > w + 120 || y < -60 || y > h + 60) {
      el.style.display = "none";
      continue;
    }
    el.style.display = "block";
    el.style.left = `${x.toFixed(1)}px`;
    el.style.top = `${y.toFixed(1)}px`;
  }
  for (const [slot, el] of nameTags) {
    if (!seen.has(slot)) {
      el.remove();
      nameTags.delete(slot);
    }
  }
}

// ---- Ping / tag markers (team-only, or global in race/freestyle) ----

const pingsEl = document.getElementById("pings")!;
type PingKind = "look" | "enemy" | "heal";

interface PingMarker {
  id: number;
  slot: number;
  o: [number, number, number];
  nick: string;
  kind: PingKind;
  born: number;
  die: number;
  /** Fade window at the end of `die` (ms). */
  fade: number;
  /** Opacity at the start of the fade window (lets a re-fade start smoothly). */
  op0: number;
  el: HTMLDivElement;
  label: HTMLSpanElement;
}

const pingMarkers: PingMarker[] = [];
let pingSeq = 0;
let lastPingAt = 0;

/** Current marker opacity: full until the end-fade window, then linear to 0. */
function pingOpacity(m: PingMarker, now: number): number {
  const k = (m.die - now) / Math.max(1, m.fade);
  return Math.max(0, Math.min(1, k)) * m.op0;
}

const PING_LABEL: Record<PingKind, string> = {
  look: "PING",
  enemy: "ENEMIES",
  heal: "HEAL",
};

function addPingMark(p: NetPingMark): void {
  const now = performance.now();
  // Only one active ping: every existing marker — including any previous
  // sender's — fades out when a new one arrives.
  const fadeMs = Math.max(1, adminSettings.pingFadeMs);
  for (const m of pingMarkers) {
    // Start the new fade from wherever the old marker is right now, so a ping
    // that was already fading doesn't pop back to full opacity.
    m.op0 = pingOpacity(m, now);
    m.die = Math.min(m.die, now + fadeMs);
    m.fade = Math.max(1, Math.min(m.die - now, fadeMs));
  }
  const c = trailColorForSlot(p.slot);
  const el = document.createElement("div");
  el.className = "ping";
  el.style.color = `rgb(${Math.round(c[0] * 255)}, ${Math.round(c[1] * 255)}, ${Math.round(c[2] * 255)})`;
  const dot = document.createElement("span");
  dot.className = "ping-dot";
  const label = document.createElement("span");
  label.className = "ping-label";
  el.append(dot, label);
  el.title = stripQ3Colors(p.nick || "");
  pingsEl.appendChild(el);
  const kind: PingKind =
    p.kind === "enemy" || p.kind === "heal" ? p.kind : "look";
  pingMarkers.push({
    id: ++pingSeq,
    slot: p.slot,
    o: [p.o[0], p.o[1], p.o[2]],
    nick: p.nick || "",
    kind,
    born: now,
    die: now + Math.max(500, adminSettings.pingTtlMs),
    fade: Math.max(1, adminSettings.pingEndFadeMs),
    op0: 1,
    el,
    label,
  });
  audio.play("ping");
}

/** Fire a ping at the crosshair target and relay it to teammates (server). */
function sendPing(kind: PingKind = "look"): void {
  if (sessionId === null) return;
  const now = performance.now();
  if (now - lastPingAt < Math.max(0, adminSettings.pingCooldownMs)) return;
  lastPingAt = now;
  let eye = lastRay?.eye;
  let fwd = lastRay?.fwd;
  if (!eye || !fwd) {
    const e = core.session_eye(sessionId) as unknown as Float32Array;
    const a = core.session_angles(sessionId) as unknown as Float32Array;
    eye = [e[0], e[1], e[2]];
    fwd = camBasis(a[0], a[1]).fwd;
  }
  const range = 16384;
  const start: [number, number, number] = [eye[0], eye[1], eye[2]];
  const end: [number, number, number] = [
    start[0] + fwd[0] * range,
    start[1] + fwd[1] * range,
    start[2] + fwd[2] * range,
  ];
  let point: [number, number, number] = end;
  const tr = core.session_trace_ray(
    sessionId,
    start[0], start[1], start[2],
    end[0], end[1], end[2],
    1.0,
  ) as unknown as Float32Array;
  if (tr.length >= 5 && tr[4] < 0.5 && tr[0] < 1) {
    const t = tr[0] * range;
    point = [start[0] + fwd[0] * t, start[1] + fwd[1] * t, start[2] + fwd[2] * t];
  }
  point = clipBeamToRemotes(start, point, now);
  const slot = mySlot >= 0 ? mySlot : 0;
  const team = lastSelf?.team ?? 0;
  const nick = getIdentity()?.nickname ?? "";
  addPingMark({ slot, team, o: point, nick, kind });
  if (net?.connected) {
    net.sendText(JSON.stringify({ t: "pingMark", o: point, kind }));
  }
}

/** Project + fade the active ping markers; called from the render loop. */
function updatePings(
  now: number,
  vp: Float32Array,
  w: number,
  h: number,
  eye: readonly [number, number, number] | Float32Array,
): void {
  for (let i = pingMarkers.length - 1; i >= 0; i--) {
    const m = pingMarkers[i];
    const left = m.die - now;
    if (left <= 0) {
      m.el.remove();
      pingMarkers.splice(i, 1);
      continue;
    }
    const { x, y, visible } = projectToScreen(vp, m.o, w, h);
    if (!visible || x < -80 || x > w + 80 || y < -60 || y > h + 60) {
      m.el.style.display = "none";
      continue;
    }
    m.el.style.display = "flex";
    m.el.style.left = `${x.toFixed(1)}px`;
    m.el.style.top = `${y.toFixed(1)}px`;
    m.el.style.opacity = String(pingOpacity(m, now));
    const dist = Math.hypot(m.o[0] - eye[0], m.o[1] - eye[1], m.o[2] - eye[2]);
    const nick = stripQ3Colors(m.nick).trim();
    m.label.textContent =
      `${nick ? `${nick} · ` : ""}${PING_LABEL[m.kind]} · ${Math.round(dist / 39.37)}m`;
  }
}

// ---- Recording persistence (localStorage, keyed by map) ----

const REC_STORE_PREFIX = "webrace.rec.v1:";

function bytesToB64(bytes: Uint8Array): string {
  let s = "";
  for (let i = 0; i < bytes.length; i++) s += String.fromCharCode(bytes[i]);
  return btoa(s);
}

function b64ToBytes(b64: string): Uint8Array {
  const s = atob(b64);
  const out = new Uint8Array(s.length);
  for (let i = 0; i < s.length; i++) out[i] = s.charCodeAt(i);
  return out;
}

/** Persist the current recording (commands + keyframes + ghosts) for this map. */
function saveRecording(map: string): void {
  if (sessionId === null || !map) return;
  const bytes = core.session_export_recording(sessionId, map) as unknown as Uint8Array;
  if (!bytes || bytes.length === 0) return;
  try {
    localStorage.setItem(REC_STORE_PREFIX + map, bytesToB64(bytes));
  } catch {
    /* quota exceeded — ignore */
  }
}

/** Restore a saved recording for this map into the live session. */
function loadRecording(map: string, id: number): void {
  try {
    const raw = localStorage.getItem(REC_STORE_PREFIX + map);
    if (!raw) return;
    core.session_import_recording(id, b64ToBytes(raw));
  } catch {
    /* corrupt entry — ignore */
  }
}

async function main() {
  installHopper(); // named for Grace Hopper: catch bugs before they vanish
  try {
    setStatus("loading WASM core…");
    const initOut = await init();
    memory = initOut.memory;
    core.install_panic_hook();
    // The JS timebase and the Rust core must agree; a stale WASM bundle would
    // otherwise predict at a different rate than the server.
    const coreRate = core.tick_rate();
    if (coreRate !== TICK_RATE) {
      setStatus(`core/client tick-rate mismatch (${coreRate} vs ${TICK_RATE} Hz) — rebuild the WASM core`);
      log(`FATAL: WASM core tick rate ${coreRate} Hz, client ${TICK_RATE} Hz`);
      return;
    }
    log(`cross-origin isolated: ${crossOriginIsolated} (SharedArrayBuffer=${typeof SharedArrayBuffer !== "undefined"})`);
    setStatus("ready — Play Race or Play Team");

    playRaceBtn.addEventListener("click", () => playOnline("race"));
    playTeamBtn.addEventListener("click", () => playOnline("team"));
    mapInput.addEventListener("keydown", (e) => {
      if (e.key === "Enter") playOnline("race");
    });
    collisionToggleBtn?.addEventListener("click", () => proposeCollision(!pvpCollision));
    updateCollisionUi();

    // Settings menu.
    menu = new Menu(
      settingsStore,
      {
        onFov: (v) => {
          fov = v;
          applyWeaponProfile(currentWeapon());
        },
        onPanini: (strength) => {
          if (projectionOverride) return;
          // The per-frame camera sync applies the new warp next frame.
          panini = strength;
        },
        onSensitivity: (s) => {
          sensitivity = s;
          applyWeaponProfile(currentWeapon());
        },
        onCrosshair: (store) => {
          crosshairStore = store;
          applyWeaponProfile(currentWeapon());
        },
        onWeaponSettings: (ws) => {
          weaponSettings = ws;
          applyWeaponProfile(currentWeapon());
        },
        onHud: (hud) => {
          hudSettings = hud;
        },
        onHudLayout: (store, preview) => {
          hudLayout = store;
          if (preview) hudRuntime?.setDoc(preview);
          else applyHudDoc();
        },
        onHudEditor: (open) => {
          hudEditorOpen = open;
          hudRuntime?.setEditing(open);
          if (open) document.exitPointerLock?.();
          else applyHudDoc();
        },
        onPaletteToggle: (open) => previewWireframe("palette", open),
        onHudTestValue: (source, value) => hudRuntime?.setValue(source, value),
        onBinds: (b) => {
          binds = b;
          actionByCode = codeToAction(b);
        },
        onHome: () => {
          // Back to the landing page: drop the map/server params and reload.
          location.href = location.pathname;
        },
        onPlayMap: (map) => {
          menu?.setOpen(false);
          mapInput.value = map;
          proposeMap(map);
        },
        onConsoleCommand: (line) => consoleExec(line),
        onNickname: (nickname) => {
          nicknameInput.value = nickname;
          announceNickname();
        },
        onRule: (key, value) => {
          net?.sendText(JSON.stringify({ t: "rule", k: key, v: value }));
          // A live rule edit is server state. Only an admin's edit changes the
          // authoritative copy (the server re-broadcasts `rulesState`); a
          // non-admin keeps predicting locally until the server answers.
          if (net?.connected && net.admin) {
            serverRules = { ...(serverRules ?? {}), [key]: value };
          }
          if (sessionId !== null) core.session_set_rule(sessionId, key, value);
        },
        onRules: (rules) => {
          net?.sendText(JSON.stringify({ t: "rules", r: rules }));
          // Only an admin's live edit changes the authoritative set; a local
          // profile/reset must not override the server prediction.
          if (net?.connected && net.admin) {
            serverRules = { ...(serverRules ?? {}), ...rules };
          }
          applyMovementRules(serverRules ?? rules);
        },
        onRulesLocal: () => applyClientMovementRules(),
        onRequestRules: () => {
          net?.requestRules();
        },
        getServerFamily: () => (serverMode ? (serverMode.structure === "race" ? "race" : "combat") : null),
        onVoteRules: (rules, label) => {
          if (!net?.connected) {
            showAnnounce("rules vote is a multiplayer option", 2000);
            return;
          }
          net.proposeRules(rules, label);
          showAnnounce(`vote: apply ruleset "${label}" — Y yes / N no`, 2500);
        },
        onGameModeLocal: (mode) => {
          // The menu's applyAll() also re-applies the local mode; never let it
          // replace the server's authoritative mode while connected.
          if (!serverMode) applyGameModeLocal(mode);
        },
        onVoteGameMode: (mode, rules, label) => {
          if (!net?.connected) {
            applyGameModeLocal(mode);
            showAnnounce(`mode "${label}" applied locally`, 2000);
            return;
          }
          net.proposeMode(dumpMode(mode), rules, label);
          showAnnounce(`vote: mode "${label}" — Y yes / N no`, 2500);
        },
        onRequestGameMode: () => net?.requestMode(),
        onSounds: (sounds) => audio.setAssignments(sounds),
        onVolume: (v) => {
          audio.setVolume(v);
        },
        onTrueLightning: (value) => {
          CG_TRUELIGHTNING = value;
        },
        onTrail: (config) => {
          trailSeconds = config.seconds;
          trailWidth = config.width;
          trailIntensity = config.intensity;
          trailSelfMode = config.selfMode;
          trailSelfColor = config.selfColor;
          trailSelfTeamColor = config.selfTeamColor;
          trailEnemyMode = config.enemyMode;
          trailEnemyColor = config.enemyColor;
          trailEnemyTeamColor = config.enemyTeamColor;
          applyPlayerTrail();
        },
        onFeedback: () => {
          audio.setHitVolume(settingsStore.value().hitFeedback.hitSoundVolume);
        },
        getDebug: () => debugState(),
        onDebug: (key, value) => applyDebug(key, value),
        onDebugReset: () => resetDebug(),
        onDebugReload: (server, map) => applyDebugReload(server, map),
        getVfxColor: (key) => getVfxColor(key),
        onVfxColor: (key, hex) => setVfxColor(key, hex),
        getProjectileColor: (w) => getProjectileColorHex(w),
        onProjectileColor: (w, hex) => setProjectileColor(w, hex),
        getProjectileAlpha: (w) => getProjectileAlpha(w),
        onProjectileAlpha: (w, a) => setProjectileAlpha(w, a),
        onTextureMode: (mode) => {
          textureMode = mode;
          renderer?.setTextureMode(mode);
        },
        onPlaySound: (file) => audio.playFile(file),
        onListSounds: () => audio.listFiles(),
        onMode: (mode) => {
          if (sessionId === null) return;
          const id = sessionId;
          if (mode === 3) {
            recallTransition(() => {
              core.session_set_mode(id, 3);
            });
          } else {
            core.session_set_mode(id, mode);
          }
          menu?.refreshRecall();
        },
        onRecallStep: (delta) => {
          if (sessionId === null) return;
          const id = sessionId;
          recallTransition(() => {
            if (core.session_mode(id) !== 3) core.session_set_mode(id, 3);
            core.session_recall_step(id, delta);
          });
          menu?.refreshRecall();
        },
        onRecallSelect: (index) => {
          if (sessionId === null) return;
          const id = sessionId;
          recallTransition(() => {
            if (core.session_mode(id) !== 3) core.session_set_mode(id, 3);
            core.session_recall_set_index(id, index);
          });
          menu?.refreshRecall();
        },
        onRestart: () => {
          if (sessionId !== null) core.session_reset(sessionId);
          menu?.refreshRecall();
        },
        getRecall: () => {
          if (sessionId === null) return null;
          const keyframes = core.session_recall_keyframes(sessionId) as unknown as Uint32Array;
          return {
            mode: core.session_mode(sessionId),
            count: core.session_recall_count(sessionId),
            index: core.session_recall_index(sessionId),
            tick: core.session_tick(sessionId),
            length: core.session_recall_length(sessionId),
            keyframes: Array.from(keyframes),
          };
        },
        onSaveSlot: (name) => {
          if (sessionId !== null) core.session_save_slot(sessionId, name);
        },
        onRecallSlot: (name) => {
          if (sessionId !== null) core.session_recall_slot(sessionId, name);
        },
        onDeleteSlot: (name) => {
          if (sessionId !== null) core.session_delete_slot(sessionId, name);
        },
        getSlots: () => {
          if (sessionId === null) return [];
          return core.session_slot_names(sessionId) as unknown as string[];
        },
        getGhostSource: () => ghostSource,
        onGhostSource: (source) => {
          ghostSource =
            source === "pr" ? "pr" : source === "wr" ? "wr" : source === "off" ? "off" : "last";
          applyGhostSource();
        },
        onTeam: (slot, team) => {
          net?.sendText(JSON.stringify({ t: "team", slot, team }));
        },
        getRoster: () => roster,
        getCurrentMap: () => currentMap,
        getRawStatus: () => rawInputStatus(),
        getAdminSettings: () => adminSettings,
        onAdminSettings: (settings) => {
          adminSettings = settings;
          adminDirty = true;
        },
        onSaveAdminSettings: async (settings, token) => {
          const r = await saveAdminSettings(settings, token);
          if (r.ok) {
            adminSettings = settings;
            adminDirty = false;
          }
          return r;
        },
        getRole: () => currentRole,
        onRole: (role) => {
          currentRole = role;
          savePreviewRole(role);
          menu?.refreshSettingsAccess();
        },
      },
      (open) => {
        // The open menu looks great in wireframe; preview it without touching
        // the user's wireframe setting (restored when it closes).
        previewWireframe("menu", open);
        // Re-apply/refresh the theme when the menu opens too; this recovers
        // cleanly if the startup fetch happened before the backend was ready.
        if (open) {
          applyTheme(loadCachedTheme());
          void refreshThemeFromServer();
          refreshAdminSettings();
        }
        // Release pointer lock when opening the menu so the mouse is usable.
        if (open && document.pointerLockElement === canvas) {
          document.exitPointerLock?.();
        }
        // Clear held keys when toggling.
        for (const k of Object.keys(keys) as (keyof typeof keys)[]) keys[k] = false;
        rocketDashHeld = false;
      },
    );

    // Non-menu settings changes (console, future import/reset paths) notify;
    // re-apply the whole UI/runtime from the store. Menu edits commit silently
    // because they have already applied their own change.
    settingsStore.subscribe(() => menu?.applyAll());

    // Pull the global admin defaults (ping timings) once the menu exists so the
    // admin panel can re-render with the server values.
    refreshAdminSettings();

    // Published settings defaults: apply the cached copy now, then refresh.
    applyRuntimeSettingDefaults();
    refreshGlobalSettingsDefaults();

    // Prefill nickname from stored identity.
    const existing = getIdentity();
    if (existing) nicknameInput.value = existing.nickname;

    // `?wire=1` starts in debug wireframe mode (before the map/renderer loads).
    // Tolerate a stray second `?` (e.g. `?fpscap=1000?profile=1`) as `&`.
    const params = new URLSearchParams(location.search.replace(/\?/g, "&"));
    const wire = params.get("wire");
    if (wire === "1" || wire === "true") {
      wireframe = true;
      urlPinnedRuntimeKeys.add("wireframe");
    }
    // `?profile=1` shows the in-app frame profiler; `?fpscap=N` caps the loop.
    const prof = params.get("profile");
    if (prof === "1" || prof === "true" || prof === "") {
      profileEnabled = true;
      urlPinnedRuntimeKeys.add("profiler");
    }
    // `?pvs=0` disables PVS culling (for A/B screenshot comparison).
    const pvs = params.get("pvs");
    if (pvs === "0" || pvs === "false") {
      pvsEnabled = false;
      urlPinnedRuntimeKeys.add("pvs");
    }
    // `?frustum=0` disables view-frustum culling (for A/B screenshot comparison).
    const frustum = params.get("frustum");
    if (frustum === "0" || frustum === "false") {
      frustumEnabled = false;
      urlPinnedRuntimeKeys.add("frustum");
    }
    // `?inkshell=0` forces cel-outline shells off (they need culling to render).
    const inkshell = params.get("inkshell");
    if (inkshell === "0" || inkshell === "false") showOutlineShells = false;
    // `?cull=0` disables world face culling (A/B screenshots / debugging).
    const cull = params.get("cull");
    if (cull === "0" || cull === "false") cullEnabled = false;
    const cullMode = params.get("cullmode");
    if (cullMode === "front" || cullMode === "back" || cullMode === "none") cullOverride = cullMode;
    const msaa = params.get("msaa");
    if (msaa === "0" || msaa === "false") msaaEnabled = false;
    const lightmap = params.get("lightmap");
    if (lightmap === "0" || lightmap === "false") lightmapEnabled = false;
    // `?face=1` runs the continuous debug face probe (logs each new face hit).
    const face = params.get("face");
    if (face === "1" || face === "true") faceProbeOn = true;
    // `?nomodels=1` skips model instances (debug: isolate overlay artifacts).
    if (params.get("nomodels") === "1") hideModels = true;
    if (params.get("noparticles") === "1") hideParticles = true;
    if (params.get("noviewmodel") === "1") hideViewModel = true;
    if (params.get("multidraw") === "0") multiDrawEnabled = false;
    const gpu = params.get("gpu");
    if (gpu === "low" || gpu === "default") lowPowerGpu = true;
    const cap = Number(params.get("fpscap"));
    if (Number.isFinite(cap) && cap > 0) {
      fpsCap = cap;
      urlPinnedRuntimeKeys.add("fpscap");
    }
    // The MessageChannel scheduler in `scheduleLoop` resets Chrome's timer
    // nesting, so a 333/360 fps software cap is reachable (the old ~250 fps
    // ceiling only applied to plain nested setTimeout).
    const FPS_CAP_MAX = 500;
    if (fpsCap > FPS_CAP_MAX) {
      log(`fps cap ${fpsCap} limited to ${FPS_CAP_MAX}`);
      fpsCap = FPS_CAP_MAX;
    }
    if (wireframe) log("wireframe ON");
    if (profileEnabled) log("Abrash ON (frame profiler)");
    if (fpsCap > 0) log(`fps cap ${fpsCap}`);

    // Phase 4 tuning knobs + net diagnostics:
    //   ?interp=N   remote-interpolation delay (ms, 20..300)
    //   ?smooth=N   reconciliation correction smoothing (ms, 0..1000)
    //   ?lag=N      simulated one-way latency (ms, both directions)
    //   ?jitter=N   simulated +/- jitter (ms)
    //   ?loss=P     simulated message loss (%)
    //   ?netstats=1 bandwidth readout (bytes/s in the fps line) + the netgraph
    const clampNum = (v: number, lo: number, hi: number) => Math.min(hi, Math.max(lo, v));
    const numParam = (name: string): number | null => {
      const raw = params.get(name);
      if (raw === null || raw === "") return null;
      const v = Number(raw);
      return Number.isFinite(v) ? v : null;
    };
    const interp = numParam("interp");
    if (interp !== null && interp > 0) {
      INTERP_MS = clampNum(interp, 20, 300);
      interpOverride = true;
      urlPinnedRuntimeKeys.add("interp");
    }
    const smooth = numParam("smooth");
    if (smooth !== null) {
      RECONCILE_MS = clampNum(smooth, 0, 1000);
      urlPinnedRuntimeKeys.add("smooth");
    }
    const lag = numParam("lag");
    if (lag !== null && lag > 0) {
      simLagMs = clampNum(lag, 0, 1000);
      urlPinnedRuntimeKeys.add("lag");
    }
    const jitter = numParam("jitter");
    if (jitter !== null && jitter > 0) {
      simJitterMs = clampNum(jitter, 0, 500);
      urlPinnedRuntimeKeys.add("jitter");
    }
    const loss = numParam("loss");
    if (loss !== null && loss > 0) {
      simLossPct = clampNum(loss, 0, 100);
      urlPinnedRuntimeKeys.add("loss");
    }
    const trueLight = numParam("cg_truelightning") ?? numParam("truelightning");
    if (trueLight !== null) CG_TRUELIGHTNING = clampNum(trueLight, 0, 1);
    // `?downstream=N` (alias `?snap=N`) sets the downstream snapshot rate in Hz
    // (see DOWNSTREAM_RATES); 125 = the full server rate (no decimation) and
    // is the public default; 62.5 is the low-bandwidth option.
    const downstream = numParam("downstream") ?? numParam("snap");
    if (downstream !== null && (DOWNSTREAM_RATES as readonly number[]).includes(downstream)) {
      downstreamHz = downstream;
      log(`downstream snapshots ${downstream} Hz`);
    }
    // `?budget=N` sets the soft main-thread work budget (ms) the presentation
    // scheduler respects; the profiler shows the overrun %.
    const budget = numParam("budget");
    if (budget !== null && budget > 0) frameBudgetMs = clampNum(budget, 1, 50);
    log(`frame budget ${frameBudgetMs}ms`);
    // `?particles=N` / `?smoke=N` cap the client-local VFX pools
    // (defaults 4096 glow / 2048 smoke).
    const pc = numParam("particles");
    if (pc !== null) maxParticles = Math.max(0, Math.min(65536, Math.round(pc)));
    const sm = numParam("smoke");
    if (sm !== null) maxSmoke = Math.max(0, Math.min(65536, Math.round(sm)));
    // Bloom: `?bloom=1` enables, `?bloom=0` disables; intensity/threshold overridable.
    const diagParam = params.get("diag");
    if (diagParam === "1" || diagParam === "true" || diagParam === "") diagOnLoad = true;
    const bloomParam = params.get("bloom");
    if (bloomParam === "0" || bloomParam === "false") {
      bloomEnabled = false;
      urlPinnedRuntimeKeys.add("bloom");
    } else if (bloomParam === "1" || bloomParam === "true" || bloomParam === "") {
      bloomEnabled = true;
      urlPinnedRuntimeKeys.add("bloom");
    }
    const projParam = params.get("proj");
    if (projParam === "panini") {
      panini = 1;
      projectionOverride = true;
    } else if (projParam === "rectilinear") {
      panini = 0;
      projectionOverride = true;
    } else if (projParam !== null) {
      // Allow `?proj=0.4` for quick strength comparisons.
      const n = Number(projParam);
      if (Number.isFinite(n)) {
        panini = Math.min(3, Math.max(0, n));
        projectionOverride = true;
      }
    }
    const bi = numParam("bloomintensity");
    if (bi !== null) {
      bloomIntensity = clampNum(bi, 0, 3);
      urlPinnedRuntimeKeys.add("bloomintensity");
    }
    const bt = numParam("bloomthreshold");
    if (bt !== null) {
      bloomThreshold = clampNum(bt, 0, 1);
      urlPinnedRuntimeKeys.add("bloomthreshold");
    }
    // Net stats on by default while developing; `?netstats=0` hides them and the
    // Video-tab toggles flip them live.
    if (params.has("netstats") || params.has("netgraph")) {
      urlPinnedRuntimeKeys.add("netgraph");
    }
    netStatsEnabled = params.has("netstats") ? params.get("netstats") !== "0" : true;
    netGraphOn = netStatsEnabled || params.has("netgraph");
    if (INTERP_MS !== 100 || RECONCILE_MS !== 150) {
      log(`net tune: interp ${INTERP_MS}ms smooth ${RECONCILE_MS}ms`);
    }

    // `?server=race|team|auto|ws://host:port` auto-connects; without it the
    // landing Play Race / Play Team buttons choose the instance.
    const serverParam = params.get("server");
    if (serverParam === "auto" || serverParam === "1") {
      serverOverride = autoServerUrl();
    } else if (serverParam === "race" || serverParam === "team") {
      serverOverride = serverFamilyUrl(serverParam);
    } else if (serverParam && /^wss?:\/\//.test(serverParam)) {
      serverOverride = serverParam;
    } else {
      serverOverride = null;
      if (serverParam) log(`ignoring invalid ?server=${serverParam}`);
    }
    if (serverOverride) connectToServer(serverOverride);

    // The server's map drives loading (via the welcome); this only seeds the
    // landing map field / vote proposal.
    const q = params.get("map");
    if (q) mapInput.value = q;

  } catch (e) {
    setStatus("failed to initialize: " + (e as Error).message);
    log((e as Error).stack || String(e));
  }
}

/** The deployed game-server WebSocket (`wss://<host>/webrace/ws`). */
function autoServerUrl(): string {
  return `${location.protocol === "https:" ? "wss" : "ws"}://${location.host}/webrace/ws`;
}

/**
 * A family instance: `/webrace/ws/race` or `/webrace/ws/team` on the deployed
 * site, ports 4175/4176 for the local dev servers.
 */
function serverFamilyUrl(kind: ServerKind): string {
  if (location.pathname.includes("/webrace")) {
    return `${location.protocol === "https:" ? "wss" : "ws"}://${location.host}/webrace/ws/${kind}`;
  }
  return `${location.protocol === "https:" ? "wss" : "ws"}://${location.hostname}:${kind === "race" ? 4175 : 4176}`;
}

/**
 * The server to join by default: the live `/webrace/ws` when served under
 * `/webrace`, otherwise the local dev game server on port 4175.
 */
function defaultServerUrl(): string {
  if (location.pathname.includes("/webrace")) return autoServerUrl();
  return `${location.protocol === "https:" ? "wss" : "ws"}://${location.hostname}:4175`;
}

/** Optional playtest tag from `?tag=...`; carried in hello + every sample. */
const PLAYTEST_TAG = (() => {
  try {
    return (new URLSearchParams(location.search).get("tag") || "").slice(0, 80);
  } catch {
    return "";
  }
})();

function clientInfo(): Record<string, unknown> {
  let gpu = "";
  try {
    const c = document.querySelector("canvas");
    const gl = (c?.getContext("webgl2") ?? c?.getContext("webgl")) as WebGL2RenderingContext | null;
    if (gl) {
      const ext = gl.getExtension("WEBGL_debug_renderer_info");
      gpu = String(ext ? gl.getParameter(ext.UNMASKED_RENDERER_WEBGL) : gl.getParameter(gl.RENDERER));
    }
  } catch {
    /* ignore */
  }
  const nav = navigator as Navigator & { deviceMemory?: number };
  return {
    ua: navigator.userAgent.slice(0, 240),
    platform: navigator.platform,
    cores: navigator.hardwareConcurrency,
    memory: nav.deviceMemory ?? null,
    screen: `${screen.width}x${screen.height}`,
    dpr: devicePixelRatio,
    gpu: gpu.slice(0, 160),
    language: navigator.language,
  };
}

/** Lightweight frame-time ring, independent of the debug profiler overlay. */
const playtestFrameMs: number[] = [];

// ---- Camera diagnostic: is every view change explained by mouse input? ----
// Yaw/pitch may only change through `add_mouse` (raw accumulator) or an
// explicit view reset (teleport/recall/reset). Comparing the rendered per-frame
// delta with the counts accumulated since the previous frame proves which one
// happened. `camMismatchMax` > 0 means something moved the view without the
// mouse; a large `camDYawMax`/`camDPitchMax` with a matching mouse total is a
// real flick (or a browser delta spike). Reported in the playtest telemetry.
let diagSens = 0;
let diagYawMult = 1;
let diagPitchMult = 1;
let frameMouseDx = 0;
let frameMouseDy = 0;
let diagMouseMax = 0;
let diagCamDyawMax = 0;
let diagCamDpitchMax = 0;
let diagCamMismatchMax = 0;
let diagCamDeyeMax = 0;
let diagFrameDtMax = 0;
let diagReconFrames = 0;
let diagMismatchFrames = 0;
let diagWorst: Record<string, number | boolean> | null = null;
let diagLastYaw = Number.NaN;
let diagLastPitch = 0;
let diagLastNow = 0;
let diagLastEyeX = 0;
let diagLastEyeY = 0;
let diagLastEyeZ = 0;
// Raw deltas of the biggest single mouse events in the window. A pointer-lock
// warp shows up here as a delta comparable to the window size (e.g. ±1280 on a
// 2560-wide canvas), which no real mouse flick produces in one event.
let diagTopDeltas: Array<{ m: number; dx: number; dy: number; b: number; gap: number }> = [];
let diagMouseEvents = 0;
let diagMouseGapMax = 0;
let diagMouseMaxNearButton = 0;
let diagLastMouseAt = 0;
let diagLastButtonAt = 0;

/** Wrap an angle difference into (-π, π]. */
function wrapPi(a: number): number {
  while (a > Math.PI) a -= Math.PI * 2;
  while (a <= -Math.PI) a += Math.PI * 2;
  return a;
}

function diagCameraFrame(now: number, eye: Float32Array, yaw: number, pitch: number): void {
  if (!Number.isFinite(diagLastYaw)) {
    diagLastYaw = yaw;
    diagLastPitch = pitch;
    diagLastNow = now;
    diagLastEyeX = eye[0];
    diagLastEyeY = eye[1];
    diagLastEyeZ = eye[2];
    frameMouseDx = 0;
    frameMouseDy = 0;
    return;
  }
  const DEG = Math.PI / 180;
  const dt = now - diagLastNow;
  const dyaw = wrapPi(yaw - diagLastYaw);
  const dpitch = pitch - diagLastPitch;
  const deye = Math.hypot(eye[0] - diagLastEyeX, eye[1] - diagLastEyeY, eye[2] - diagLastEyeZ);
  // `Angles::add_mouse`: yaw_frac -= dx * sens * 0.022 * yaw_mult (in degrees).
  const expYaw = -frameMouseDx * diagSens * 0.022 * diagYawMult * DEG;
  const expPitch = -frameMouseDy * diagSens * 0.022 * diagPitchMult * DEG;
  // The pitch clamp (looking straight up/down) legitimately stops the delta.
  const atPitchClamp =
    Math.abs(pitch) > 89.6 * DEG || Math.abs(diagLastPitch) > 89.6 * DEG;
  const yawMis = Math.abs(wrapPi(dyaw - expYaw)) / DEG;
  const pitchMis = atPitchClamp ? 0 : Math.abs(dpitch - expPitch) / DEG;
  const mis = diagSens > 0 ? Math.max(yawMis, pitchMis) : 0;
  const dyawDeg = Math.abs(dyaw) / DEG;
  const dpitchDeg = Math.abs(dpitch) / DEG;
  if (dyawDeg > diagCamDyawMax) diagCamDyawMax = dyawDeg;
  if (dpitchDeg > diagCamDpitchMax) diagCamDpitchMax = dpitchDeg;
  if (deye > diagCamDeyeMax) diagCamDeyeMax = deye;
  if (dt > diagFrameDtMax) diagFrameDtMax = dt;
  if (reconcileOffset) diagReconFrames++;
  if (mis > diagCamMismatchMax) {
    diagCamMismatchMax = mis;
    diagWorst = {
      misDeg: +mis.toFixed(2),
      dyawDeg: +dyawDeg.toFixed(2),
      dpitchDeg: +dpitchDeg.toFixed(2),
      expYawDeg: +(Math.abs(expYaw) / DEG).toFixed(2),
      expPitchDeg: +(Math.abs(expPitch) / DEG).toFixed(2),
      mouseDx: frameMouseDx,
      mouseDy: frameMouseDy,
      deye: +deye.toFixed(1),
      dtMs: +dt.toFixed(1),
      recon: !!reconcileOffset,
    };
  }
  if (mis > 2) diagMismatchFrames++;
  diagLastYaw = yaw;
  diagLastPitch = pitch;
  diagLastNow = now;
  diagLastEyeX = eye[0];
  diagLastEyeY = eye[1];
  diagLastEyeZ = eye[2];
  frameMouseDx = 0;
  frameMouseDy = 0;
}

/** Snapshot the diagnostic maxima and clear the window. */
function takeCamDiag(): Record<string, unknown> {
  const out = {
    camDYawMax: +diagCamDyawMax.toFixed(2),
    camDPitchMax: +diagCamDpitchMax.toFixed(2),
    camMismatchMax: +diagCamMismatchMax.toFixed(2),
    camMismatchFrames: diagMismatchFrames,
    camDeyeMax: +diagCamDeyeMax.toFixed(1),
    camMouseMax: diagMouseMax,
    camDtMax: +diagFrameDtMax.toFixed(1),
    camReconFrames: diagReconFrames,
    camWorst: diagWorst,
    camTopDeltas: diagTopDeltas,
    camMouseEvents: diagMouseEvents,
    camMouseGapMax: +diagMouseGapMax.toFixed(1),
    camMouseNearBtn: diagMouseMaxNearButton,
  };
  diagCamDyawMax = 0;
  diagCamDpitchMax = 0;
  diagCamMismatchMax = 0;
  diagMismatchFrames = 0;
  diagCamDeyeMax = 0;
  diagMouseMax = 0;
  diagFrameDtMax = 0;
  diagReconFrames = 0;
  diagWorst = null;
  diagTopDeltas = [];
  diagMouseEvents = 0;
  diagMouseGapMax = 0;
  diagMouseMaxNearButton = 0;
  return out;
}

function playtestFrameStats(): { p50: number; p99: number; fps: number; hitches: number } {
  const n = playtestFrameMs.length;
  if (n === 0) return { p50: 0, p99: 0, fps: 0, hitches: 0 };
  const s = [...playtestFrameMs].sort((a, b) => a - b);
  const at = (p: number) => s[Math.min(n - 1, Math.floor((p / 100) * n))];
  const p50 = at(50);
  const p99 = at(99);
  let over = 0;
  let total = 0;
  const limit = Math.max(8, p50 * 1.5);
  for (const v of playtestFrameMs) {
    total += v;
    if (v > limit) over++;
  }
  return {
    p50,
    p99,
    fps: p50 > 0 ? Math.round(1000 / p50) : 0,
    hitches: total > 0 ? Math.round((over / (total / 1000)) * 10) / 10 : 0,
  };
}

/** One 1 Hz playtest sample (netcode + frame health + current settings). */
function playtestSample(): Record<string, unknown> {
  const st = profiler?.stats();
  const snap = snapBuffer.latest();
  const frames = playtestFrameStats();
  return {
    t: Date.now(),
    tick: snap?.tick ?? null,
    ping: myPing(),
    jitterMs: Math.round(netJitterMs * 10) / 10,
    snapHz: Math.round(netSnapRate),
    gaps: snapGaps,
    rewindTicks: rewindTicks(),
    interpMs: INTERP_MS,
    smoothMs: RECONCILE_MS,
    corrLast: lastCorr,
    corrCount,
    corrMax,
    reconcileMs: Math.round(lastReconcileMs * 100) / 100,
    mouseLatency: mouseLatencyStats(),
    downHz: downstreamHz,
    lagMs: simLagMs,
    jitterImpairMs: simJitterMs,
    lossPct: simLossPct,
    fov,
    renderFov,
    panini,
    sens: sensitivity,
    p50Frame: frames.p50,
    p99Frame: frames.p99,
    fps: frames.fps,
    hitchesPerSec: frames.hitches,
    profOverBudgetPct: st?.overBudgetPct ?? null,
    gpuMs: st?.gpuLastMs ?? null,
    gpuDelay: st?.gpuAvgDelayFrames ?? null,
    contextLost: renderer?.contextLostCount() ?? 0,
    hidden: document.hidden,
    dpr: devicePixelRatio,
    // Camera vs input (see `diagCameraFrame`).
    ...takeCamDiag(),
  };
}

const playtestSamples: Record<string, unknown>[] = [];
window.setInterval(() => {
  if (sessionId === null || !net?.connected) return;
  playtestSamples.push(playtestSample());
  if (playtestSamples.length > 30) playtestSamples.shift();
}, 1000);
window.setInterval(() => {
  if (!playtestSamples.length || !net?.connected) return;
  net.sendText(JSON.stringify({ t: "tel", samples: playtestSamples }));
  playtestSamples.length = 0;
}, 5000);

/**
 * Tell the server our current nickname (players/scoreboard tab + nametags).
 * Called on join and again whenever the nickname changes in Settings.
 */
function announceNickname(): void {
  const identity = getIdentity();
  net?.sendText(
    JSON.stringify({
      t: "hello",
      nick: identity?.nickname ?? "",
      token: identity?.token ?? "",
      adminToken: loadAdminToken(),
      protocolVersion: core.protocol_version(),
      tag: PLAYTEST_TAG,
      client: clientInfo(),
    }),
  );
}

/** Connect to an authoritative game server (idempotent). */
function connectToServer(url: string): void {
  if (net) return;
  net = new NetTransport();
  // ?lag/?jitter/?loss (or the live debug toggles) are applied here so the
  // transport exists before the first command batch.
  rebuildNetSim();
  net.onWelcome = (slot, spawns, spawn) => {
    // A stale cached client must not decode the current snapshot schema.
    const serverProto = net?.protocolVersion ?? 0;
    if (serverProto !== core.protocol_version()) {
      addChatLine(
        "",
        `server protocol ${serverProto || "unknown"} ≠ client ${core.protocol_version()} — update/reload required`,
        true,
      );
      showAnnounce("client/server protocol mismatch — reload and rejoin", 5000);
      log(`mp: protocol mismatch (server ${serverProto || "?"}, client ${core.protocol_version()})`);
      net?.close();
      return;
    }
    // Tick-rate handshake: a 250 Hz server must never silently drive a 125 Hz
    // client (or vice versa) — the prediction would diverge from tick one.
    const serverTickRate = net?.tickRate ?? 0;
    if (serverTickRate !== TICK_RATE) {
      addChatLine(
        "",
        `server tick rate ${serverTickRate || "unknown"} Hz ≠ client ${TICK_RATE} Hz — update/reload required`,
        true,
      );
      showAnnounce("server/client tick-rate mismatch — reload and rejoin", 5000);
      log(`mp: tick-rate mismatch (server ${serverTickRate || "?"} Hz, client ${TICK_RATE} Hz)`);
      net?.close();
      return;
    }
    mySlot = slot;
    // Match the client's local cadence to the server's per-client stride.
    const se = net?.snapEvery ?? 0;
    if (se > 0) downstreamHz = TICK_RATE / se;
    // Tell the server our nickname (for the players/scoreboard tab).
    announceNickname();
    // The server owns the ruleset and mode. Adopt them rather than pushing our
    // local sliders (a joining client must never overwrite the match config).
    net?.requestRules();
    net?.requestMode();
    serverSpawn = spawn;
    // The server's map is authoritative. If a map is already loaded and
    // differs, this is a passed vote — do a **full page reload** so the
    // renderer's GPU buffers, the sim session, and the connection all reset
    // cleanly. The map is written into the URL so the reload converges.
    const serverMap = net?.map ?? null;
    if (serverMap && serverMap.toLowerCase() !== currentMap.toLowerCase()) {
      if (currentMap) {
        const sp = new URLSearchParams(location.search);
        sp.set("map", serverMap);
        log(`mp: map changed to "${serverMap}" — reloading`);
        location.replace(`${location.pathname}?${sp.toString()}`);
        return;
      }
      log(`mp: loading server map "${serverMap}"`);
      mapInput.value = serverMap;
      void loadMap(serverMap);
      return;
    }
    if (spawn) applyServerSpawn();
    log(`mp: joined slot ${slot} (${spawns} spawns) @ ${url}`);
  };
  net.onSnapshot = (snap) => processSnapshot(snap);
  net.onVote = (v) => {
    activeVote = v;
    const yes = v.yes?.length ?? 0;
    const no = v.no?.length ?? 0;
    const what =
      v.option === "collision"
        ? `collision ${v.value ? "ON" : "OFF"}`
        : v.option === "mode"
          ? `mode${v.label ? ` "${v.label}"` : ""}`
          : v.option === "rules"
            ? `apply ruleset${v.label ? ` "${v.label}"` : ""}`
            : `map ${v.map}`;
    const pct = v.percent ?? net?.votePercent ?? 75;
    voteHudEl.textContent = `vote: ${what} — Y yes / N no  (${yes} yes / ${no} no, ${pct}% passes)`;
    hudRuntime?.setShown("vote-hud", true);
    updateVoteButtons(v);
    showAnnounce(`vote: ${what}`, 1800);
  };
  net.onVoteResult = (r) => {
    activeVote = null;
    voteHudEl.textContent = "";
    hudRuntime?.setShown("vote-hud", false);
    hideVoteButtons();
    const what =
      r.option === "collision"
        ? `collision ${r.value ? "ON" : "OFF"}`
        : r.option === "mode"
          ? `mode${r.label ? ` "${r.label}"` : ""}`
          : r.option === "rules"
            ? `ruleset${r.label ? ` "${r.label}"` : ""}`
            : `map ${r.map}`;
    showAnnounce(
      r.passed ? `vote passed: ${what}` : `vote failed${r.reason ? ` (${r.reason})` : ""}`,
      2500,
    );
  };
  net.onOptions = (o) => {
    pvpCollision = o.collision;
    updateCollisionUi();
  };
  net.onRulesState = (r) => {
    serverRules = r.rules;
    applyMovementRules(serverRules);
    menu?.setServerRules(r.rules, r.rulesHash);
  };
  net.onModeState = (r) => {
    const mode = parseModeDump(r.mode);
    serverMode = mode;
    serverModeStructure = mode.structure;
    serverWarmupReady = mode.warmup_ready;
    syncVoidAuthority();
    applyGameModeLocal(mode);
    menu?.setServerMode(mode, r.label, r.modeHash);
    showAnnounce(`mode: ${r.label || "custom"}`, 1500);
  };
  net.onKills = (kills) => {
    pendingKills.push(...kills);
    if (pendingKills.length > 64) pendingKills.splice(0, pendingKills.length - 64);
  };
  net.onCaptures = (caps) => {
    pendingCaptures.push(...caps);
    if (pendingCaptures.length > 32) pendingCaptures.splice(0, pendingCaptures.length - 32);
  };
  net.onChat = (c) => {
    pendingChat.push({ nick: c.nick, text: c.text, sys: c.sys });
    if (pendingChat.length > 64) pendingChat.shift();
  };
  net.onAdminState = (admin) => {
    if (admin) addChatLine("", "admin token accepted — live ruleset tuning enabled", true);
  };
  net.onPingMark = (p) => {
    pendingPings.push(p);
    if (pendingPings.length > 32) pendingPings.shift();
  };
  net.onClose = () => {
    serverModeStructure = null;
    serverRules = null;
    serverMode = null;
    syncVoidAuthority();
    applyClientMovementRules();
    if (menu) applyGameModeLocal(menu.getMode());
    pendingBeamFx.length = 0;
    pendingCombatEv = 0;
    pendingKills = [];
    pendingCaptures = [];
    pendingChat = [];
    pendingPings = [];
    mySlot = -1;
    snapBuffer.clear();
    activeVote = null;
    lastMatch = null;
    lastPlayers = [];
    latestHeals = [];
    latestFlags = [];
    lastFlagKey = "";
    lastSpectateKey = "";
    lastScoreHtml = "";
    scoreboardVisible = false;
    killFeedEl.innerHTML = "";
    voteHudEl.textContent = "";
    hudRuntime?.setShown("vote-hud", false);
    hideVoteButtons();
    addChatLine("", "disconnected", true);
    log("mp: disconnected");
  };
  net.connect(url, downstreamStride());
  log(`mp: connecting to ${url}…`);
  // The landing overlay stays up until the server answers and the map loads.
  window.setTimeout(() => {
    if (sessionId === null && !net?.connected) {
      setStatus("server not responding — check the connection and try again");
    }
  }, 4000);
}

/** Landing "Play Race"/"Play Team": join that server instance. */
function playOnline(kind: ServerKind): void {
  if (net?.connected) return;
  requestGameFullscreen();
  const url = serverFamilyUrl(kind);
  // Reflect the choice in the URL (`?server=race|team`) so a refresh keeps you
  // online on the same instance.
  try {
    const sp = new URLSearchParams(location.search);
    sp.set("server", kind);
    history.replaceState(null, "", `${location.pathname}?${sp.toString()}`);
  } catch {
    /* ignore (e.g. file://) */
  }
  setStatus("joining the server…");
  connectToServer(url);
}

async function loadMap(explicitName?: string) {
  const name = (explicitName ?? mapInput.value).trim();
  if (!name || !memory) return;
  if (!net?.connected) {
    setStatus("connect to a server to load a map");
    return;
  }
  setStatus(`loading ${name}…`);
  try {
    // Ensure a nickname is registered (for leaderboard submission).
    const nick = nicknameInput.value.trim();
    if (nick) {
      try {
        await registerNickname(nick);
      } catch (e) {
        log(`nickname: ${(e as Error).message}`);
      }
    }
    currentMap = name.toLowerCase();
    // Shader/texture resolution is per-map and the client caches results, so
    // point both at this map and drop shader defs before resolving it.
    setShaderMap(name);
    setTextureMap(name);
    clearShaderCache();
    renderer?.releaseMapTextures(); // free the previous map's GPU textures
    diagTex = [];
    predictedBeams.length = 0;    predictedProjectiles = [];
    predProjT = 0;
    predNextFire = 0;

    const bytes = await fetchBspPlain(name);
    // The WASM map registry keeps every parsed BSP alive until it is released;
    // the client only ever renders one map, so drop the previous one here (the
    // renderer already freed its GPU textures above). Mirrors the server's map
    // change. Release before parsing so map handles stay dense and the freshly
    // parsed map is always handle 0, which `currentMapId` still points at while
    // the new scene is built.
    core.bsp_release_all();
    const mapId = core.bsp_parse(name, bytes);

    const vertCount = core.bsp_vertex_count(mapId);
    const idxCount = core.bsp_index_count(mapId);
    const triCount = core.bsp_triangle_count(mapId);
    const brushCount = core.bsp_brush_count(mapId);
    ensureRenderer();
    if (!renderer) return;
    applyWireframe();
    renderer.setTextureMode(textureMode);
    await buildMapScene(name, mapId, vertCount, idxCount);
    attachMapSkybox(mapId);
    startMapSession(mapId);
    finishMapLoad(name, mapId, triCount, brushCount);
  } catch (e) {
    setStatus(`load failed: ${(e as Error).message}`);
    log((e as Error).stack || String(e));
  }
}

/** Create the renderer + presentation systems on the first map load. */
function ensureRenderer(): void {
  if (!renderer) {
    renderer = new Renderer(canvas, lowPowerGpu, msaaEnabled);
    renderer.setCullingEnabled(cullEnabled);
    renderer.setMultiDrawEnabled(multiDrawEnabled);
    setupResize();
    particles = new ParticleSystem(renderer.gl, maxParticles, "additive", "facet");
    smoke = new ParticleSystem(renderer.gl, maxSmoke, "alpha", "facet");
    railRings = new ParticleSystem(renderer.gl, maxRailRings, "additive", "ring");
    renderer.setParticles(hideParticles ? [] : [smoke, particles, railRings]);
    playerTrails = new PlayerTrailSystem(renderer.gl);
    renderer.setPlayerTrails(playerTrails);
    applyPlayerTrail();
    // Particle collision against the world (BSP trace via the core). Reads the
    // live session id; presentation-only, so it never affects the sim.
    const tracer = (
      x0: number, y0: number, z0: number, x1: number, y1: number, z1: number,
    ): Float32Array | null => {
      if (!vfxCollide || sessionId === null) return null;
      const r = core.session_trace_ray(
        sessionId, x0, y0, z0, x1, y1, z1, 1.5,
      ) as unknown as Float32Array;
      return r.length >= 5 ? r : null;
    };
    particles.collide = tracer;
    smoke.collide = tracer;
    applyVfx();
    applyBloom();
    profiler = new Profiler(renderer.gl, document.getElementById("profiler"));
    profiler.setBudget(frameBudgetMs);
    profiler.setEnabled(profileEnabled);
  }
}

/** Upload geometry/collision materials, mover chunks and runtime models. */
async function buildMapScene(name: string, mapId: number, vertCount: number, idxCount: number): Promise<void> {
  if (!renderer || !memory) return;
  renderer.uploadMap(
    memory,
    core.bsp_vertices_ptr(mapId),
    vertCount,
    core.bsp_indices_ptr(mapId),
    idxCount,
  );

  // Upload moving brush entities into their own buffer.
  renderer.uploadMovers(
    memory,
    core.bsp_mover_vertices_ptr(mapId),
    core.bsp_mover_vertex_count(mapId),
    core.bsp_mover_indices_ptr(mapId),
    core.bsp_mover_index_count(mapId),
  );

  // Upload the lightmap atlas.
  renderer.uploadLightmap(
    memory,
    core.bsp_lightmap_ptr(mapId),
    core.bsp_lightmap_len(mapId),
    core.bsp_lightmap_w(mapId),
    core.bsp_lightmap_h(mapId),
  );

  // Determine draw chunks + load textures per unique shader.
  const chunkCount = core.bsp_chunk_count(mapId);
  const shaderIdx = new Uint32Array(chunkCount);
  const chunkFirst = new Uint32Array(chunkCount);
  const chunkCountArr = new Uint32Array(chunkCount);
  core.bsp_chunks(mapId, shaderIdx, chunkFirst, chunkCountArr);

  // Mover draw chunks (per mover + shader).
  const moverChunkCount = core.bsp_mover_chunk_count(mapId);
  const moverIdx = new Uint32Array(moverChunkCount);
  const moverShader = new Uint32Array(moverChunkCount);
  const moverFirst = new Uint32Array(moverChunkCount);
  const moverCountArr = new Uint32Array(moverChunkCount);
  core.bsp_mover_chunks(mapId, moverIdx, moverShader, moverFirst, moverCountArr);

  // Unique shaders used by world geometry and movers.
  const uniqueShaders = Array.from(
    new Set([...Array.from(shaderIdx), ...Array.from(moverShader)]),
  );
  const shaderNames = uniqueShaders.map((s) => core.bsp_shader_name(mapId, s));
  const defs = await loadShaders(shaderNames);

  // Client-discovered surfaceparms that affect *collision*. The BSP's shader
  // flags don't always carry `surfaceparm slick` (it's a runtime shader parm).
  // Apply before session_new so the collision world sees it.
  const SURF_SLICK = 0x2;
  for (let i = 0; i < uniqueShaders.length; i++) {
    const def = defs.get(shaderNames[i].toLowerCase());
    if (def?.surfaceParms.has("slick")) {
      core.bsp_add_shader_flags(mapId, uniqueShaders[i], SURF_SLICK);
    }
  }

  // Texture registration by name (cache so anim frames / overlays share).
  // `clampmap` needs a separate CLAMP_TO_EDGE texture, so the key includes it.
  const texIdByName = new Map<string, number>();
  const getTex = async (name: string, clamp = false): Promise<number | null> => {
    const key = name.toLowerCase() + (clamp ? "|clamp" : "");
    diagTex.push(key);
    const cached = texIdByName.get(key);
    if (cached !== undefined) return cached === -1 ? null : cached;
    const img = await loadTexture(name);
    const id = img ? renderer!.registerTexture(img, clamp) : -1;
    texIdByName.set(key, id);
    return id === -1 ? null : id;
  };

  type ChunkInfo = {
    tex: number[];
    animFreq: number;
    blend: BlendMode;
    lit: boolean;
    cull: CullMode;
    effects: StageEffects;
    fog?: FogParms;
    fogVolume: boolean;
    portal: boolean;
    polygonOffset: boolean;
    overlays: Array<{ tex: number; blend: BlendMode; effects: StageEffects }>;
  };
  const infoByShader = new Map<number, ChunkInfo | null>();
  fogByShader = new Map<number, FogParms>();
  // Build material *properties* now (from the shader defs) with EMPTY texture
  // arrays, so the world can be revealed immediately; textures stream in below.
  const texPlans = new Map<
    number,
    {
      frames: string[];
      clamp: boolean;
      overlays: Array<{ name: string; clamp: boolean; blend: BlendMode; effects: StageEffects }>;
    }
  >();
  for (const s of uniqueShaders) {
    const name = core.bsp_shader_name(mapId, s);
    const def = defs.get(name.toLowerCase()) ?? null;
    if (!def) {
      infoByShader.set(s, {
        tex: [],
        animFreq: 0,
        blend: "opaque",
        lit: lightmapEnabled,
        // Unknown shader: don't gamble on winding, keep the old two-sided look.
        cull: cullOverride ?? "none",
        effects: { tcMods: [] },
        fogVolume: false,
        portal: false,
        polygonOffset: false,
        overlays: [],
      });
      texPlans.set(s, { frames: [name], clamp: false, overlays: [] });
      continue;
    }
    // Cel outline shells only render correctly with face culling on (their
    // hull is back-facing); without it the black shell covers the map.
    if (def.outlineShell && (!showOutlineShells || !cullEnabled || cullOverride === "none")) {
      infoByShader.set(s, null);
      continue;
    }
    const plan = buildPlan(def);
    if (plan.noDraw) {
      infoByShader.set(s, null);
      continue;
    }
    if (plan.fog) fogByShader.set(s, plan.fog);
    const frames = plan.base.anim ?? (plan.base.tex ? [plan.base.tex] : [name]);
    infoByShader.set(s, {
      tex: [],
      animFreq: plan.base.animFreq,
      // Fog volumes are drawn as coloured fog, not as their shader stages.
      blend: plan.fogVolume ? "blend" : plan.base.blend,
      lit: plan.fogVolume ? false : lightmapEnabled && plan.lit,
      cull: cullOverride ?? plan.cull,
      effects: plan.fogVolume ? { tcMods: [] } : plan.base.effects,
      fog: plan.fog,
      fogVolume: plan.fogVolume,
      portal: plan.portal,
      polygonOffset: plan.polygonOffset,
      overlays: [],
    });
    texPlans.set(s, {
      frames: plan.fogVolume ? [] : frames,
      clamp: plan.base.clamp,
      overlays: plan.fogVolume
        ? []
        : plan.overlays
            .filter((o) => o.tex)
            .map((o) => ({ name: o.tex as string, clamp: o.clamp, blend: o.blend, effects: o.effects })),
    });
  }

  // Compute a representative plane for a portal chunk (from its first tri).
  const vertsAll = new Float32Array(memory.buffer, core.bsp_vertices_ptr(mapId), vertCount);
  // Per-map far plane: the world-space diagonal (plus margin) covers every
  // point of the map from any eye position. A fixed 200k far wasted most of
  // the depth buffer's precision (z-fighting at distance, e.g. skate2fix).
  {
    let mnx = Infinity, mny = Infinity, mnz = Infinity;
    let mxx = -Infinity, mxy = -Infinity, mxz = -Infinity;
    for (let i = 0; i + 2 < vertCount; i += 17) {
      const x = vertsAll[i], y = vertsAll[i + 1], z = vertsAll[i + 2];
      if (x < mnx) mnx = x; if (x > mxx) mxx = x;
      if (y < mny) mny = y; if (y > mxy) mxy = y;
      if (z < mnz) mnz = z; if (z > mxz) mxz = z;
    }
    const diag = Number.isFinite(mnx)
      ? Math.hypot(mxx - mnx, mxy - mny, mxz - mnz)
      : 8192;
    cameraFar = Math.min(131072, Math.max(4096, diag * 1.15 + 512));
    // Only needs to be a sane positive radius: the sky shader projects it to
    // the far plane (z = w), so the cube no longer has to fit inside `far`.
    renderer?.setSkyboxScale((cameraFar * 0.9) / 80000);
  }
  const idxAll = new Uint32Array(memory.buffer, core.bsp_indices_ptr(mapId), idxCount);
  const chunkPlane = (first: number) => {
    const i0 = idxAll[first];
    const i1 = idxAll[first + 1];
    const i2 = idxAll[first + 2];
    const a: [number, number, number] = [vertsAll[i0 * 17], vertsAll[i0 * 17 + 1], vertsAll[i0 * 17 + 2]];
    const b = [vertsAll[i1 * 17], vertsAll[i1 * 17 + 1], vertsAll[i1 * 17 + 2]];
    const c = [vertsAll[i2 * 17], vertsAll[i2 * 17 + 1], vertsAll[i2 * 17 + 2]];
    const e1 = [b[0] - a[0], b[1] - a[1], b[2] - a[2]];
    const e2 = [c[0] - a[0], c[1] - a[1], c[2] - a[2]];
    const n: [number, number, number] = [
      e1[1] * e2[2] - e1[2] * e2[1],
      e1[2] * e2[0] - e1[0] * e2[2],
      e1[0] * e2[1] - e1[1] * e2[0],
    ];
    const len = Math.hypot(n[0], n[1], n[2]) || 1;
    n[0] /= len; n[1] /= len; n[2] /= len;
    return { n, p: a };
  };

  // Portal surfaces are drawn separately (reflected view texture), so keep
  // them out of the normal world chunks.
  const chunks = [];
  const portalChunks: PortalChunk[] = [];
  // Face-probe bookkeeping: the world/portal split also tags each index range
  // so a ray hit can report exactly which kind of chunk and shader it hit.
  const pickWorldChunks: PickChunk[] = [];
  const pickPortalChunks: PickChunk[] = [];
  for (let i = 0; i < chunkCount; i++) {
    const info = infoByShader.get(shaderIdx[i]);
    if (!info) continue; // missing or nodraw
    const { portal, ...rest } = info;
    const sh = shaderIdx[i];
    if (portal && chunkCountArr[i] >= 3) {
      portalChunks.push({
        first: chunkFirst[i],
        count: chunkCountArr[i],
        plane: chunkPlane(chunkFirst[i]),
        ...rest,
      });
      pickPortalChunks.push({
        first: chunkFirst[i],
        count: chunkCountArr[i],
        shader: sh,
        kind: PICK_PORTAL,
        mover: -1,
      });
    } else {
      chunks.push({ first: chunkFirst[i], count: chunkCountArr[i], ...rest });
      pickWorldChunks.push({
        first: chunkFirst[i],
        count: chunkCountArr[i],
        shader: sh,
        kind: 0,
        mover: -1,
      });
    }
  }
  renderer.setChunks(chunks);
  renderer.setPortalChunks(portalChunks);
  if (portalChunks.length) {
    log(
      `portal surfaces: ${portalChunks.length} chunks ` +
        `[${pickPortalChunks.map((c) => `${core.bsp_shader_name(mapId, c.shader)}:${c.count}`).join(", ")}]`,
    );
  }

  // Per-shader materials for PVS-visible runs, indexed by BSP shader id.
  // Portal shaders are drawn separately, so their material is null here.
  const shaderTotal = core.bsp_shader_count(mapId);
  const materials: (DrawMaterial | null)[] = new Array(shaderTotal).fill(null);
  for (const [s, info] of infoByShader) {
    if (!info || info.portal) continue;
    const { portal: _portal, ...mat } = info;
    materials[s] = mat;
  }
  renderer.setMaterials(materials);

  // Phase 4: stream textures in *without* blocking the reveal — push ids into the
  // shared arrays (the live chunk/material objects) as each resolves.
  void (async () => {
    await Promise.all(
      [...texPlans].map(async ([s, p]) => {
        const info = infoByShader.get(s);
        if (!info) return;
        const ids = (await Promise.all(p.frames.map((f) => getTex(f, p.clamp)))).filter(
          (x): x is number => x !== null,
        );
        info.tex.push(...ids);
        for (const o of p.overlays) {
          const id = await getTex(o.name, o.clamp);
          if (id !== null) info.overlays.push({ tex: id, blend: o.blend, effects: o.effects });
        }
      }),
    );
  })();
  diagMap = name;
  diagUniqueShaders = uniqueShaders;
  diagShaderNames = shaderNames;
  diagShaderIdx = Array.from(shaderIdx);
  diagInfo = infoByShader;

  // PVS: one (shader, first, count) triple per world face is the max.
  currentMapId = mapId;
  const worldFaces = core.bsp_world_face_count(mapId);
  visRuns = worldFaces > 0 ? new Uint32Array(worldFaces * 3) : null;
  renderer.setVisibleRuns(null);

  // Runtime models belong to the previous map; drop them (and re-upload the
  // synthetic ghost box, which `clearModels` also deletes).
  modelEntities.length = 0;
  renderer.clearModels();
  resetAssetGpu(); // GPU model ids from the previous map are gone
  particles?.clear();
  smoke?.clear();
  railRings?.clear();
  playerTrails?.clear();
  for (const m of pingMarkers) m.el.remove();
  pingMarkers.length = 0;
  netBeams.length = 0;
  localBeams.length = 0;
  localSeenBeams.clear();
  prevProj.clear();
  ghostModelId = renderer.uploadModel(makeCapsuleModel(15, -24, 40), [null]);
  ghostOutlineModelId = renderer.uploadModel(makeCapsuleModel(17, -26, 42), [null]);
  ghostHaloModelId = renderer.uploadModel(makeCapsuleModel(19, -28, 44), [null]);
  playerGearModelId = renderer.uploadModel(makePlayerGearModel(), [null]);
  playerGlowModelId = renderer.uploadModel(makePlayerGlowModel(), [null]);
  itemModelId = renderer.uploadModel(makeCubeModel(10), [null]);
  {
    const n = core.bsp_item_count(mapId);
    if (n > 0) log(`${n} item pickups`);
  }
  // Teleporter destinations (for the destination markers).
  teleporterDests.length = 0;
  {
    const n = core.bsp_teleporter_count(mapId);
    const tp = new Float32Array(6);
    for (let i = 0; i < n; i++) {
      if (core.bsp_teleporter(mapId, i, tp)) {
        teleporterDests.push([tp[0], tp[1], tp[2]]);
      }
    }
  }
  projectileModelId = renderer.uploadModel(makeCubeModel(6), [null]);
  ballModelId = renderer.uploadModel(makeSphereModel(), [null]);
  teleportModelId = renderer.uploadModel(makeDiamondModel(1), [null]);
  beamModelId = renderer.uploadModel(makeCubeModel(0.5), [null]);
  debrisModelId = renderer.uploadModel(makeCubeModel(2), [null]);
  debrisChunks.length = 0;
  viewModelId = renderer.uploadModel(makeGunModel(), [null]);
  // Load the runtime 3D asset pack (models/wr/**) in the background — the
  // placeholders above are used until each model is uploaded. Cached across
  // map loads, so only the GPU upload is redone.
  void loadAssetPack(renderer).then((n) => {
    if (n > 0) log(`loaded ${n} model asset(s)`);
  });

  // Mover chunks (same shader info, plus the mover index for the matrix).
  const moverChunks: MoverChunk[] = [];
  const pickMoverChunks: PickChunk[] = [];
  for (let i = 0; i < moverChunkCount; i++) {
    const info = infoByShader.get(moverShader[i]);
    if (!info) continue;
    const { portal: _portal, ...rest } = info;
    moverChunks.push({
      model: moverIdx[i],
      first: moverFirst[i],
      count: moverCountArr[i],
      ...rest,
    });
    pickMoverChunks.push({
      first: moverFirst[i],
      count: moverCountArr[i],
      shader: moverShader[i],
      kind: PICK_MOVER,
      mover: moverIdx[i],
    });
  }
  renderer.setMoverChunks(moverChunks);

  // Snapshot the mesh pointers + chunk map for the debug face probe. Views
  // are rebuilt at probe time from `memory.buffer` (they can detach if the
  // WASM heap grows), so we only keep the raw pointers + lengths here.
  pickCtx = {
    vertsPtr: core.bsp_vertices_ptr(mapId),
    vertCount,
    idxPtr: core.bsp_indices_ptr(mapId),
    idxCount,
    moverVertsPtr: core.bsp_mover_vertices_ptr(mapId),
    moverVertCount: core.bsp_mover_vertex_count(mapId),
    moverIdxPtr: core.bsp_mover_indices_ptr(mapId),
    moverIdxCount: core.bsp_mover_index_count(mapId),
    chunks: pickWorldChunks.concat(pickPortalChunks),
    moverChunks: pickMoverChunks,
  };

}

/** Create the playable session bound to the freshly loaded map. */
function startMapSession(mapId: number): void {
  // Create a playable session at spawn point 0.
  if (sessionId !== null) core.session_drop(sessionId);
  sessionId = core.session_new(mapId, 0);
  prevTickEye = null;
  curTickEye = null;
  reconcileOffset = null; // a pending correction belongs to the old map
  diagLastYaw = Number.NaN; // camera diagnostic: re-seed on the new map
  lastLocalPose = null;
  syncVoidAuthority(); // the mode may have arrived before this session existed
  // A fresh session always starts in Run mode: reset the practice-detach latch
  // so a map switch can't fire a spurious resync.
  wasPracticeDetached = false;
  // Race-only prejump/start-line rules (arena modes don't have them).
  if (menu) applyGameModeLocal(serverMode ?? menu.getMode());
  // Apply any tuned movement rules to the prediction (must match the server).
  applyClientMovementRules();
  // Bind the map name to this session so a finish is always submitted under
  // the map it was actually run on (never the next one being loaded).
  activeMap = currentMap;
  lastSubmitted = null;
  // Restore a previously-saved recording for this map (recall + ghost).
  loadRecording(activeMap, sessionId);
  // Multiplayer: start recording prediction history for reconciliation.
  if (net) core.session_set_networked(sessionId, true);
  // Align the local prediction with the server's assigned spawn, if known.
  applyServerSpawn();
  // Ghost defaults to off (the user's toggle persists across map loads); load
  // the saved "last run" ghost and fetch PR/WR so switching is instant.
  ghostLast = core.session_ghost_export(sessionId) as unknown as Float32Array;
  ghostPR = null;
  ghostWR = null;
  raceBoard = null;
  raceBoardSerial++;
  resetCheckpointHud();
  void refreshRaceBoard(activeMap);
  wasRecording = false;
  void refreshGhosts(activeMap).then(() => {
    if (ghostSource !== "last") applyGhostSource();
    menu?.refreshRecall();
  });
  // Apply saved sensitivity to the new session.
  menu?.applyAll();
  // A new map starts on Maps (the menu remembers tabs within a map/session).
  menu?.resetToMaps();

}

/** Reveal the map and emit the load summary. */
function finishMapLoad(name: string, mapId: number, triCount: number, brushCount: number): void {
  if (sessionId === null) return;
  overlay.classList.add("hidden");
  setStatus("");
  log(
    `${name}: ${triCount} tris, ${brushCount} brushes, ` +
      `${core.bsp_jumppad_count(mapId)} jumppads, ` +
      `${core.bsp_velocity_pad_count(mapId)} velocity pads, ` +
      `${core.bsp_teleporter_count(mapId)} teleporters`,
  );
  log("CLICK the screen to lock mouse · WASD move · SPACE dash · right-click jump");

  const eye = core.session_eye(sessionId) as unknown as Float32Array;
  log(`spawn eye (render): ${[eye[0], eye[1], eye[2]].map((v) => v.toFixed(1)).join(", ")}`);
  if (diagOnLoad) {
    diagOnLoad = false;
    // Let one frame render so GL errors from the first draw are included.
    window.setTimeout(() => {
      printDiag();
      toggleConsole(true);
    }, 1500);
  }
  scheduleLoop();
}

/** Resolve and attach the map's skybox (clears any previous one). */
function attachMapSkybox(mapId: number): void {
  if (!renderer) return;
  renderer.clearSkybox();
  void loadSkybox(mapId)
    .then((faces) => {
      log(`skybox: ${faces ? "drawn" : "none (flat sky)"}`);
      if (faces) renderer!.setSkybox(faces);
    })
    .catch((e) => log(`skybox: ${(e as Error).message}`));
}

/** Load the skybox faces for the map's sky shader (`skyparms`), if any. */
async function loadSkybox(
  mapId: number,
): Promise<Array<{ width: number; height: number; data: Uint8ClampedArray | Uint8Array } | null> | null> {
  const SURF_SKY = 0x4;
  const count = core.bsp_shader_count(mapId);
  if (count === 0) return null;
  const flags = new Int32Array(count);
  core.bsp_shader_flags(mapId, flags);
  let skyIdx = -1;
  for (let i = 0; i < count; i++) {
    if (flags[i] & SURF_SKY) {
      skyIdx = i;
      break;
    }
  }
  let def = skyIdx >= 0 ? await loadShader(core.bsp_shader_name(mapId, skyIdx)) : null;
  if (!def?.skyparmsBase) {
    // Some maps (e.g. sst13's 13black_hd) declare `skyParms` without the
    // `surfaceparm sky` flag, so the BSP has no SURF_SKY shader at all. Scan
    // sky-named shaders for the skybox plan as a fallback.
    for (let i = 0; i < count; i++) {
      const n = core.bsp_shader_name(mapId, i);
      if (!/sky/i.test(n)) continue;
      const d = await loadShader(n);
      if (d?.skyparmsBase) {
        def = d;
        break;
      }
    }
  }
  if (!def?.skyparmsBase) return null;

  const suf = ["rt", "bk", "lf", "ft", "up", "dn"];
  const faces = await Promise.all(suf.map((s) => loadTexture(`${def.skyparmsBase}_${s}`)));
  // If none of the faces loaded, don't draw an untextured (grey) skybox — fall
  // back to the flat clear colour instead.
  return faces.some((f) => f) ? faces : null;
}

function setupResize() {
  let lastW = 0;
  let lastH = 0;
  const update = () => {
    if (!renderer) return;
    // `clientWidth` is 0 while the canvas is hidden/unsized; fall back to the
    // viewport so the drawing buffer never stays 0x0 (Firefox can keep the
    // default framebuffer incomplete -> black screen with only the HUD).
    const w = canvas.clientWidth || window.innerWidth;
    const h = canvas.clientHeight || window.innerHeight;
    if (w <= 0 || h <= 0) return; // mid-transition; avoid 0-size buffers
    canvasCssW = w;
    canvasCssH = h;
    if (w === lastW && h === lastH && canvas.width === w && canvas.height === h) return;
    lastW = w;
    lastH = h;
    canvas.width = w;
    canvas.height = h;
    renderer.resize(w, h);
  };
  // The canvas can gain a size without a window resize (landing overlay hiding,
  // CSS layout): observe it so the buffer always matches the element.
  if (typeof ResizeObserver !== "undefined") {
    new ResizeObserver(() => schedule()).observe(canvas);
  }
  // Coalesce resize/fullscreen bursts to one per frame — a fullscreen transition
  // fires many resize events, and each size change recreates portal FBOs.
  let scheduled = false;
  const schedule = () => {
    if (scheduled) return;
    scheduled = true;
    requestAnimationFrame(() => {
      scheduled = false;
      update();
    });
  };
  window.addEventListener("resize", schedule);
  document.addEventListener("fullscreenchange", schedule);
  update();
}

// ---- Pointer lock + input ----

let pointerLockPending = false;
let lockAfterFullscreen = false;
/** Whether the last pointer lock actually granted unadjusted (raw) movement. */
let rawInputGranted = false;
let rawInputUnsupported = false;
let rawInputWarned = false;

/** Human-readable state for the Weapons → Mouse "Raw input" row. */
function rawInputStatus(): string {
  if (!rawMouseInput) return "off (OS pointer curve)";
  if (rawInputGranted) return "raw (unadjusted)";
  if (rawInputUnsupported) return "OS curve — raw unsupported here";
  return "raw requested (click to lock)";
}
let lastPointerUnlockAt = 0;
let pointerUnlockHint: number | undefined;

function lockPointer() {
  const el = canvas as HTMLCanvasElement & {
    requestPointerLock: (opts?: {
      unadjustedMovement?: boolean;
    }) => void | Promise<void>;
  };
  if (typeof el.requestPointerLock !== "function") {
    lockEl.textContent = "browser does not support pointer lock";
    return;
  }
  // The canvas + document click handlers used to both fire, and the promise
  // catch retried immediately after an Esc. Chrome refuses a re-lock for a
  // short cooldown after exit, so that retry produced an unhandled
  // SecurityError ("Pointer lock cannot be acquired immediately after the user
  // has exited the lock") and Hopper caught it. Dedupe + wait out the cooldown.
  if (pointerLockPending) return;
  const now = performance.now();
  if (now - lastPointerUnlockAt < 1400) {
    lockEl.textContent = "🔓 wait a moment, then click to lock";
    lockEl.style.color = "var(--ui-danger)";
    if (pointerUnlockHint !== undefined) clearTimeout(pointerUnlockHint);
    pointerUnlockHint = window.setTimeout(() => {
      pointerUnlockHint = undefined;
      if (document.pointerLockElement !== canvas) {
        lockEl.textContent = "🔓 click to lock mouse";
        lockEl.style.color = "var(--ui-danger)";
      }
    }, 1400 - (now - lastPointerUnlockAt) + 50);
    return;
  }
  pointerLockPending = true;
  const release = () => {
    pointerLockPending = false;
  };
  const request = (raw: boolean): Promise<void> | void => {
    // Raw (unadjusted) movement skips the OS acceleration curve, which is what
    // makes aim snappy/consistent. Fall back to a plain lock if rejected.
    return raw ? el.requestPointerLock({ unadjustedMovement: true }) : el.requestPointerLock();
  };
  try {
    const p = request(rawMouseInput);
    if (p && typeof p.catch === "function") {
      p.then(
        () => {
          rawInputGranted = rawMouseInput;
          if (rawMouseInput) rawInputUnsupported = false;
        },
        (err: unknown) => {
          release();
          rawInputGranted = false;
          // Linux Chromium rejects unadjustedMovement; say so once instead of
          // silently falling back to the OS pointer curve.
          if (
            rawMouseInput &&
            !rawInputWarned &&
            (err as { name?: string })?.name === "NotSupportedError"
          ) {
            rawInputWarned = true;
            rawInputUnsupported = true;
            showAnnounce(
              "raw mouse not supported by this browser/OS — using the OS pointer curve",
              5000,
            );
          }
          // Swallow the rejection (don't let it reach Hopper). One plain-lock
          // fallback for browsers that reject the raw option; if that also
          // fails, the next click retries.
          if (rawMouseInput) {
            try {
              const p2 = request(false);
              if (p2 && typeof p2.catch === "function") {
                p2.then(
                  () => {
                    rawInputGranted = false;
                  },
                  release,
                );
              }
            } catch {
              release();
            }
          }
        },
      );
    } else {
      rawInputGranted = rawMouseInput;
    }
  } catch {
    release();
  }
}

// Clicks anywhere re-lock (HUD elements are pointer-events:none), but NOT while
// the menu is open — menu clicks must not re-lock the mouse. This single
// document handler covers the canvas too; don't add a second canvas handler.
document.addEventListener("click", () => {
  // The menu, palette, HUD editor, console and chat own the mouse while open.
  if (menu?.isOpen() || menu?.isPaletteOpen() || hudEditorOpen || consoleOpen || chatOpen) return;
  if (overlay.classList.contains("hidden") && document.pointerLockElement !== canvas) {
    if (document.fullscreenElement) {
      lockEl.textContent = "requesting pointer lock…";
      lockPointer();
    } else {
      // Try pointer lock first: entering fullscreen in the same gesture used to
      // cancel it, so the lock is issued before the transition. If the
      // transition still drops it, fullscreenchange retries once.
      lockEl.textContent = "entering fullscreen…";
      lockAfterFullscreen = true;
      lockPointer();
      requestGameFullscreen();
    }
  }
});

// ---- Keyboard Lock (gameplay) -------------------------------------------
// Ctrl+W / Ctrl+T / Ctrl+1..8 are browser shortcuts; while pointer-locked
// they otherwise close or switch tabs even though the combo is a game bind.
// The Keyboard Lock API hands those keys to the page (where supported). It
// needs a user gesture, which the click that acquired pointer lock provides.
interface KeyboardLockApi {
  lock?: (keyCodes?: string[]) => Promise<void>;
  unlock?: () => void;
}

let keyboardLocked = false;
let keyboardLockWanted = false;

// Keyboard Lock only suppresses browser shortcuts in fullscreen, so entering
// gameplay also enters fullscreen. The click that acquires pointer lock is a
// valid user gesture for this. A rejection (policy/iframe) is non-fatal.
function requestGameFullscreen(): void {
  if (document.fullscreenElement) return;
  const el = document.documentElement as HTMLElement & {
    requestFullscreen?: () => Promise<void>;
  };
  if (typeof el.requestFullscreen !== "function") return;
  try {
    const p = el.requestFullscreen();
    if (p && typeof p.catch === "function") p.catch(() => {});
  } catch {
    /* ignore */
  }
}

// ---- History back/forward trap (mouse thumb buttons) --------------------
// Cancelling the mouse event is not enough in every browser/driver: the
// button can still traverse history. While the game owns the pointer, keep a
// same-URL sentinel on top of the history stack and re-push it whenever the
// browser pops it. Opening the menu (pointer unlock) disarms the trap, so the
// normal back button works there.
let historyTrapArmed = false;

function armHistoryTrap(): void {
  if (historyTrapArmed) return;
  historyTrapArmed = true;
  try {
    // Don't stack a new sentinel on every lock cycle while the previous one is
    // still current (disarming leaves it in place).
    const state = history.state as { webraceTrap?: boolean } | null;
    if (!state?.webraceTrap) {
      history.pushState({ webraceTrap: true }, "", location.href);
    }
  } catch {
    historyTrapArmed = false;
  }
}

function disarmHistoryTrap(): void {
  historyTrapArmed = false;
}

window.addEventListener("popstate", () => {
  if (!historyTrapArmed) return;
  try {
    history.pushState({ webraceTrap: true }, "", location.href);
  } catch {
    /* ignore */
  }
});

// Keys the browser treats as shortcuts. Keyboard Lock only takes effect in
// fullscreen, and locking a key captures its modifier combos (Ctrl+W etc.).
// Include the game's current binds plus the common browser shortcuts so a
// movement/crouch combo cannot fall through to the browser.
const BROWSER_SHORTCUT_KEYS = [
  "KeyW", "KeyT", "KeyN", "KeyR", "KeyF", "KeyS", "KeyO", "KeyL", "KeyD",
  "KeyJ", "KeyH", "KeyP", "Tab", "PageUp", "PageDown", "Escape",
  "Digit1", "Digit2", "Digit3", "Digit4", "Digit5", "Digit6", "Digit7",
  "Digit8", "Digit9",
];

function keyboardLockKeys(): string[] {
  const keys = new Set(BROWSER_SHORTCUT_KEYS);
  for (const code of Object.keys(binds)) {
    if (code.startsWith("Mouse") || code.startsWith("Wheel")) continue;
    keys.add(code);
  }
  return [...keys];
}

function setGameKeyboardLock(on: boolean): void {
  const kb = (navigator as Navigator & { keyboard?: KeyboardLockApi }).keyboard;
  if (!kb?.lock) return;
  keyboardLockWanted = on;
  if (!on) {
    if (keyboardLocked) {
      try {
        kb.unlock?.();
      } catch {
        /* ignore */
      }
      keyboardLocked = false;
    }
    return;
  }
  if (keyboardLocked) return;
  kb.lock(keyboardLockKeys())
    .then(() => {
      keyboardLocked = true;
      // Pointer unlock/menu won the race with the lock request.
      if (!keyboardLockWanted) {
        try {
          kb.unlock?.();
        } catch {
          /* ignore */
        }
        keyboardLocked = false;
      }
    })
    .catch(() => {
      keyboardLocked = false;
    });
}

document.addEventListener("fullscreenchange", () => {
  // Entering fullscreen from the click-to-play gesture can cancel the
  // simultaneous pointer-lock request; retry it within the same activation.
  if (document.fullscreenElement && lockAfterFullscreen) {
    lockAfterFullscreen = false;
    lockPointer();
  }
  // Chrome only grants keyboard lock in fullscreen; (re)request when entering
  // while already playing.
  if (document.pointerLockElement === canvas) setGameKeyboardLock(true);
});

document.addEventListener("pointerlockerror", () => {
  pointerLockPending = false;
  // During the fullscreen transition Chrome refuses a same-gesture pointer
  // lock. Invite the next click instead of showing a scary error.
  lockEl.textContent = document.fullscreenElement
    ? "🔓 click to lock mouse"
    : "🔓 pointer lock refused by browser";
  lockEl.style.color = "var(--ui-danger)";
});

document.addEventListener("pointerlockchange", () => {
  if (document.pointerLockElement === canvas) {
    pointerLockPending = false;
    if (pointerUnlockHint !== undefined) {
      clearTimeout(pointerUnlockHint);
      pointerUnlockHint = undefined;
    }
    lockEl.textContent = "🔒 mouse locked — right-click jump · SPACE dash · ESC menu";
    lockEl.style.color = "var(--ui-success)";
    setGameKeyboardLock(true);
    armHistoryTrap();
  } else {
    pointerLockPending = false;
    lastPointerUnlockAt = performance.now();
    lockEl.textContent = "🔓 click to lock mouse";
    lockEl.style.color = "var(--ui-danger)";
    setGameKeyboardLock(false);
    disarmHistoryTrap();
    for (const k of Object.keys(keys) as (keyof typeof keys)[]) keys[k] = false;
  }
});

// Mouse look (raw movement). Sensitivity is applied inside the sim (Quake
// m_yaw/m_pitch model), so we just forward the raw deltas.
//
// `pointerrawupdate` (Chromium) is delivered as soon as the browser produces
// the movement, whereas `mousemove`/`pointermove` may be delayed to the next
// rAF. For an FPS this removes roughly a frame of aim latency ("floaty" mouse).
// Both coalesce; sum the coalesced sub-events instead of double-counting the
// parent event's total.
//
// Firefox: do NOT use `pointerrawupdate` by default. Since it shipped (Firefox
// 148) it can report a pointer-lock warp as relative movement — observed as a
// single ~1296-count delta (≈ the cursor's absolute screen X on a 2560-wide
// window) right at a mousedown, which snapped the view ~48° for one frame on
// every rocket jump. The plain `mousemove` path is clean (verified in-game);
// `?rawupdate=1` still forces the raw path for A/B testing.
let mouseRawUpdateMode = false;
function addMouseLook(dx: number, dy: number, buttons = 0): void {
  if (sessionId === null || !dx && !dy) return;
  core.session_add_mouse(sessionId, dx, dy);
  // Camera diagnostic: deltas since the previous rendered frame (see
  // `diagCameraFrame`). Only applied events count, same as the sim.
  const t = performance.now();
  const gap = diagLastMouseAt ? t - diagLastMouseAt : 0;
  diagLastMouseAt = t;
  frameMouseDx += dx;
  frameMouseDy += dy;
  const mag = Math.abs(dx) + Math.abs(dy);
  if (mag > diagMouseMax) diagMouseMax = mag;
  diagMouseEvents++;
  if (gap > diagMouseGapMax) diagMouseGapMax = gap;
  if (diagLastButtonAt && t - diagLastButtonAt < 60 && mag > diagMouseMaxNearButton) {
    diagMouseMaxNearButton = mag;
  }
  // Keep the 3 biggest single-event deltas (with the button mask at the time).
  if (mag >= 200) {
    const last = diagTopDeltas[diagTopDeltas.length - 1];
    if (diagTopDeltas.length < 3 || mag > (last?.m ?? 0)) {
      diagTopDeltas.push({
        m: mag,
        dx: Math.round(dx),
        dy: Math.round(dy),
        b: buttons,
        gap: +gap.toFixed(1),
      });
      diagTopDeltas.sort((a, b) => b.m - a.m);
      if (diagTopDeltas.length > 3) diagTopDeltas.length = 3;
    }
  }
  if (mousePendingT === 0) mousePendingT = t;
}
function onMouseLook(e: MouseEvent | PointerEvent): void {
  if (sessionId === null || document.pointerLockElement !== canvas) return;
  const pe = e as PointerEvent;
  if (mouseRawUpdateMode && pe.pointerType && pe.pointerType !== "mouse") return;
  let dx = 0;
  let dy = 0;
  if (mouseRawUpdateMode && typeof pe.getCoalescedEvents === "function") {
    const evs = pe.getCoalescedEvents();
    if (evs.length > 0) {
      for (const ce of evs) {
        dx += ce.movementX ?? 0;
        dy += ce.movementY ?? 0;
      }
      if (dx === 0 && dy === 0) {
        dx = e.movementX ?? 0;
        dy = e.movementY ?? 0;
      }
    } else {
      dx = e.movementX ?? 0;
      dy = e.movementY ?? 0;
    }
  } else {
    dx = e.movementX ?? 0;
    dy = e.movementY ?? 0;
  }
  addMouseLook(dx, dy, (e as MouseEvent).buttons ?? 0);
}
// Firefox's `pointerrawupdate` can report a pointer-lock warp as relative
// movement (a single ~1296-count delta ≈ the cursor's absolute screen X on a
// 2560-wide window at a mousedown, snapping the view ~48° for a frame). Use the
// clean `mousemove` path there unless `?rawupdate=1` asks for the A/B.
const FIREFOX = /Firefox\/|FxiOS\//i.test(navigator.userAgent);
const rawUpdateParam = new URLSearchParams(location.search).get("rawupdate");
mouseRawUpdateMode =
  "onpointerrawupdate" in window &&
  (rawUpdateParam === "1" || (rawUpdateParam !== "0" && !FIREFOX));
document.addEventListener(
  mouseRawUpdateMode ? "pointerrawupdate" : "mousemove",
  onMouseLook as EventListener,
  { passive: true },
);

// Mouse wheel dispatches the bound WheelUp/WheelDown action (by default:
// scroll up = ping, scroll down = Machinegun). If a direction is unbound it
// still scrubs the recall timeline, so recall remains usable with a mouse.
document.addEventListener(
  "wheel",
  (e) => {
    if (sessionId === null) return;
    if (document.pointerLockElement !== canvas) return;
    e.preventDefault();
    const up = e.deltaY < 0;
    const action = actionByCode.get(up ? "WheelUp" : "WheelDown");
    // Wheel always scrubs the recall timeline, even if a direction is unbound.
    dispatchAction(action ?? (up ? "recall_next" : "recall_prev"));
  },
  { passive: false },
);

/** Race/practice/recall bindings are only meaningful in race modes. */
const RACE_ONLY_ACTIONS = new Set<Action>([
  "practice",
  "noclip",
  "recall",
  "recall_prev",
  "recall_next",
  "ghost",
  "restart",
  "position_save",
]);

/** Weapon-select actions -> core weapon ids (see `core/src/weapons.rs`). */
const WEAPON_ACTIONS: Record<string, number> = {
  weapon_melee: 1,
  weapon_machinegun: 2,
  weapon_shotgun: 3,
  weapon_grenade: 4,
  weapon_rocket: 5,
  weapon_plasma: 6,
  weapon_lightning: 7,
  weapon_railgun: 8,
  weapon_experimental: 9,
  weapon_ball: 10,
};

/** Dispatch a discrete (non-held) action. */
function dispatchAction(action: Action): void {
  if (!isRaceMode() && RACE_ONLY_ACTIONS.has(action)) {
    showAnnounce("race modes only", 1200);
    return;
  }
  if (action in WEAPON_ACTIONS) {
    const w = WEAPON_ACTIONS[action];
    if (net?.connected || sessionId !== null) {
      selectWeapon(w, true);
      showAnnounce(WEAPON_NAMES[w] ?? `weapon ${w}`, 900);
    }
    return;
  }
  switch (action) {
    case "restart":
      if (sessionId !== null) {
        core.session_reset(sessionId);
        // Multiplayer: the server owns the respawn — but NOT while we are
        // detached in a local practice/recall session, where its spawn would
        // overwrite a recalled keyframe start.
        if (net?.connected && !practiceDetached()) {
          net.sendText(JSON.stringify({ t: "restart" }));
        }
      }
      break;
    case "search":
      menu?.togglePalette();
      break;
    case "menu":
      if (menu) {
        menu.setOpen(!menu.isOpen());
        if (menu.isOpen() && currentMap) {
          // Populate the leaderboard map field with the current map.
          menu.prefillLeaderboardMap(currentMap);
          void menu.refreshLeaderboard(currentMap);
        }
      }
      break;
    case "position_save":
      if (sessionId !== null) {
        if (net?.connected && !practiceDetached()) {
          // Multiplayer: the server owns the spawn; keep the local one in sync
          // so a restart is instant, and tell the server.
          core.session_save_spawn_now(sessionId);
          net.sendText(JSON.stringify({ t: "savepos" }));
          showAnnounce("position saved", 1200);
        } else {
          const ok = core.session_position_save(sessionId);
          showAnnounce(
            ok ? "position saved" : "can't save — before start line, on ground",
            ok ? 1200 : 1800,
          );
        }
      }
      break;
    case "weapon_next":
      cycleWeapon(1);
      break;
    case "weapon_prev":
      cycleWeapon(-1);
      break;
    case "vote_yes":
      if (net?.connected && activeVote) {
        net.sendText(JSON.stringify({ t: "vote", yes: true }));
      }
      break;
    case "vote_no":
      if (net?.connected && activeVote) {
        net.sendText(JSON.stringify({ t: "vote", yes: false }));
      }
      break;
    case "practice": {
      if (sessionId === null) break;
      const inPractice = core.session_mode(sessionId) !== 0;
      core.session_set_mode(sessionId, inPractice ? 0 : 1);
      menu?.refreshRecall();
      showAnnounce(inPractice ? "run mode" : "practice mode", 1200);
      break;
    }
    case "noclip": {
      if (sessionId === null) break;
      const inNoclip = core.session_mode(sessionId) === 2;
      core.session_set_mode(sessionId, inNoclip ? 1 : 2);
      menu?.refreshRecall();
      showAnnounce(inNoclip ? "practice mode" : "noclip", 1200);
      break;
    }
    case "recall": {
      if (sessionId === null) break;
      const id = sessionId;
      const inRecall = core.session_mode(id) === 3;
      recallTransition(() => core.session_set_mode(id, inRecall ? 1 : 3));
      menu?.refreshRecall();
      showAnnounce(inRecall ? "practice mode" : "recall — W/S or scroll to step", 1600);
      break;
    }
    case "recall_prev":
    case "recall_next": {
      if (sessionId === null) break;
      const id = sessionId;
      // Scrub ONLY while already in recall. Scrolling (or these keys) must never
      // *enter* recall — that freezes the sim and strands you unable to move.
      if (core.session_mode(id) !== 3) break;
      recallTransition(() => core.session_recall_step(id, action === "recall_prev" ? -1 : 1));
      menu?.refreshRecall();
      break;
    }
    case "ghost":
      if (sessionId !== null) cycleGhostSource();
      break;
    case "ping":
      sendPing("look");
      break;
    case "ping_enemy":
      sendPing("enemy");
      break;
    case "ping_heal":
      sendPing("heal");
      break;
    case "wireframe":
      toggleWireframe();
      break;
    case "face_probe":
      probeFace(false);
      break;
    case "stuck_probe":
      probeStuck("manual");
      break;
    case "throw_ball":
      if (sessionId !== null && !net?.connected) {
        core.session_throw_ball(sessionId);
      } else {
        showAnnounce("throw ball: not available online — use the Ball Launcher", 1600);
      }
      break;
    case "weeball":
      // Wipeout ability (Phase 2). The bind ships now so players can set it
      // before the mode goes live.
      net?.sendText(JSON.stringify({ t: "weeball" }));
      break;
    case "mark":
      if (net?.connected) net.sendText(JSON.stringify({ t: "mark" }));
      break;
    case "screenshot":
      requestScreenshot();
      break;
    case "scoreboard":
      scoreboardVisible = !scoreboardVisible;
      updateMatchHud();
      break;
    case "ready": {
      const ready = !(lastSelf?.ready ?? true);
      net?.sendText(JSON.stringify({ t: "ready", yes: ready }));
      showAnnounce(ready ? "ready" : "not ready", 1200);
      break;
    }
    case "spectate_next":
      cycleSpectate(1);
      updateSpectateHud();
      break;
    case "spectate_prev":
      cycleSpectate(-1);
      updateSpectateHud();
      break;
    case "spectate_mode":
      spectateMode = spectateMode === 0 ? 1 : 0;
      showAnnounce(spectateMode ? "spectate: chase" : "spectate: first person", 1200);
      updateSpectateHud();
      break;
    case "chat":
      openChat();
      break;
    case "console":
      toggleConsole();
      break;
    case "profiler":
      profileEnabled = !profileEnabled;
      profiler?.setEnabled(profileEnabled);
      log(`Abrash ${profileEnabled ? "ON" : "OFF"}`);
      break;
    default:
      break;
  }
}

/** Toggle debug wireframe rendering (also available via `?wire=1`). */
function toggleWireframe() {
  wireframe = !wireframe;
  applyWireframe();
  log(`wireframe ${wireframe ? "ON" : "OFF"}`);
}

/** Structured result of one face probe (also returned by `__webrace.faceProbe`). */
interface FaceProbeInfo {
  hit: boolean;
  kind?: "world" | "portal" | "mover";
  mover?: number;
  shader?: string;
  shaderId?: number;
  renderFace?: number;
  bspFace?: number;
  patch?: boolean;
  back?: boolean;
  t?: number;
  point?: [number, number, number];
  normal?: [number, number, number];
  /** The three vertices of the hit triangle (world space). */
  tri?: [[number, number, number], [number, number, number], [number, number, number]];
}

/**
 * Cast a ray from the camera through the loaded mesh and report the nearest
 * triangle it hits — which chunk family (static world / portal surface / moving
 * brush entity), the shader, and (world faces) the BSP face index plus whether
 * it's a bezier patch. Built to pin "a surface drawn in the wrong place" bugs in
 * one shot instead of guessing from screenshots.
 */
function computeFaceProbe(eyeIn?: [number, number, number], fwdIn?: [number, number, number]): FaceProbeInfo {
  const eye = eyeIn ?? lastRay?.eye;
  const fwd = fwdIn ?? lastRay?.fwd;
  if (!pickCtx || !eye || !fwd || currentMapId < 0 || !memory) return { hit: false };
  const ctx = pickCtx;
  // Rebuild the typed-array views from the *live* heap each probe: WASM heap
  // growth detaches older views, and the game allocates during play.
  const verts = new Float32Array(memory.buffer, ctx.vertsPtr, ctx.vertCount);
  const indices = new Uint32Array(memory.buffer, ctx.idxPtr, ctx.idxCount);
  const moverVerts = ctx.moverVertCount
    ? new Float32Array(memory.buffer, ctx.moverVertsPtr, ctx.moverVertCount)
    : null;
  const moverIndices = ctx.moverIdxCount
    ? new Uint32Array(memory.buffer, ctx.moverIdxPtr, ctx.moverIdxCount)
    : null;
  const hit = pickRay(
    {
      verts,
      indices,
      chunks: ctx.chunks,
      moverVerts,
      moverIndices,
      moverChunks: ctx.moverChunks,
      moverMatrices: frameMovers,
    },
    eye[0], eye[1], eye[2],
    fwd[0], fwd[1], fwd[2],
  );
  if (!hit) return { hit: false };

  const kind: FaceProbeInfo["kind"] =
    hit.kind === PICK_MOVER ? "mover" : hit.kind === PICK_PORTAL ? "portal" : "world";
  const info: FaceProbeInfo = {
    hit: true,
    kind,
    mover: hit.mover >= 0 ? hit.mover : undefined,
    shader: core.bsp_shader_name(currentMapId, hit.shader) || "(no shader)",
    shaderId: hit.shader,
    back: hit.back,
    t: hit.t,
    point: hit.point,
    normal: hit.normal,
  };
  if (hit.kind !== PICK_MOVER) {
    // The hit triangle's vertices (world space) — reveals degenerate/flat or
    // oversized patch triangles at a glance.
    const v = [0, 1, 2].map((k) => {
      const o = indices[hit.triangle * 3 + k] * 17;
      return [verts[o], verts[o + 1], verts[o + 2]] as [number, number, number];
    }) as [[number, number, number], [number, number, number], [number, number, number]];
    info.tri = v;
    const face = new Float32Array(4);
    if (core.bsp_face_at_triangle(currentMapId, hit.triangle, face) === 0) {
      info.renderFace = face[0] | 0;
      info.bspFace = face[1] | 0;
      info.patch = face[3] > 0.5;
    }
  }
  return info;
}

/**
 * Log one face probe. `continuous` (the `?face=1` mode) suppresses a line unless
 * the hit identity changed since the previous probe, so walking around logs each
 * new face exactly once.
 */
function probeFace(continuous: boolean): void {
  if (!pickCtx || !lastRay || currentMapId < 0 || !memory) {
    log("face probe: no map loaded");
    return;
  }
  const r = computeFaceProbe();
  if (!r.hit) {
    if (!continuous) {
      const eye = lastRay.eye;
      log(`face probe: no hit from (${eye.map((v) => v.toFixed(1)).join(", ")})`);
    }
    return;
  }
  const kindName = r.kind === "mover" ? `mover#${r.mover}` : r.kind === "portal" ? "PORTAL" : "world";
  const faceNote =
    r.renderFace !== undefined
      ? ` renderFace#${r.renderFace} bspFace#${r.bspFace}${r.patch ? " PATCH" : ""}`
      : "";
  const line =
    `face probe: ${kindName} shader="${r.shader}" [id ${r.shaderId}]${faceNote}` +
    ` back=${r.back} t=${r.t!.toFixed(1)}` +
    ` at (${r.point!.map((v) => v.toFixed(1)).join(", ")})` +
    ` n=(${r.normal!.map((v) => v.toFixed(2)).join(", ")})`;
  const key = `${kindName}|${r.shaderId}|${faceNote}`;
  if (continuous && key === lastProbeKey) return;
  lastProbeKey = key;
  reportProbe(line);
}

/**
 * World ray through canvas pixel `(sx, sy)` (top-left origin) using the current
 * camera basis + FOV, then probe the mesh. Lets a screenshot coordinate be
 * mapped straight to the face that's drawn there.
 */
function computeFaceProbeAtPixel(sx: number, sy: number): FaceProbeInfo {
  if (!lastRay) return { hit: false };
  const w = canvas.width, h = canvas.height;
  const hfov = (fov * Math.PI) / 180;
  const vfov = 2 * Math.atan(Math.tan(hfov / 2) / (w / h));
  const ndcX = (2 * sx) / w - 1;
  const ndcY = 1 - (2 * sy) / h;
  const th = Math.tan(hfov / 2);
  const tv = Math.tan(vfov / 2);
  const { eye, fwd, right, up } = lastRay;
  let dx = fwd[0] + right[0] * ndcX * th + up[0] * ndcY * tv;
  let dy = fwd[1] + right[1] * ndcX * th + up[1] * ndcY * tv;
  let dz = fwd[2] + right[2] * ndcX * th + up[2] * ndcY * tv;
  const l = Math.hypot(dx, dy, dz) || 1;
  return computeFaceProbe([eye[0], eye[1], eye[2]], [dx / l, dy / l, dz / l]);
}

/** Gather the stuck-probe data for the player's current box. */
function computeStuck() {
  if (currentMapId < 0 || sessionId === null) return null;
  const o = core.session_origin(sessionId) as unknown as Float32Array;
  const origin = new Float32Array([o[0], o[1], o[2]]);
  const buf = new Float32Array(64);
  const box = playerBox();
  const n = core.bsp_solid_brushes_overlapping(
    currentMapId, origin, box.mins, box.maxs, buf,
  );
  const tris = core.bsp_tris_overlapping(
    currentMapId, origin, box.mins, box.maxs,
  );
  const parts: string[] = [];
  for (let i = 0; i < Math.min(n, buf.length); i++) {
    const b = buf[i] | 0;
    const nm = core.bsp_shader_name(currentMapId, core.bsp_brush_shader(currentMapId, b)) || "(no shader)";
    parts.push(`#${b}[${nm}]`);
  }
  return { o, n, tris, parts, onGround: core.session_on_ground(sessionId), speed: core.session_speed(sessionId) };
}

/**
 * Debug stuck-probe: report what solid geometry the player's box is embedded in
 * (collision brushes by index + shader, and how many patch triangles it
 * penetrates). The collision analog of the face probe, for "I'm stuck in the
 * wall / a round surface" reports.
 */
function probeStuck(tag: string): void {
  const s = computeStuck();
  if (!s) {
    reportProbe("stuck probe: no session");
    return;
  }
  reportProbe(
    `stuck probe (${tag}): at (${s.o[0].toFixed(1)}, ${s.o[1].toFixed(1)}, ${s.o[2].toFixed(1)})` +
      ` onGround=${s.onGround} speed=${Math.round(s.speed)}` +
      ` brushes-in-box=${s.n}${s.parts.length ? " " + s.parts.join(" ") : ""} patchTris=${s.tris}`,
  );
}

/**
 * Auto-detect being *embedded* in geometry (the player is holding a
 * **directional** movement key, not moving, AND the box overlaps a solid brush
 * or penetrates a patch triangle) and log one stuck probe. Merely pushing
 * against a wall does not count, and jumping/dashing in place are not attempts
 * to move — they must not trigger the full-map brush/triangle scan.
 */
function checkStuck(now: number): void {
  const moving = keys.forward || keys.back || keys.moveleft || keys.moveright;
  const reset = () => {
    stuckSinceMs = 0;
    stuckLogged = false;
  };
  if (sessionId === null || !moving || (menu?.isOpen() ?? false)) {
    reset();
    return;
  }
  // Cheap gate first: only a near-stationary player can be wedged. This keeps
  // the (expensive) full brush + patch-triangle scan off the hot path while
  // actually moving.
  if (core.session_speed(sessionId) >= 12) {
    reset();
    return;
  }
  if (stuckSinceMs === 0) stuckSinceMs = now;
  if (stuckLogged || now - stuckSinceMs < 600) return;
  // Slow for >600ms: confirm it's a real embed before logging (and pay the scan
  // at most ~once per 600ms while blocked).
  const s = computeStuck();
  if (s && (s.n > 0 || s.tris > 0)) {
    probeStuck("auto");
    stuckLogged = true;
  } else {
    stuckSinceMs = 0;
  }
}

/** Live video/network/advanced state (mirrors the URL options). */
function debugState(): Record<string, number | boolean> {
  return {
    netgraph: netGraphOn,
    profiler: profileEnabled,
    wireframe,
    pvs: pvsEnabled,
    frustum: frustumEnabled,
    rawmouse: rawMouseInput,
    rawgranted: rawInputGranted,
    rawunsupported: rawInputUnsupported,
    interp: INTERP_MS,
    smooth: RECONCILE_MS,
    lag: simLagMs,
    jitter: simJitterMs,
    loss: simLossPct,
    fpscap: fpsCap,
    truelightning: CG_TRUELIGHTNING,
    downstream: downstreamHz,
    vfxamount: vfxAmount,
    smokedensity: vfxSmokeDensity,
    smokeopacity: vfxSmokeOpacity,
    vfxsize: vfxSize,
    vfxtrails: vfxTrails,
    vfxcollide: vfxCollide,
    bloom: bloomEnabled,
    bloomintensity: bloomIntensity,
    bloomthreshold: bloomThreshold,
  };
}

/** Rebuild (or clear) the impairment sim after lag/jitter/loss changed. */
function rebuildNetSim(): void {
  const lagged = simLagMs || simJitterMs || simLossPct;
  net?.setImpairment(
    lagged
      ? { delayMs: simLagMs, jitterMs: simJitterMs, lossPct: simLossPct }
      : null,
  );
  if (lagged) log(`net sim: lag ${simLagMs}ms jitter ${simJitterMs}ms loss ${simLossPct}%`);
}

/** Apply one live video/network/advanced option. */
function applyDebug(key: string, value: number | boolean): void {
  const num = (v: number, lo: number, hi: number) => Math.min(hi, Math.max(lo, v));
  switch (key) {
    case "netgraph":
      netStatsEnabled = Boolean(value);
      netGraphOn = Boolean(value);
      break;
    case "profiler":
      profileEnabled = Boolean(value);
      profiler?.setEnabled(profileEnabled);
      break;
    case "wireframe":
      wireframe = Boolean(value);
      applyWireframe();
      break;
    case "pvs":
      pvsEnabled = Boolean(value);
      break;
    case "frustum":
      frustumEnabled = Boolean(value);
      break;
    case "rawmouse":
      rawMouseInput = Boolean(value);
      // Applies at the next pointer lock; re-lock now if we're already locked so
      // it can be A/B'd live.
      if (document.pointerLockElement === canvas) {
        document.exitPointerLock();
        window.setTimeout(lockPointer, 60);
      }
      break;
    case "interp":
      INTERP_MS = num(Number(value), 20, 300);
      interpOverride = true;
      break;
    case "smooth":
      RECONCILE_MS = num(Number(value), 0, 1000);
      break;
    case "lag":
      simLagMs = num(Number(value), 0, 1000);
      rebuildNetSim();
      break;
    case "jitter":
      simJitterMs = num(Number(value), 0, 500);
      rebuildNetSim();
      break;
    case "loss":
      simLossPct = num(Number(value), 0, 100);
      rebuildNetSim();
      break;
    case "fpscap":
      fpsCap = num(Number(value), 0, 500);
      break;
    case "truelightning":
      CG_TRUELIGHTNING = num(Number(value), 0, 1);
      break;
    case "downstream": {
      const hz = Number(value);
      downstreamHz = (DOWNSTREAM_RATES as readonly number[]).includes(hz) ? hz : 125;
      log(`downstream snapshots ${downstreamHz} Hz`);
      // Ask the server to change its per-connection stride live (Option 2).
      net?.sendText(JSON.stringify({ t: "rate", n: downstreamStride() }));
      break;
    }
    case "vfxamount":
      vfxAmount = num(Number(value), 0, 3);
      applyVfx();
      break;
    case "smokedensity":
      vfxSmokeDensity = num(Number(value), 0, 3);
      applyVfx();
      break;
    case "smokeopacity":
      vfxSmokeOpacity = num(Number(value), 0, 1);
      applyVfx();
      break;
    case "vfxsize":
      vfxSize = num(Number(value), 0.2, 3);
      applyVfx();
      break;
    case "vfxtrails":
      vfxTrails = Boolean(value);
      break;
    case "vfxcollide":
      vfxCollide = Boolean(value);
      break;
    case "bloom":
      bloomEnabled = Boolean(value);
      applyBloom();
      break;
    case "bloomintensity":
      bloomIntensity = num(Number(value), 0, 3);
      applyBloom();
      break;
    case "bloomthreshold":
      bloomThreshold = num(Number(value), 0, 1);
      applyBloom();
      break;
  }
}

/** Restore every live video/network/advanced option to its default. */
function resetDebug(): void {
  // Advanced toggles.
  netStatsEnabled = false;
  netGraphOn = false;
  profileEnabled = false;
  profiler?.setEnabled(false);
  wireframe = false;
  applyWireframe();
  pvsEnabled = true;
  frustumEnabled = true;
  rawMouseInput = true;
  // Network tuning + impairment simulator.
  INTERP_MS = 100;
  interpOverride = false;
  RECONCILE_MS = 150;
  simLagMs = 0;
  simJitterMs = 0;
  simLossPct = 0;
  rebuildNetSim();
  downstreamHz = 125;
  net?.sendText(JSON.stringify({ t: "rate", n: downstreamStride() }));
  // Video performance + effects.
  fpsCap = 0;
  CG_TRUELIGHTNING = 1;
  vfxAmount = 1;
  vfxSmokeDensity = 0.5;
  vfxSmokeOpacity = 0.1;
  vfxSize = 1;
  vfxTrails = true;
  vfxCollide = true;
  bloomEnabled = true;
  bloomIntensity = 0.9;
  bloomThreshold = 0.7;
  applyVfx();
  applyBloom();
  menu?.refreshDebug();
  log("live options reset to defaults");
}

/** Reconnect/reload with the given `server`/`map` URL options. */
function applyDebugReload(server: string, map: string): void {
  const p = new URLSearchParams(location.search);
  if (server) p.set("server", server);
  else p.delete("server");
  if (map) p.set("map", map);
  else p.delete("map");
  const qs = p.toString();
  location.search = qs ? `?${qs}` : "";
}

// True when the event target is a text input/textarea/contenteditable (so we
// don't hijack typing keys like Space).
function isTypingTarget(e: Event): boolean {
  const t = e.target as HTMLElement | null;
  if (!t) return false;
  const tag = t.tagName;
  return tag === "INPUT" || tag === "TEXTAREA" || t.isContentEditable;
}

/** True when a modal UI (landing, menu, console, chat) owns input. */
function uiCapturesGameInput(): boolean {
  return (
    !overlay.classList.contains("hidden") ||
    (menu?.isOpen() ?? false) ||
    (menu?.isPaletteOpen() ?? false) ||
    hudEditorOpen ||
    consoleOpen ||
    chatOpen
  );
}

/** True while gameplay (not a menu/overlay) owns the mouse. */
function gameOwnsMouseButtons(): boolean {
  return (
    document.pointerLockElement === canvas ||
    (sessionId !== null && !uiCapturesGameInput())
  );
}

// Browsers reserve mouse buttons 3/4 for history back/forward. Cancel them
// while playing so thumb buttons can be binds; `mouseup` is the event Chrome
// actually honours (mousedown/auxclick cover the other engines). Do not stop
// propagation: the game's own mousedown/mouseup handlers still run.
for (const type of ["mousedown", "mouseup", "auxclick"] as const) {
  window.addEventListener(
    type,
    (e) => {
      if ((e.button === 3 || e.button === 4) && gameOwnsMouseButtons()) e.preventDefault();
    },
    true,
  );
}

window.addEventListener("keydown", (e) => {
  if (isTypingTarget(e)) return;
  const action = actionByCode.get(e.code);
  if (!action) return;
  // The menu itself owns Escape/its bound "menu" key so it can close; every
  // other game input is suppressed while a modal UI is open.
  if (uiCapturesGameInput() && !(menu?.isOpen() && action === "menu")) return;
  if (e.repeat) return;
  // Keep the browser from acting on bound keys (F3/F4, Space scroll, …) while
  // playing (pointer-locked); Space is always suppressed.
  if (
    e.code === "Space" ||
    action === "screenshot" ||
    action === "search" ||
    document.pointerLockElement === canvas
  ) {
    e.preventDefault();
  }
  if (action === "zoom") {
    pressZoom();
    return;
  }
  if (action === "attack2" && mouse4EngagesZoom()) {
    mouse4ZoomEngaged = true;
    pressZoom();
    return;
  }
  // Rocket secondary is the rocket-dash script; don't emit weak fire.
  if (action === "attack2" && currentWeapon() === ROCKET_WEAPON) {
    rocketDashHeld = true;
    return;
  }
  // Recall: the sim is frozen, so forward/back step the recorded timeline
  // instead of moving (W = next keyframe, S = previous). Outside recall the
  // same binds are plain movement — one bind, two jobs depending on the mode.
  if (
    (action === "forward" || action === "back") &&
    sessionId !== null &&
    core.session_mode(sessionId) === 3
  ) {
    dispatchAction(action === "forward" ? "recall_next" : "recall_prev");
    return;
  }
  if (action in keys) {
    const wasDown = keys[action];
    keys[action] = true;
    if (!wasDown && (action === "attack" || action === "attack2")) noteShotPress();
  } else {
    dispatchAction(action);
  }
});
window.addEventListener("keyup", (e) => {
  if (isTypingTarget(e)) return;
  if (uiCapturesGameInput()) return;
  const action = actionByCode.get(e.code);
  if (action === "zoom") {
    releaseZoom();
    return;
  }
  if (action === "attack2" && mouse4ZoomEngaged) {
    mouse4ZoomEngaged = false;
    releaseZoom();
    return;
  }
  if (action === "attack2" && rocketDashHeld) {
    rocketDashHeld = false;
    return;
  }
  if (action && action in keys) {
    keys[action] = false;
    if ((action === "attack" || action === "attack2") && !keys.attack && !keys.attack2) {
      pendingShotStamp = null;
    }
  }
});

// Mouse buttons.
document.addEventListener("mousedown", (e) => {
  if (uiCapturesGameInput()) return;
  diagLastButtonAt = performance.now();
  const code = mouseButtonToCode(e.button);
  const action = actionByCode.get(code);
  if (!action) return;
  if (action === "zoom") {
    pressZoom();
    return;
  }
  if (action === "attack2" && mouse4EngagesZoom()) {
    mouse4ZoomEngaged = true;
    pressZoom();
    return;
  }
  if (action === "attack2" && currentWeapon() === ROCKET_WEAPON) {
    rocketDashHeld = true;
    return;
  }
  if (action in keys) {
    const wasDown = keys[action];
    keys[action] = true;
    if (!wasDown && (action === "attack" || action === "attack2")) noteShotPress();
  } else {
    dispatchAction(action);
  }
});
document.addEventListener("mouseup", (e) => {
  if (uiCapturesGameInput()) return;
  diagLastButtonAt = performance.now();
  const code = mouseButtonToCode(e.button);
  const action = actionByCode.get(code);
  if (action === "zoom") {
    releaseZoom();
    return;
  }
  if (action === "attack2" && mouse4ZoomEngaged) {
    mouse4ZoomEngaged = false;
    releaseZoom();
    return;
  }
  if (action === "attack2" && rocketDashHeld) {
    rocketDashHeld = false;
    return;
  }
  if (action && action in keys) {
    keys[action] = false;
    if ((action === "attack" || action === "attack2") && !keys.attack && !keys.attack2) {
      pendingShotStamp = null;
    }
  }
});
document.addEventListener("contextmenu", (e) => e.preventDefault());

// Push held keys into the sim each frame before stepping.
function pushKeys() {
  if (sessionId === null) return;
  const dash = rocketDashActive();
  core.session_set_keys(
    sessionId,
    keys.forward, keys.back || dash, keys.moveleft, keys.moveright,
    keys.jump, keys.crouch || dash, keys.special || dash,
    keys.attack || dash, keys.attack2 && !dash,
  );
}

// ---- Main loop ----

let frame = 0;
let lastFpsTime = performance.now();
let lastTick = performance.now();
// Presentation-only sub-tick camera smoothing. The predicted eye is a 125 Hz
// step; rendering it directly at 240/360 fps makes the world judder. Keep the
// last two tick eyes and project forward at render time. Extrapolation (the
// default) renders the *present* predicted position instead of lagging one
// tick behind; `?camsmooth=interp` uses the older interpolate-only path and
// `?camsmooth=0` disables smoothing for A/B tests. The sim/hit-reg path never
// reads these.
const camSmoothParam = new URLSearchParams(location.search).get("camsmooth");
const camSmoothMode =
  camSmoothParam === "0" || camSmoothParam === "off"
    ? "off"
    : camSmoothParam === "interp"
      ? "interp"
      : "extrap";
const camSmooth = camSmoothMode !== "off";
let prevTickEye: Float32Array | null = null;
let curTickEye: Float32Array | null = null;
/** performance.now() of the last simulation step (extrapolation guard). */
let lastStepAt = 0;
// TICK_MS comes from ./tickrate (fixed 125 Hz / 8 ms step).
// Text readouts (speed/coords/timer) trigger layout and are expensive at high
// frame rates, so throttle them. The movement HUD (steering aids) runs every
// frame instead — see `loop`.
const HUD_INTERVAL_MS = 50; // 20 Hz
let lastLoopStart = 0;

// ---- Presentation subsystem scheduler (framerate-independent) ----
// Non-sim presentation work runs on its own cadence (see scheduler.ts) so a slow
// subsystem degrades itself, never the frame. `frameBudgetMs` is the soft
// main-thread budget; the scheduler stops starting new tasks once exceeded.
let frameBudgetMs = 6; // `?budget=` overrides
const scheduler = new Scheduler();
// Numeric HUD readouts (speed/coords/timer) — was an inline 20 Hz throttle.
scheduler.add({ name: "hud", intervalMs: HUD_INTERVAL_MS, budgetMs: 1.5, run: () => runHudReadout() });
// FPS text + netgraph + adaptive interpolation — was an inline 500 ms throttle.
scheduler.add({ name: "stats", intervalMs: 500, budgetMs: 1, run: (now) => runStats(now) });
// Chat log fade (drop lines older than the visible window).
scheduler.add({ name: "chat", intervalMs: 500, budgetMs: 0.5, run: () => renderChat() });
// Particle VFX: fixed ~60 Hz logical step, decoupled from the render rate.
scheduler.add({
  name: "particles",
  intervalMs: 16,
  budgetMs: 1.5,
  run: (now) => {
    particles?.update(now);
    smoke?.update(now);
    railRings?.update(now);
    updateDebris(now);
  },
});
// Debug face probe: `?face=1` casts the camera ray at 2 Hz and logs each new
// face it lands on (see `probeFace`). Steady-state cost is only paid when on.
scheduler.add({
  name: "faceprobe",
  intervalMs: 500,
  budgetMs: 4,
  run: () => {
    if (faceProbeOn) probeFace(true);
  },
});
// Auto stuck-probe: logs once when the player is holding movement but not
// moving (embedded in geometry), so reports capture the exact spot.
scheduler.add({ name: "stuckcheck", intervalMs: 100, budgetMs: 1, run: (now) => checkStuck(now) });

/** Numeric HUD readouts (speed/coords/timer). Scheduled, not per-frame. */
function runHudReadout(): void {
  if (sessionId === null || (menu?.isOpen() ?? false)) return;
  updateCombatHud();
  updateMatchHud();

  const speed = core.session_speed(sessionId);
  speedEl.textContent = `${Math.round(speed)} ups`;
  if (!hudEditorOpen) hudRuntime?.setValue("speed", speed);

  // World position + velocity readout (for debugging movement).
  const o = core.session_origin(sessionId) as unknown as Float32Array;
  const vel = core.session_velocity(sessionId) as unknown as Float32Array;
  coordsEl.textContent =
    `pos ${o[0].toFixed(1)} ${o[1].toFixed(1)} ${o[2].toFixed(1)}\n` +
    `vel ${vel[0].toFixed(1)} ${vel[1].toFixed(1)} ${vel[2].toFixed(1)}`;

  // Race timer display.
  const ticks = core.session_race_ticks(sessionId);
  const finished = core.session_race_finished(sessionId);
  const running = core.session_race_running(sessionId);
  const totalCp = core.session_race_total_checkpoints(sessionId);
  const splits = core.session_race_splits(sessionId) as unknown as Uint32Array;
  // Prejump is a race-only rule.
  const raceMode = isRaceMode();
  const prejumped = raceMode && core.session_prejumped(sessionId);
  const racePrejumped = raceMode && core.session_race_prejumped(sessionId);
  const pjNote = racePrejumped ? "  PREJUMP" : "";
  const fmt = (t: number) => {
    const ms = ticksToMs(t);
    const m = Math.floor(ms / 60000);
    const s = Math.floor((ms % 60000) / 1000);
    const cs = Math.floor((ms % 1000) / 10);
    return `${m}:${s.toString().padStart(2, "0")}.${cs.toString().padStart(2, "0")}`;
  };
  if (core.session_mode(sessionId) === 3) {
    // Recall: show the keyframe readout instead of the run timer.
    timerEl.textContent = "";
    updateRecallHud();
  } else if (!raceMode) {
    recallHudEl.textContent = "";
    timerEl.textContent = "";
  } else {
    recallHudEl.textContent = "";
    const practiceRun =
      core.session_is_practice(sessionId) || core.session_is_recalled(sessionId);
    const practiceTag = practiceRun ? "  (practice)" : "";
    if (finished) {
      timerEl.textContent = `finish ${fmt(ticks)}${practiceTag}${pjNote}`;
      timerEl.style.color = practiceRun ? "var(--ui-info-bright)" : racePrejumped ? "var(--ui-danger)" : "var(--ui-success)";
      // Submission is handled by `maybeSubmitFinish()` every frame.
    } else if (running) {
      const cpNote = totalCp > 0 ? `  (${splits.length}/${totalCp} cp)` : "";
      timerEl.textContent = `${fmt(ticks)}${cpNote}${practiceTag}${pjNote}`;
      timerEl.style.color = practiceRun ? "var(--ui-info-bright)" : racePrejumped ? "var(--ui-danger)" : "var(--ui-accent)";
    } else {
      // Pre-race: warn as soon as the jump/dash/walljump allowance is exceeded.
      timerEl.textContent = prejumped ? "PREJUMP" : "";
      timerEl.style.color = "var(--ui-danger)";
    }
  }
  updateCheckpointHud(running, finished, splits, totalCp, raceMode);
  // Content-driven visibility also goes through the layout runtime, so the CSS
  // `:empty` rules are not a second owner.
  hudRuntime?.setShown("timer", (timerEl.textContent ?? "") !== "");
  hudRuntime?.setShown("recall-hud", (recallHudEl.textContent ?? "") !== "");
}

/** FPS text + netgraph + adaptive interp. Scheduled (~2 Hz), not per-frame. */
function runStats(now: number): void {
  const dt = (now - lastFpsTime) / 1000;
  const fs = playtestFrameStats();
  const ml = mouseLatencyStats();
  let label = `${Math.round(frame / dt)} fps · ${fs.p50.toFixed(1)} ms p50 / ${fs.p99.toFixed(1)} p99`;
  if (ml.n > 0) label += ` · in ${ml.p50.toFixed(1)} ms`;
  if (netStatsEnabled && net?.connected) {
    const kb = (n: number) => (n / 1024).toFixed(1);
    label +=
      `  ↓${kb(net?.downBytesPerSec(now) ?? 0)}K/s (${net?.downMsgsPerSec(now) ?? 0})` +
      `  ↑${kb(net?.upBytesPerSec(now) ?? 0)}K/s (${net?.upMsgsPerSec(now) ?? 0})`;
  }
  fpsEl.textContent = label;
  if (netGraphOn) updateNetGraph(dt);
  lastFpsTime = now;
  frame = 0;
}

/**
 * Adaptive interpolation: until an explicit `?interp=`, track the measured
 * snapshot interval + jitter so remotes render as close to present as the link
 * allows (Phase 7). ~2 intervals + 2× jitter keeps a sample in front.
 *
 * Runs at the end of the frame (not inside the stats HUD task) because it is
 * netcode configuration, not a diagnostic. A small hysteresis avoids
 * oscillating the value every sample window.
 */
function updateAdaptiveInterp(): void {
  if (interpOverride || !net?.connected || ngSnapHist.length < 8) return;
  const sorted = [...ngSnapHist].sort((a, b) => a - b);
  const med = sorted[Math.floor(sorted.length / 2)];
  const target = Math.min(150, Math.max(24, Math.round(2 * med + 2 * netJitterMs)));
  if (Math.abs(target - INTERP_MS) < 4) return;
  const prev = INTERP_MS;
  INTERP_MS = target;
  if (Math.abs(target - prev) >= 12) {
    log(`mp: interp ${prev} -> ${target} ms (snap ${med.toFixed(1)} ms, jitter ${netJitterMs.toFixed(1)} ms)`);
  }
}

// Only ever have ONE requestAnimationFrame chain. `loadMap` and `loop` both
// want to (re)start it; without this guard every map load added another
// concurrent loop and the FPS counter reported N x the real refresh rate.
let loopScheduled = false;
/** Next absolute deadline for the software frame cap (performance.now clock). */
let nextFrameAt = 0;
/** Interval the deadline above belongs to; a cap change resets the deadline. */
let schedIntervalMs = 0;
/** MessageChannel resets Chrome's timer-nesting level, defeating the 4 ms clamp. */
let schedChannel: MessageChannel | null = null;
function scheduleLoop(intervalMs = 0) {
  if (loopScheduled) return;
  loopScheduled = true;
  const run = () => {
    loopScheduled = false;
    loop();
  };
  if (intervalMs <= 0) {
    // Uncapped: follow the display's vsync via rAF.
    nextFrameAt = 0;
    schedIntervalMs = 0;
    requestAnimationFrame(run);
    return;
  }
  // Software cap. Chrome clamps *nested* setTimeout calls to 4 ms (~250 fps),
  // which made a 333/360 fps cap impossible. Posting through a MessageChannel
  // first resets the timer-nesting level, and a deadline accumulator absorbs
  // the 1 ms timer granularity, so the requested rate is actually reached.
  if (intervalMs !== schedIntervalMs) {
    schedIntervalMs = intervalMs;
    nextFrameAt = 0;
  }
  const now = performance.now();
  if (nextFrameAt === 0) nextFrameAt = now;
  // Advance the absolute cadence *before* correcting for an overrun, so a
  // short loop keeps its deadline instead of drifting by the render cost each
  // frame (which would turn a 333 fps cap into ~250 fps).
  nextFrameAt += intervalMs;
  if (nextFrameAt < now) nextFrameAt = now;
  const delay = Math.max(0, Math.round(nextFrameAt - now));
  if (!schedChannel && typeof MessageChannel !== "undefined") {
    schedChannel = new MessageChannel();
  }
  if (schedChannel) {
    schedChannel.port1.onmessage = () => setTimeout(run, delay);
    schedChannel.port2.postMessage(0);
  } else {
    // Very old browser: plain timer (subject to the 4 ms clamp).
    setTimeout(run, delay);
  }
}

// Submit a finished run to the leaderboard, exactly once per (session, ticks,
// map). Called every frame so a fast map switch can't drop or misattribute it —
// crucially using `activeMap` (the session's map), never `currentMap` (which is
// updated *before* the new session exists).
function maybeSubmitFinish(): void {
  if (sessionId === null) return;
  if (!core.session_race_finished(sessionId)) return;
  const ticks = core.session_race_ticks(sessionId);
  const alreadySubmitted =
    lastSubmitted !== null &&
    lastSubmitted.session === sessionId &&
    lastSubmitted.ticks === ticks &&
    lastSubmitted.map === activeMap;
  if (alreadySubmitted) return;
  lastSubmitted = { session: sessionId, ticks, map: activeMap };
  // Persist the run's recording so recall + ghost survive a reload.
  saveRecording(activeMap);
  // Recalled and practice runs are never submitted to the leaderboard.
  if (core.session_is_practice(sessionId) || core.session_is_recalled(sessionId)) {
    // Practice/recalled runs never hit the leaderboard, but show the time you
    // would have gotten (a future feature could submit best practice runs).
    showAnnounce(`practice finish ${formatTicks(ticks)}`, 3000);
    log("practice/recalled run — not submitted to the leaderboard");
    return;
  }
  const identity = getIdentity();
  if (!identity) return;
  const splits = core.session_race_splits(sessionId) as unknown as Uint32Array;
  const racePrejumped = core.session_race_prejumped(sessionId);
  const timeMs = Math.round(ticksToMs(ticks));
  // Report the finish to the game server (session scoreboard).
  if (net?.connected) {
    net.sendText(JSON.stringify({ t: "finish", timeMs }));
  }
  // Upload the run's ghost with the time (kept server-side if it's your best).
  const ghost = core.session_ghost_export(sessionId) as unknown as Float32Array;
  ghostLast = ghost;
  void submitTime(identity.token, activeMap, timeMs, Array.from(splits), racePrejumped, ghost, net?.finishTicket ?? "");
  // The leaderboard may now have a new PR (and possibly WR) ghost for you.
  void refreshGhosts(activeMap).then(() => {
    if (ghostSource !== "last") applyGhostSource();
  });
  void refreshRaceBoard(activeMap);
}

let screenshotPending = false;

/** Queue a screenshot for the end of the current rendered frame. */
function requestScreenshot(): void {
  screenshotPending = true;
}

/**
 * Save the current frame as a PNG. The WebGL canvas is captured right after
 * `renderer.draw` (the drawing buffer is not preserved), then the cgaz strip
 * is composited on top at its on-screen position.
 */
function captureScreenshot(): void {
  const w = canvas.width;
  const h = canvas.height;
  if (w <= 0 || h <= 0) return;
  const shot = document.createElement("canvas");
  shot.width = w;
  shot.height = h;
  const ctx = shot.getContext("2d");
  if (!ctx) return;
  ctx.drawImage(canvas, 0, 0, w, h);
  const cgaz = document.getElementById("cgaz-hud") as HTMLCanvasElement | null;
  if (cgaz && getComputedStyle(cgaz).display !== "none") {
    const r = cgaz.getBoundingClientRect();
    const alpha = Number(getComputedStyle(cgaz).opacity);
    if (r.width > 0 && r.height > 0 && alpha > 0) {
      const sx = w / Math.max(1, window.innerWidth);
      const sy = h / Math.max(1, window.innerHeight);
      ctx.globalAlpha = alpha;
      ctx.drawImage(cgaz, r.left * sx, r.top * sy, r.width * sx, r.height * sy);
      ctx.globalAlpha = 1;
    }
  }
  const d = new Date();
  const p2 = (n: number) => String(n).padStart(2, "0");
  const stamp = `${d.getFullYear()}${p2(d.getMonth() + 1)}${p2(d.getDate())}-${p2(d.getHours())}${p2(d.getMinutes())}${p2(d.getSeconds())}`;
  const map = (activeMap || "map").replace(/[^a-z0-9_-]+/gi, "_");
  const name = `webrace_${map}_${stamp}.png`;
  shot.toBlob((blob) => {
    if (!blob) {
      showAnnounce("screenshot failed", 2000);
      return;
    }
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = name;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 5000);
    showAnnounce("screenshot saved", 1500);
    // Also try to put it on the clipboard (paste straight into chat).
    if (navigator.clipboard && window.ClipboardItem) {
      void navigator.clipboard
        .write([new ClipboardItem({ "image/png": blob })])
        .catch(() => {});
    }
  }, "image/png");
}

interface FrameTimings {
  simMs: number;
  renderMs: number;
  reconcileMs: number;
  netMs: number;
  hudMs: number;
  tasksMs: number;
}

const EMPTY_TIMINGS: FrameTimings = {
  simMs: 0,
  renderMs: 0,
  reconcileMs: 0,
  netMs: 0,
  hudMs: 0,
  tasksMs: 0,
};

/** Sim/input/network phase of one frame. */
function simulateFrame(now: number, detached: boolean): FrameTimings {
  // Snapshot/network callbacks only queue presentation work; drain it here so
  // VFX, audio and DOM updates are part of the measured frame, not the socket.
  flushNetworkPresentation();
  let simMs = 0;
  let reconcileMs = 0;
  let netMs = 0;
  let hudMs = 0;

  // Practice detach (multiplayer): local practice modes must not be driven by
  // the authoritative server (see `practiceDetached`).
  // Step the sim at the fixed simulation rate using an accumulator.
  const menuOpen = menu?.isOpen() ?? false;
  // Online the server never pauses, so freezing local prediction while the
  // settings menu is open leaves a command gap; closing it then produces a
  // large reconciliation (the Sponge spike / "weird input" after changing
  // settings). Keep stepping + sending no-op commands online. The menu never pauses the online sim.
  // While the menu is open, force neutral keys: menu clicks/space must not
  // reach the sim (they used to fire the weapon once the sim kept running).
  if (menuOpen) {
    for (const k of Object.keys(keys) as (keyof typeof keys)[]) keys[k] = false;
  }
  const keepSimWhileMenu = net?.connected ?? false;
  if (sessionId !== null && (!menuOpen || keepSimWhileMenu)) {
    if (detached !== wasPracticeDetached) {
      // Local combat must run ourselves while detached: the server sees no
      // inputs from us, so nothing else would fire our weapons (no explosions,
      // no rocket-jump knockback).
      core.session_set_local_combat(sessionId, detached);
      if (detached) {
        log("practice: local sim — the server sees you standing still");
      } else if (net?.connected ?? false) {
        // Back to normal play: the server stood still while we were detached, so
        // resync — local reset + `restart` respawns us at the run spawn (matching
        // the offline practice-exit).
        core.session_reset(sessionId);
        net?.sendText(JSON.stringify({ t: "restart" }));
      }
      wasPracticeDetached = detached;
    }
    // Apply the latest snapshot's reconciliation once per frame (coalesced).
    if (pendingReconcile) {
      if (detached) {
        // The server's state is not ours while practising locally.
        pendingReconcile = null;
      } else {
        const { ack, ps } = pendingReconcile;
        pendingReconcile = null;
        const recStart = performance.now();
        const corr = core.session_reconcile(
          sessionId,
          ack,
          b64ToBytes(ps),
        ) as unknown as Float32Array;
        lastReconcileMs = performance.now() - recStart;
        reconcileMs = lastReconcileMs;
        lastAck = ack;
        reconcileCount++;
        if (
          corr.length === 3 &&
          Math.abs(corr[0]) + Math.abs(corr[1]) + Math.abs(corr[2]) > 0.01
        ) {
          const t = performance.now();
          // Compose with the un-decayed part of the previous correction. A new
          // reconcile recomputes `corr` from the *simulated* position, so
          // overwriting a still-decaying offset snaps the camera back by the
          // leftover — at snapshot rate that reads as flicker whenever the
          // prediction is being corrected (rocket jumps: the client does not
          // predict the server's knockback).
          let ox = corr[0];
          let oy = corr[1];
          let oz = corr[2];
          if (reconcileOffset) {
            const k = 1 - Math.min(1, (t - reconcileOffset.t) / RECONCILE_MS);
            ox += reconcileOffset.d[0] * k;
            oy += reconcileOffset.d[1] * k;
            oz += reconcileOffset.d[2] * k;
          }
          reconcileOffset = { d: [ox, oy, oz], t };
          // The sub-tick camera history must not treat the correction as
          // movement. `corr = old - new`, so translating both captured eyes by
          // `-corr` puts them in the corrected frame; the decaying offset above
          // then moves the view from `old` to `new` smoothly. Without this the
          // last tick's displacement includes the correction and extrapolation
          // multiplies it into a one-frame camera spike.
          if (prevTickEye) {
            prevTickEye[0] -= corr[0];
            prevTickEye[1] -= corr[1];
            prevTickEye[2] -= corr[2];
          }
          // `prevTickEye === curTickEye` for one step after a teleport/first
          // step; shifting that shared array twice would over-correct.
          if (curTickEye && curTickEye !== prevTickEye) {
            curTickEye[0] -= corr[0];
            curTickEye[1] -= corr[1];
            curTickEye[2] -= corr[2];
          }
          lastCorr = Math.hypot(corr[0], corr[1], corr[2]);
          noteSyncCorrection(corr[0], corr[1], corr[2]);
          corrCount++;
          if (lastCorr > corrMax) corrMax = lastCorr;
          ngCorrHist.push(lastCorr);
          if (ngCorrHist.length > 48) ngCorrHist.shift();
        }
      }
    }
    // Remote player boxes: feed movers (always) and PvP blockers (when the
    // server option is on) from the latest snapshot. One buffer, two consumers.
    if (net?.connected) {
      const snap = snapBuffer.latest();
      const remote = new Float32Array((snap?.players.length ?? 0) * 3);
      let n = 0;
      if (snap) {
        for (const p of snap.players) {
          if (p.slot === mySlot) continue;
          remote[n++] = p.o[0];
          remote[n++] = p.o[1];
          remote[n++] = p.o[2];
        }
      }
      const boxes = remote.subarray(0, n);
      core.session_set_remote_boxes(sessionId, boxes);
      core.session_set_blockers(sessionId, pvpCollision ? boxes : remote.subarray(0, 0));
      // CTF flag-carrier speed is a server post-move clamp; mirror it locally.
      const carrying = latestFlags.some((f) => f.carrier === mySlot);
      const factor =
        carrying && serverMode?.objective === "ctf" ? serverMode.carrier_speed : 1;
      core.session_set_carrier_factor(sessionId, factor);
    } else {
      core.session_set_remote_boxes(sessionId, new Float32Array(0));
      core.session_set_carrier_factor(sessionId, 1);
    }
    // process queued mouse + keys, then step the fixed-rate loop.
    let steps = 0;
    let events = 0;
    const simStart = performance.now();
    // Recall is frozen in time: don't step, and pin the clock so unfreezing
    // can't fast-forward through a backlog. While dead (multiplayer) the server
    // freezes us too, so stop predicting to avoid fighting the respawn — unless
    // we are detached (then the server's state is not ours).
    const dead = (net?.connected ?? false) && lastSelf !== null && !lastSelf.alive;
    if (core.session_mode(sessionId) === 3 || (dead && !detached)) {
      lastTick = now;
    } else {
      while (now - lastTick >= TICK_MS && steps < 8) {
        pushKeys();
        events |= core.session_step(sessionId);
        lastStepAt = performance.now();
        if (camSmooth) {
          const e = core.session_eye(sessionId) as unknown as Float32Array;
          if (curTickEye) {
            const dx = e[0] - curTickEye[0];
            const dy = e[1] - curTickEye[1];
            const dz = e[2] - curTickEye[2];
            // Teleport/respawn: don't slide the camera across the map.
            prevTickEye = dx * dx + dy * dy + dz * dz > 128 * 128 ? e : curTickEye;
          } else {
            prevTickEye = e;
          }
          curTickEye = e;
        }
        lastTick += TICK_MS;
        steps++;
      }
    }
    simMs = performance.now() - simStart;
    // Input timing: an event is "applied" once its tick has stepped. Mouse feel
    // is local (this path touches no network state).
    if (steps > 0 && mousePendingT > 0) {
      mouseLatencies.push(performance.now() - mousePendingT);
      if (mouseLatencies.length > 512) mouseLatencies.shift();
      mousePendingT = 0;
    }
    // Re-apply the selected ghost when a fresh run starts (the core resets its
    // ghost to the previous run at that point).
    const nowRecording = core.session_recall_active(sessionId);
    if (nowRecording && !wasRecording) applyGhostSource();
    wasRecording = nowRecording;
    // Play sounds for gameplay events emitted across this frame's ticks.
    if (events) audio.handleBits(events);
    if (events) crosshairSignals(events);
    // Local combat events (online ones arrive via the snapshot `ev` instead —
    // except while detached, where the local combat sim owns them).
    if ((!net?.connected || detached) && events && sessionId !== null) {
      if (events & EV_FIRE) {
        const s = WEAPON_FIRE_SOUND[core.session_weapon(sessionId)];
        if (s) audio.play(s);
        emitMuzzleFlash(core.session_weapon(sessionId));
      }
      if (events & EV_DEATH) showAnnounce("you died", 1500);
    }
    // A prejumped run was just aborted/respawned.
    if (events & EV_PREJUMP && isRaceMode()) showAnnounce("nice try fork!", 2000);
    if (now - lastTick >= TICK_MS) {
      // We fell behind (e.g. tab was backgrounded) — drop the backlog.
      lastTick = now;
    }

    // Multiplayer: send the commands generated this frame (sequenced). While
    // detached (local practice) we still *drain* (to keep the history bounded)
    // but never transmit: the server must not see the practice inputs.
    const netSendStart = performance.now();
    if (net && net.connected) {
      const flat = core.session_drain_commands(sessionId) as unknown as Int32Array;
      if (!detached && flat.length >= 7) {
        const cmds = [];
        for (let i = 0; i + 7 <= flat.length; i += 7) {
          cmds.push({
            s: flat[i],
            f: flat[i + 1],
            r: flat[i + 2],
            u: flat[i + 3],
            b: flat[i + 4],
            y: flat[i + 5],
            p: flat[i + 6],
          });
        }
        // A shot pressed since the last batch uses the exact frame captured on
        // the attack press (pose + target stamp); held fire falls back to the
        // latest rendered frame on the following batches.
        const shot =
          pendingShotStamp && now - pendingShotStamp.t < 250 ? pendingShotStamp : null;
        pendingShotStamp = null;
        const text = JSON.stringify({
          t: "cmd",
          cmds,
          i: INTERP_MS,
          // The last *completed* render frame is what the player saw; sim runs
          // before render in this frame, so this is intentionally one frame old.
          st: shot?.st ?? lastRenderStamp,
          lp: shot?.lp ?? lastLocalPose,
        });
        net.sendCommands(text, now);
      }
      // Instant local fire VFX (own hitscan beam) so firing doesn't wait a RTT.
      // Not while detached: the local combat sim spawns the *real* projectiles
      // and beams, so predicting a visual on top would double them.
      if (!detached) tryPredictFire(now);
    }
    netMs += performance.now() - netSendStart;

    // Submit a finish immediately (every frame), before any map switch can
    // reassign it. Uses the finishing session's own map (`activeMap`).
    maybeSubmitFinish();

    // Movement HUD (strafe/bunny indicators, accel bar, strafe triangles):
    // steering aids must update every frame to feel smooth, so they are NOT
    // throttled. The numeric readouts (speed/coords/timer) now run on the
    // presentation scheduler (`runHudReadout`).
    const hudStart = performance.now();
    const hint = core.session_movement_hint(sessionId) as unknown as Float32Array;
    const cgazOn = hudRuntime?.isVisible("cgaz-hud") ?? true;
    const cgaz = cgazOn
      ? (core.session_cgaz_profile(sessionId) as unknown as Float32Array)
      : null;
    moveHud.update(hint, renderFov, cgaz, hudSettings.cgazStyle, panini);
    hudMs += performance.now() - hudStart;
  }


  return { ...EMPTY_TIMINGS, simMs, reconcileMs, netMs, hudMs };
}

/** Run the presentation scheduler + throttled HUD tasks. */
function runScheduledTasks(now: number, loopStart: number): number {
  // Presentation scheduler: run due framerate-independent tasks (HUD text,
  // netgraph, adaptive interp) within the frame budget. When the frame is over
  // budget they wait for a lighter frame instead of blowing it.
  const tasksStart = performance.now();
  hudRuntime?.setRaceMode(isRaceMode());
  scheduler.run(now, loopStart + frameBudgetMs);
  const tasksMs = performance.now() - tasksStart;
  crosshairHud?.tick(now);
  hudRuntime?.tick(now);
  const spectatingNow = isSpectating();
  if (spectatingNow !== lastSpectate) {
    lastSpectate = spectatingNow;
    applyHudDoc();
  }


  return tasksMs;
}

/** Render/world-presentation phase of one frame. */
interface FrameCamera {
  proj: Float32Array;
  view: Float32Array;
  eye: Float32Array;
  yaw: number;
  pitch: number;
  vp: Float32Array;
}

/** Camera, FOV, recall/reconcile smoothing and the view matrix. */
function computeFrameCamera(now: number): FrameCamera {
  if (sessionId === null) throw new Error("computeFrameCamera without a session");
  const aspect = canvas.width / canvas.height;
  // Quake FOV: the cvar is the HORIZONTAL fov; the vertical fov is derived
  // from the aspect ratio (tan(hfov/2) / aspect). Same model as Warfork/q3.
  const hfov = (renderFov * Math.PI) / 180;
  const vfov = 2 * Math.atan(Math.tan(hfov / 2) / aspect);
  const proj = perspective(vfov, aspect, CAMERA_NEAR, cameraFar);
  let eyeRaw = core.session_eye(sessionId) as unknown as Float32Array;
  let anglesRaw = core.session_angles(sessionId) as unknown as Float32Array;
  if (isSpectating()) {
    const sp = spectatePose(now);
    if (sp) {
      eyeRaw = sp.eye;
      anglesRaw = new Float32Array([sp.yaw, sp.pitch]);
    }
  }
  // Animate between recall keyframes (view-only; the sim is frozen).
  let eye: Float32Array = eyeRaw;
  let yaw = anglesRaw[0];
  let pitch = anglesRaw[1];
  // Sub-tick local camera smoothing. `lastTick` is the scheduled time of the
  // step that produced `curTickEye`; the next step is due one TICK_MS later, so
  // `frac` runs 0→1 across the current tick interval. Extrapolate uses
  // k = 1 + frac (cur + the last tick's displacement × frac): the camera shows
  // the estimated present instead of lagging one tick. Interpolation
  // (k = frac) is the A/B fallback.
  //
  // GOTCHA: the step loop advances `lastTick` *after* stepping, so `lastTick`
  // is the previous tick, not the next one. Subtracting another TICK_MS here
  // (as an earlier version did) pins `frac` at 1 forever: the camera then only
  // moves when a tick steps (125 Hz stair-steps) and double-counts any impulse
  // (rocket jump / dash) by a full tick. Keep this in sync with the loop.
  if (camSmooth && !isSpectating() && !recallAnim && prevTickEye && curTickEye) {
    const frac = Math.min(1, Math.max(0, (now - lastTick) / TICK_MS));
    // Only extrapolate while the sim is actually stepping; a pause/death
    // leaves the last two eyes frozen and a 1-tick lead would stick.
    const stepping = lastStepAt > 0 && now - lastStepAt <= TICK_MS * 1.5;
    const k = camSmoothMode === "interp" || !stepping ? frac : 1 + frac;
    eye = new Float32Array([
      prevTickEye[0] + (curTickEye[0] - prevTickEye[0]) * k,
      prevTickEye[1] + (curTickEye[1] - prevTickEye[1]) * k,
      prevTickEye[2] + (curTickEye[2] - prevTickEye[2]) * k,
    ]);
  }
  if (recallAnim) {
    const t = Math.min(1, (now - recallAnim.start) / RECALL_ANIM_MS);
    const s = t * t * (3 - 2 * t); // smoothstep
    eye = new Float32Array([
      recallAnim.fromEye[0] + (recallAnim.toEye[0] - recallAnim.fromEye[0]) * s,
      recallAnim.fromEye[1] + (recallAnim.toEye[1] - recallAnim.fromEye[1]) * s,
      recallAnim.fromEye[2] + (recallAnim.toEye[2] - recallAnim.fromEye[2]) * s,
    ]);
    yaw = lerpAngle(recallAnim.fromYaw, recallAnim.toYaw, s);
    pitch = recallAnim.fromPitch + (recallAnim.toPitch - recallAnim.fromPitch) * s;
    if (t >= 1) recallAnim = null;
  }
  // Smooth server corrections (rollback/replay reconciliation) over ~150 ms.
  if (reconcileOffset) {
    const t = Math.min(1, (now - reconcileOffset.t) / RECONCILE_MS);
    const k = 1 - t;
    eye = new Float32Array([
      eye[0] + reconcileOffset.d[0] * k,
      eye[1] + reconcileOffset.d[1] * k,
      eye[2] + reconcileOffset.d[2] * k,
    ]);
    if (t >= 1) reconcileOffset = null;
  }
  // Remember the rendered local pose for the next command batch. This is the
  // camera the player actually aimed with; the server validates it against
  // its predicted state before using it for a shot.
  if (!isSpectating() && !recallAnim) {
    lastLocalPose = { x: eye[0], y: eye[1], z: eye[2], yaw, pitch };
  }
  const view = lookAt([eye[0], eye[1], eye[2]], yaw, pitch);
  const vp = multiply(proj, view);
  // The panini warp is a final composite pass; keep its FOV in sync with zoom.
  renderer?.setPanini(panini > 0, hfov, vfov, panini);

  // Snapshot the eye ray (post recall/reconcile) for the debug face probe.
  {
    const b = camBasis(yaw, pitch);
    lastRay = {
      eye: [eye[0], eye[1], eye[2]],
      fwd: [b.fwd[0], b.fwd[1], b.fwd[2]],
      right: [b.right[0], b.right[1], b.right[2]],
      up: [b.up[0], b.up[1], b.up[2]],
    };
  }

  // Camera diagnostic: prove the view delta matches the mouse counts since the
  // previous frame (see `diagCameraFrame`). Recomputed `yaw`/`pitch` here are
  // the ones the player actually aimed with. Re-seed after a camera override
  // (spectate/recall) so returning to the local view can't look like a jump.
  if (!isSpectating() && !recallAnim) diagCameraFrame(now, eye, yaw, pitch);
  else diagLastYaw = Number.NaN;
  return { proj, view, eye, yaw, pitch, vp };
}

/** Sample the neon player trails (local + remotes + ghost). */
function updateFrameTrails(now: number): void {
  if (sessionId === null) return;
  // Neon player trails: sample the local player, remote players and the ghost.
  // Presentation only — this never feeds back into the sim.
  if (playerTrails) {
    const samples: TrailSample[] = [];
    if (!isSpectating()) {
      const slot = mySlot >= 0 ? mySlot : 0;
      const lo = core.session_origin(sessionId) as unknown as Float32Array;
      samples.push({
        id: "local",
        x: lo[0],
        y: lo[1],
        z: lo[2] + 8, // chest height (same attachment as remotes/ghost)
        color: trailColorForSlot(slot),
      });
    }
    for (const r of snapBuffer.remoteStates(now, mySlot)) {
      samples.push({
        id: r.slot,
        x: r.origin[0],
        y: r.origin[1],
        z: r.origin[2] + 8,
        color: trailColorForSlot(r.slot),
      });
    }
    const gh = ghostInstance();
    if (gh) {
      samples.push({
        id: "ghost",
        x: gh.matrix[12],
        y: gh.matrix[13],
        z: gh.matrix[14] + 8,
        color: gh.color,
      });
    }
    playerTrails.update(now, samples);
  }
}

/** Mover model matrices, PVS visibility, and the scene fog volume. */
function updateFrameMoversAndVisibility(eye: Float32Array, vp: Float32Array): void {
  if (!renderer || sessionId === null) return;
  // Per-mover model matrices (origin + 3 basis columns), column-major.
  const transforms = core.session_mover_transforms(sessionId) as unknown as Float32Array;
  const moverMatrices = frameMovers;
  moverMatrices.length = 0;
  for (let i = 0, k = 0; i + 12 <= transforms.length; i += 12, k++) {
    let m = moverMatrixPool[k];
    if (!m) {
      m = new Float32Array(16);
      moverMatrixPool[k] = m;
    }
    // columns = axis[0], axis[1], axis[2]; translation = origin.
    m[0] = transforms[i + 3]; m[1] = transforms[i + 4]; m[2] = transforms[i + 5]; m[3] = 0;
    m[4] = transforms[i + 6]; m[5] = transforms[i + 7]; m[6] = transforms[i + 8]; m[7] = 0;
    m[8] = transforms[i + 9]; m[9] = transforms[i + 10]; m[10] = transforms[i + 11]; m[11] = 0;
    m[12] = transforms[i]; m[13] = transforms[i + 1]; m[14] = transforms[i + 2]; m[15] = 1;
    moverMatrices.push(m);
  }

  // PVS: feed the renderer only the faces visible from the camera's leaf
  // (falls back to full chunks when the map has no visibility data, the eye
  // is in cluster 0, or nothing runs).
  let runCount = -1;
  if (pvsEnabled && visRuns && currentMapId >= 0) {
    const planes = frustumEnabled ? frustumPlanes : EMPTY_F32;
    if (frustumEnabled) extractFrustum(vp, frustumPlanes);
    runCount = core.bsp_visible_runs(currentMapId, eye, planes, visRuns);
  }
  lastFrustumRuns = runCount;
  lastEye[0] = eye[0];
  lastEye[1] = eye[1];
  lastEye[2] = eye[2];
  // Q3 fog comes from the fog brush containing the view origin. Look up its
  // fogparms (`surfaceparm fog` + `fogparms`) and apply it to the whole scene.
  if (currentMapId >= 0) {
    const fogShader = core.bsp_fog_at(currentMapId, eye);
    renderer.setSceneFog(fogShader >= 0 ? (fogByShader.get(fogShader) ?? null) : null);
  } else {
    renderer.setSceneFog(null);
  }
  if (runCount >= 0 && visRuns) renderer.setVisibleRuns(visRuns, runCount);
  else renderer.setVisibleRuns(null);
}

/** Per-frame model/VFX instances (models, projectiles, items, ...). */
function buildFrameInstances(now: number, eye: Float32Array, yaw: number, pitch: number, detached: boolean): void {
  if (!renderer || sessionId === null) return;
  // Runtime models: per-frame instances (matrix + lightgrid tint + anim frame),
  // plus the ghost of your previous run.
  const tNow = now / 1000;
  const instances = frameInstances;
  instances.length = 0;
  for (const e of modelEntities) {
      let frame0 = 0;
      let frame1 = 0;
      let lerp = 0;
      if (e.forceFrame) {
        ({ frame0, frame1, lerp } = e.forceFrame);
      } else if (e.animFps > 0 && e.numFrames > 1) {
        const pos = tNow * e.animFps;
        frame0 = Math.floor(pos) % e.numFrames;
        frame1 = (frame0 + 1) % e.numFrames;
        lerp = pos - Math.floor(pos);
      }
      instances.push({
        model: e.model,
        matrix: modelMatrix(e.origin, e.angles, e.scale),
        color: e.color,
        alpha: e.alpha,
        frame0,
        frame1,
        lerp,
      });
    }
  const ghost = ghostInstance();
  if (ghost) instances.push(...playerInstances(ghost.matrix, ghost.color, 0.45));
  instances.push(...remoteInstances(now));
  // Projectiles: server-replicated online (interpolated), local
  // `session_entities` while practice-detached (local practice
  // online), where our own weapons are simulated by the local combat sim.
  const localProjectiles = (): NetEntity[] => {
    if (sessionId === null) return [];
    const ef = core.session_entities(sessionId) as unknown as Float32Array;
    const out: NetEntity[] = [];
    for (let i = 0; i + 12 <= ef.length; i += 12) {
      out.push({
        id: ef[i],
        k: ef[i + 1] | 0,
        w: ef[i + 2],
        own: ef[i + 3],
        o: [ef[i + 4], ef[i + 5], ef[i + 6]],
        v: [ef[i + 7], ef[i + 8], ef[i + 9]],
        s: ef[i + 10] > 0.5,
        r: ef[i + 11],
      });
    }
    return out;
  };
  let renderEntities: NetEntity[] = latestEntities;
  if (!net?.connected) {
    renderEntities = localProjectiles();
  } else if (detached) {
    // Keep other players' replicated projectiles *and* add our local ones.
    renderEntities = [
      ...snapBuffer.interpolatedEntities(now, latestEntities, !!net?.connected),
      ...localProjectiles(),
    ];
  } else {
    renderEntities = snapBuffer.interpolatedEntities(now, latestEntities, !!net?.connected);
  }
  // Locally-predicted projectiles (online): advance them, retire the ones the
  // server has since replicated, and draw the rest. Visual only — and skipped
  // while detached, where the local sim owns the real projectiles.
  if (net?.connected && !detached) {
    const pdt = predProjT > 0 ? Math.min(0.1, (now - predProjT) / 1000) : 0;
    predProjT = now;
    for (const p of predictedProjectiles) {
      p.start[0] += p.vel[0] * pdt;
      p.start[1] += p.vel[1] * pdt;
      p.start[2] += p.vel[2] * pdt;
      if (p.grenade) p.vel[2] -= p.gravity * pdt;
    }
    const serverLeft = new Map<number, number>();
    for (const e of renderEntities) {
      if (e.own === mySlot) serverLeft.set(e.w, (serverLeft.get(e.w) ?? 0) + 1);
    }
    for (const p of [...predictedProjectiles].sort((a, b) => a.spawn - b.spawn)) {
      if (p.replaced) continue;
      const n = serverLeft.get(p.weapon) ?? 0;
      if (n > 0) {
        p.replaced = true;
        serverLeft.set(p.weapon, n - 1);
      }
    }
    predictedProjectiles = predictedProjectiles.filter((p) => !p.replaced && p.die > now);
  } else {
    predictedProjectiles = [];
    predProjT = 0;
  }
  // Projectile impacts (own + remote + local): a projectile that
  // vanishes between frames has hit something → burst/spark at its last
  // position. (Remote ones can also vanish by leaving PVS; see `prevProj`.)
  if ((particles || smoke) && sessionId !== null) {
    const seen = new Set<number>();
    for (const e of renderEntities) {
      seen.add(e.id);
      prevProj.set(e.id, { o: [e.o[0], e.o[1], e.o[2]], w: e.w });
    }
    for (const [id, prev] of prevProj) {
      if (seen.has(id)) continue;
      if (EXPLOSIVE_WEAPONS.has(prev.w)) {
        const scale = prev.w === 4 ? 1.4 : 1.0;
        // Per-weapon fireball tint if the weapon has a colour override.
        const fire = projectileColorOverride[prev.w]
          ? hexToRgb(projectileColorOverride[prev.w])
          : hexToRgb(vfxColorExplosion);
        emitWeaponParticles(prev.w, () => {
          particles?.explosion(
            prev.o,
            scale,
            fire,
            hexToRgb(vfxColorSparks),
            getProjectileAlpha(prev.w),
          );
          emitDebris(prev.o, scale, hexToRgb(vfxColorSmoke));
        });
        emitWeaponSmoke(prev.w, () => smoke?.puff(prev.o, scale, hexToRgb(vfxColorSmoke)));
      } else {
        emitWeaponParticles(prev.w, () =>
          particles?.spark(prev.o, undefined, hexToRgb(vfxColorSparks), getProjectileAlpha(prev.w)),
        );
        emitWeaponSmoke(prev.w, () => smoke?.wisp(prev.o, hexToRgb(vfxColorSmoke)));
      }
      prevProj.delete(id);
    }
  }
  // Our own projectiles *visually* leave the gun. The simulation origin is
  // the eye (correct for hit detection), but drawing them there makes them
  // appear at the crosshair — so both the model AND its trail/smoke are
  // shifted by (muzzle - eye). Remote projectiles are untouched.
  const muzzleW = viewModelMuzzle(eye, yaw, pitch, currentWeapon());
  const gunDelta: [number, number, number] | null = muzzleW
    ? [muzzleW[0] - eye[0], muzzleW[1] - eye[1], muzzleW[2] - eye[2]]
    : null;
  const fromGun = (o: number[]): [number, number, number] =>
    gunDelta ? [o[0] + gunDelta[0], o[1] + gunDelta[1], o[2] + gunDelta[2]] : [o[0], o[1], o[2]];
  const isOwnProj = (e: NetEntity): boolean => (mySlot >= 0 ? e.own === mySlot : e.own > 0.5);
  // Projectile trails: a bright glow trail each frame + a thin smoke wisp
  // every few frames (client-local, short-lived). The per-weapon trail mode
  // decides which layers this weapon gets (rocket = smoke, plasma = glow).
  if (vfxTrails && (particles || smoke)) {
    const wisps = (frame & 3) === 0;
    const glowOn = (v: ReturnType<typeof weaponVfx>) =>
      v.vfxTrailMode === "both" || v.vfxTrailMode === "glow";
    const smokeOn = (v: ReturnType<typeof weaponVfx>) =>
      v.vfxTrailMode === "both" || v.vfxTrailMode === "smoke";
    for (const e of renderEntities) {
      const p = isOwnProj(e) ? fromGun(e.o) : e.o;
      const v = weaponVfx(e.w);
      if (glowOn(v)) {
        withParticleVfx(e.w, () =>
          particles?.trail(p, projectileColor(e.w), getProjectileAlpha(e.w), v.vfxTrailMs),
        );
      }
      if (wisps && smokeOn(v)) {
        withSmokeVfx(e.w, () =>
          smoke?.wisp(p, hexToRgb(vfxColorSmoke), undefined, v.vfxSmokeTrailMs),
        );
      }
    }
    for (const p of predictedProjectiles) {
      const g = fromGun(p.start);
      const v = weaponVfx(p.weapon);
      if (glowOn(v)) {
        withParticleVfx(p.weapon, () =>
          particles?.trail(g, projectileColor(p.weapon), getProjectileAlpha(p.weapon), v.vfxTrailMs),
        );
      }
      if (wisps && smokeOn(v)) {
        withSmokeVfx(p.weapon, () =>
          smoke?.wisp(g, hexToRgb(vfxColorSmoke), undefined, v.vfxSmokeTrailMs),
        );
      }
    }
  }
  if (projectileModelId >= 0) {
    // Per-weapon projectile model from the asset pack, else the placeholder
    // cube. Asset projectiles keep their own texture (no tint) and may be
    // oriented along their velocity.
    const projInstance = (o: number[], w: number, v: number[], own: boolean): ModelInstance => {
      const p = own ? fromGun(o) : [o[0], o[1], o[2]];
      const key = `projectile.${w | 0}`;
      const mid = assetModelId(key);
      if (mid >= 0) {
        const e = assetEntry(key);
        const off = e?.originOffset ?? [0, 0, 0];
        const a = e?.alignVelocity ? velocityAngles(v) : ([0, 0, 0] as [number, number, number]);
        return {
          model: mid,
          matrix: modelMatrix([p[0] + off[0], p[1] + off[1], p[2] + off[2]], a, (e?.scale ?? 1) * PROJECTILE_VISUAL_SCALE),
          color: [1, 1, 1],
          alpha: getProjectileAlpha(w),
          frame0: 0,
          frame1: 0,
          lerp: 0,
        };
      }
      return {
        model: projectileModelId,
        matrix: modelMatrix([p[0], p[1], p[2]], [0, 0, 0], PROJECTILE_VISUAL_SCALE),
        color: projectileColor(w),
        alpha: getProjectileAlpha(w),
        frame0: 0,
        frame1: 0,
        lerp: 0,
      };
    };
    for (const e of renderEntities) {
      if (e.k === 2) {
        // Physics ball: a lit sphere scaled to its collision radius, with a
        // rolling spin derived from its velocity.
        const rad = e.r && e.r > 0 ? e.r : 16;
        const sp = Math.hypot(e.v[0], e.v[1]) || 1;
        const spin = (Math.hypot(e.v[0], e.v[1], e.v[2]) / rad) * 0.5;
        const ax = (e.v[1] / sp) * spin;
        const ay = (-e.v[0] / sp) * spin;
        instances.push({
          model: ballModelId >= 0 ? ballModelId : projectileModelId,
          matrix: modelMatrix(isOwnProj(e) ? fromGun(e.o) : e.o, [ax, ay, 0], rad),
          color: projectileColor(e.w),
          alpha: getProjectileAlpha(e.w),
          frame0: 0,
          frame1: 0,
          lerp: 0,
        });
        continue;
      }
      instances.push(projInstance(e.o, e.w, e.v, isOwnProj(e)));
    }
    for (const p of predictedProjectiles) {
      instances.push(projInstance(p.start, p.weapon, p.vel, true));
    }
  }
  // CTF flag markers at home/dropped positions.
  if (latestFlags.length > 0 && net?.connected && itemModelId >= 0) {
    const tSec = now / 1000;
    for (const f of latestFlags) {
      if (!f.present || f.state === 1) continue;
      instances.push({
        model: itemModelId,
        matrix: modelMatrix(
          [f.o[0], f.o[1], f.o[2] + 24 + Math.sin(tSec * 2 + f.team) * 4],
          [0, tSec * 1.2, 0],
          8,
        ),
        color: f.team === 1 ? [1, 0.25, 0.25] : [0.25, 0.55, 1],
        alpha: 0.95,
        frame0: 0,
        frame1: 0,
        lerp: 0,
      });
    }
  }
  // Healing Weeball bubbles (Wipeout): translucent green spheres.
  if (latestHeals.length > 0 && net?.connected) {
    for (const h of latestHeals) {
      instances.push({
        model: ballModelId >= 0 ? ballModelId : projectileModelId,
        matrix: modelMatrix([h.o[0], h.o[1], h.o[2]], [0, 0, 0], h.r),
        color: [0.35, 1.0, 0.55],
        alpha: 0.22,
        frame0: 0,
        frame1: 0,
        lerp: 0,
      });
    }
  }
  // Item pickups: the local session uses its present/taken state; online uses
  // the server's replicated item list + presence deltas. Taken items stay
  // visible at 50% and fade back in over their last 5 seconds.
  const itemPts: {
    x: number;
    y: number;
    z: number;
    kind: number;
    weapon: number;
    amount: number;
    alpha: number;
  }[] = [];
  if (net?.connected) {
    const cull2 = 3000 * 3000;
    for (const it of net.items) {
      const dx = it.o[0] - eye[0];
      const dy = it.o[1] - eye[1];
      const dz = it.o[2] - eye[2];
      if (dx * dx + dy * dy + dz * dz > cull2) continue;
      if (frustumEnabled && !pointInFrustum(it.o, 128)) continue;
      let remainingMs = -1; // unknown / never respawns
      const respawnMs = it.r ?? 0;
      if (!it.p && respawnMs > 0) {
        remainingMs = Math.max(
          0,
          it.hiddenAt === undefined ? respawnMs : respawnMs - (now - it.hiddenAt),
        );
      }
      itemPts.push({
        x: it.o[0],
        y: it.o[1],
        z: it.o[2],
        kind: it.k | 0,
        weapon: it.w | 0,
        amount: it.a,
        alpha: itemAlpha(it.p, remainingMs),
      });
    }
  } else if (sessionId !== null) {
    const n = core.session_item_count(sessionId);
    for (let i = 0; i < n; i++) {
      core.session_item(sessionId, i, itemScratch);
      const present = itemScratch[6] >= 0.5;
      itemPts.push({
        x: itemScratch[0],
        y: itemScratch[1],
        z: itemScratch[2],
        kind: itemScratch[3] | 0,
        weapon: itemScratch[4] | 0,
        amount: itemScratch[5],
        alpha: itemAlpha(present, itemScratch[7]),
      });
    }
  }
  if ((itemModelId >= 0 || assetGpuCount() > 0) && itemPts.length > 0) {
    const tSec = now / 1000;
    for (let i = 0; i < itemPts.length; i++) {
      const it = itemPts[i];
      const key = itemAssetKey(it.kind, it.weapon, it.amount);
      const mid = key ? assetModelId(key) : -1;
      // Bob + spin so pickups read as items, not static blocks.
      const bob = Math.sin(tSec * 2.0 + i * 0.7) * 4.0;
      const spin = tSec * 1.3 + i;
      if (mid >= 0) {
        const e = assetEntry(key);
        const off = e?.originOffset ?? [0, 0, 0];
        instances.push({
          model: mid,
          matrix: modelMatrix(
            [it.x + off[0], it.y + off[1], it.z + off[2] + (e?.bob === false ? 0 : bob)],
            [0, e?.spin === false ? 0 : spin, 0],
            e?.scale ?? 1,
          ),
          color: [1, 1, 1],
          alpha: it.alpha,
          frame0: 0,
          frame1: 0,
          lerp: 0,
        });
      } else if (itemModelId >= 0) {
        // Placeholder: health a touch bigger, ammo smaller, weapons larger.
        const scale = it.kind === 1 ? 1.15 : it.kind === 2 ? 1.25 : it.kind === 3 ? 0.8 : 1.1;
        instances.push({
          model: itemModelId,
          matrix: modelMatrix([it.x, it.y, it.z + 10 + bob], [0, spin, 0], scale),
          color: itemColor(it.kind),
          alpha: it.alpha,
          frame0: 0,
          frame1: 0,
          lerp: 0,
        });
      }
    }
  }
  // Teleporter destination markers: a marker model (or a pulsing diamond
  // fallback) so you can see where a teleporter sends you.
  {
    const tpKey = "world.teleporter";
    const tpAsset = assetModelId(tpKey);
    if ((tpAsset >= 0 || teleportModelId >= 0) && teleporterDests.length > 0) {
      const tSec = now / 1000;
      const tpEntry = assetEntry(tpKey);
      for (let i = 0; i < teleporterDests.length; i++) {
        const d = teleporterDests[i];
        if (tpAsset >= 0) {
          instances.push({
            model: tpAsset,
            matrix: modelMatrix([d[0], d[1], d[2]], [0, tSec * 1.5 + i, 0], tpEntry?.scale ?? 1),
            color: [1, 1, 1],
            alpha: 1,
            frame0: 0,
            frame1: 0,
            lerp: 0,
          });
        } else {
          const pulse = 0.85 + Math.sin(tSec * 2.5 + i) * 0.15;
          instances.push({
            model: teleportModelId,
            matrix: modelMatrix([d[0], d[1], d[2] + 28], [0, tSec * 1.5 + i, 0], 9 * pulse),
            color: [0.35, 0.85, 1.0],
            alpha: 0.6,
            frame0: 0,
            frame1: 0,
            lerp: 0,
          });
        }
      }
    }
  }
  // Low-poly debris chunks (tumbling cubes with collision).
  if (debrisModelId >= 0) {
    for (const d of debrisChunks) {
      instances.push({
        model: debrisModelId,
        matrix: modelMatrix([d.x, d.y, d.z], [d.ax, d.ay, d.az], d.size),
        color: d.color,
        alpha: Math.min(1, d.life / 300),
        frame0: 0,
        frame1: 0,
        lerp: 0,
      });
    }
  }
}

/** Hitscan beams/tracers (server, local and predicted). */
function updateFrameBeams(now: number, eye: Float32Array, yaw: number, pitch: number, detached: boolean): void {
  if (!renderer || sessionId === null) return;
  // Hitscan beams/tracers: server-replicated online, `session_beams` while detached —
  // and also while detached (local practice online).
  let renderBeams: (NetBeam & { born: number; die: number })[] = netBeams;
  if ((!net?.connected || detached) && sessionId !== null) {
    const bf = core.session_beams(sessionId) as unknown as Float32Array;
    ingestLocalBeams(bf, now, eye, yaw, pitch);
  }
  if (!net?.connected) renderBeams = localBeams;
  else if (detached) renderBeams = [...netBeams, ...localBeams];
  if (beamModelId >= 0) {
    const localSlot = net?.connected && !detached ? mySlot : sessionId !== null ? 0 : -1;
    // Beams read best starting at the weapon muzzle (bottom-right) instead of
    // the camera, so they don't engulf the view.
    const gunMuzzle: [number, number, number] =
      viewModelMuzzle(eye, yaw, pitch, currentWeapon()) ?? placeholderMuzzle(eye, yaw, pitch);
    // Local continuous beam (the LG), always anchored to the weapon muzzle.
    // `cg_truelightning` blends the far end between the server's authoritative
    // impact (0 = real network — it sways and lags your view) and the client's
    // current crosshair target (1 = perfect follow); recomputed every frame.
    if (sessionId !== null && (keys.attack || keys.attack2)) {
      const localW = net?.connected
        ? lastSelf?.wp ?? 0
        : (core.session_weapon(sessionId) as number);
      const combat = core.session_combat(sessionId) as unknown as Float32Array;
      const localAlive = combat.length >= 7 ? combat[6] > 0.5 : true;
      const localAmmo = net?.connected
        ? lastSelf?.am ?? 0
        : combat.length >= 4
          ? combat[3]
          : 0;
      if (isSmoothWeapon(localW) && localAlive && localAmmo > 0) {
        const strong = !keys.attack2 || keys.attack;
        const lb = core.session_predict_beam(
          sessionId,
          localW,
          strong,
        ) as unknown as Float32Array;
        if (lb.length >= 6) {
          // Client side (1): the crosshair's current target (instant).
          const clientFar = clipBeamToRemotes(
            [lb[0], lb[1], lb[2]],
            [lb[3], lb[4], lb[5]],
            now,
          );
          // Server side (0): the newest own authoritative impact (delayed).
          // Before the first one arrives (~1 RTT) fall back to the client end.
          let serverFar: [number, number, number] = clientFar;
          for (let i = netBeams.length - 1; i >= 0; i--) {
            if (netBeams[i].own === localSlot) {
              serverFar = netBeams[i].b;
              break;
            }
          }
          const t = CG_TRUELIGHTNING;
          const far: [number, number, number] = [
            serverFar[0] + (clientFar[0] - serverFar[0]) * t,
            serverFar[1] + (clientFar[1] - serverFar[1]) * t,
            serverFar[2] + (clientFar[2] - serverFar[2]) * t,
          ];
          const style = beamStyle(localW);
          // Always anchored to the weapon muzzle; the far end follows the
          // crosshair (1) or the server's real (lagged) impact (0).
          const clipped = clipBeamNear(gunMuzzle, far, eye, 20);
          if (clipped) {
            frameInstances.push({
              model: beamModelId,
              matrix: beamMatrix(clipped[0], clipped[1], style.thickness),
              color: style.color,
              alpha: style.alpha,
              frame0: 0,
              frame1: 0,
              lerp: 0,
            });
          }
        }
      }
    }
    // Your own predicted beams (fire prediction) — instant, from the muzzle.
    // Fixed world-space start/end: they stay where the shot was fired.
    predictedBeams = predictedBeams.filter((b) => b.die > now);
    for (const pb of predictedBeams) {
      if (isSmoothWeapon(pb.weapon)) continue; // the LG is drawn live above
      const style = beamStyle(pb.weapon);
      const clipped = clipBeamNear(pb.start, pb.end, eye, 12);
      if (!clipped) continue;
      const alpha = style.alpha * beamFade(pb.born, pb.die, now);
      if (alpha <= 0.002) continue;
      frameInstances.push({
        model: beamModelId,
        matrix: beamMatrix(clipped[0], clipped[1], style.thickness),
        color: style.color,
        alpha,
        frame0: 0,
        frame1: 0,
        lerp: 0,
      });
    }
    for (const bm of renderBeams) {
      // Online, your own shots are drawn locally (above); skip the server's
      // echoed copy so it isn't drawn twice.
      if (bm.own === localSlot && (net?.connected || isSmoothWeapon(bm.w))) continue;
      const style = beamStyle(bm.w);
      const clipped = clipBeamNear(bm.a, bm.b, eye, 12);
      if (!clipped) continue;
      const alpha = style.alpha * beamFade(bm.born, bm.die, now);
      if (alpha <= 0.002) continue;
      frameInstances.push({
        model: beamModelId,
        matrix: beamMatrix(clipped[0], clipped[1], style.thickness),
        color: style.color,
        alpha,
        frame0: 0,
        frame1: 0,
        lerp: 0,
      });
    }
  }
}

/** First-person viewmodel, floating tags, and the actual GPU draw. */
function drawFrame(now: number, camera: FrameCamera): { renderMs: number; hudMs: number } {
  let renderMs = 0;
  let hudMs = 0;
  if (!renderer || sessionId === null) return { renderMs, hudMs };
  const { proj, view, eye, yaw, pitch, vp } = camera;
  // First-person weapon (drawn on top by the renderer). NOTE: it shares the
  // world projection on purpose — flashes/beams are world-space points, so a
  // separate "gun FOV" would put the rendered barrel somewhere other than
  // where the beam starts. Placement is tuned instead (VIEWMODEL_HAND_DEFAULT).
  renderer.setViewModel(
    hideViewModel || isSpectating()
      ? null
      : viewModelInstance(eye, yaw, pitch, currentWeapon()),
  );
  // Floating name tags above remote players (DOM work; measured as HUD).
  const tagStart = performance.now();
  updateNameTags(
    snapBuffer.remoteStates(now, mySlot),
    vp,
    canvasCssW || canvas.clientWidth,
    canvasCssH || canvas.clientHeight,
  );
  updatePings(
    now,
    vp,
    canvasCssW || canvas.clientWidth,
    canvasCssH || canvas.clientHeight,
    eye,
  );
  spawnDamageNumbers(now, vp, canvasCssW || canvas.clientWidth, canvasCssH || canvas.clientHeight);
  updateDamageNumbers(now);
  updateHitmarker(now);
  hudMs += performance.now() - tagStart;
  renderer.setModelInstances(hideModels ? [] : frameInstances);

  profiler?.gpuBegin();
  const renderStart = performance.now();
  renderer.draw(multiply(proj, view), [eye[0], eye[1], eye[2]], now / 1000, frameMovers);
  if (screenshotPending) {
    screenshotPending = false;
    captureScreenshot();
  }
  renderMs = performance.now() - renderStart;
  profiler?.gpuEnd();

  return { renderMs, hudMs };
}

function renderFrame(now: number, detached: boolean): { renderMs: number; hudMs: number } {
  if (!renderer || sessionId === null) return { renderMs: 0, hudMs: 0 };
  // Capture the exact snapshot pair this frame will draw *before* anything
  // consumes it. Fire commands sent next frame use this stamp, so lag comp
  // mirrors what the player actually saw. (It used to be a side effect of
  // `snapBuffer.remoteStates()`, which is called from several phases.)
  lastRenderStamp = snapBuffer.stamp(now);
  const camera = computeFrameCamera(now);
  updateFrameTrails(now);
  updateFrameMoversAndVisibility(camera.eye, camera.vp);
  buildFrameInstances(now, camera.eye, camera.yaw, camera.pitch, detached);
  updateFrameBeams(now, camera.eye, camera.yaw, camera.pitch, detached);
  return drawFrame(now, camera);
}

/** Finish the frame: profiler sample + next-frame scheduling. */
function finishFrame(loopStart: number, t: FrameTimings): void {
  const { simMs, renderMs, reconcileMs, netMs, hudMs, tasksMs } = t;
  profiler?.gpuPoll();

  const loopEnd = performance.now();
  if (profiler?.isEnabled()) {
    profiler.add({
      interval: lastLoopStart ? loopStart - lastLoopStart : 0,
      work: loopEnd - loopStart,
      sim: simMs,
      render: renderMs,
      reconcile: reconcileMs,
      net: netMs,
      hud: hudMs,
      tasks: tasksMs,
    });
    profiler.update(loopStart);
  }
  lastLoopStart = loopStart;
  updateAdaptiveInterp();

  // Optional frame-rate cap (`?fpscap=N`) for trace-friendly runs. The
  // deadline accumulator in `scheduleLoop` accounts for the loop's own cost.
  scheduleLoop(fpsCap > 0 ? 1000 / fpsCap : 0);
}

function loop() {
  const loopStart = performance.now();
  const now = loopStart;
  const frameDt = lastFrameMs ? Math.min(100, now - lastFrameMs) : 16;
  lastFrameMs = now;
  frame++;
  playtestFrameMs.push(frameDt);
  if (playtestFrameMs.length > 240) playtestFrameMs.shift();

  // Per-weapon profile follows the same weapon source as the HUD.
  const activeWeapon = currentWeapon();
  if (activeWeapon !== appliedWeapon) {
    appliedWeapon = activeWeapon;
    resetZoom();
    applyWeaponProfile(activeWeapon);
  }
  updateZoom(frameDt);

  const detached = practiceDetached();
  const timings = simulateFrame(now, detached);
  const netDrainStart = performance.now();
  net?.flush(now);
  timings.netMs += performance.now() - netDrainStart;
  timings.tasksMs = runScheduledTasks(now, loopStart);
  if (renderer && sessionId !== null) {
    const rendered = renderFrame(now, detached);
    timings.renderMs = rendered.renderMs;
    timings.hudMs += rendered.hudMs;
  }
  finishFrame(loopStart, timings);
}

void main();
